import { useState, useEffect, useRef, useCallback } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";
import { MapContainer, TileLayer, Marker, Circle, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import DetailedReportModal from "../components/DetailedReportModal";

// Fix para ícone do Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Componente para capturar cliques no mapa
function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click: (e) => {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

// Função para formatar horas decimais em "Xh Ymin"
function formatHoursMinutes(decimalHours, showSign = false) {
  if (decimalHours === null || decimalHours === undefined || isNaN(decimalHours)) {
    return "0h 00min";
  }
  
  const isNegative = decimalHours < 0;
  const absHours = Math.abs(decimalHours);
  const hours = Math.floor(absHours);
  const minutes = Math.round((absHours - hours) * 60);
  
  // Ajustar caso os minutos arredondem para 60
  let finalHours = hours;
  let finalMinutes = minutes;
  if (finalMinutes === 60) {
    finalHours += 1;
    finalMinutes = 0;
  }
  
  const sign = showSign ? (isNegative ? "-" : "+") : (isNegative ? "-" : "");
  return `${sign}${finalHours}h ${String(finalMinutes).padStart(2, '0')}min`;
}
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import {
  Clock,
  Users,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  Plus,
  UserPlus,
  MapPin,
  Camera,
  ChevronDown,
  Calendar,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle,
  MessageSquare,
  MessageCircle,
  Home,
  BarChart3,
  Building2,
  Building,
  Phone,
  Mail,
  MoreVertical,
  Upload,
  DollarSign,
  CreditCard,
  Receipt,
  Pencil,
  Trash2,
  Download,
  Search,
  HeadphonesIcon,
  Edit3,
  PlusCircle,
  Save,
  XCircle,
  Eye,
  Printer,
  FileDown,
  RotateCcw,
  ZoomIn,
  ArrowRight,
  ArrowLeft,
  Target,
  AlertTriangle,
  GraduationCap,
  Briefcase,
  Lock,
  Shield,
  Stethoscope,
  FileImage,
  CalendarDays,
  ClipboardList,
  Baby,
  Heart,
  HelpCircle,
  Palmtree,
  Code,
  User,
  Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Dashboard Overview Component
function DashboardOverview() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [attendanceType, setAttendanceType] = useState("present"); // "present", "absent", "all", "records"
  const [atestadosDashboard, setAtestadosDashboard] = useState(null);
  const [activeVacations, setActiveVacations] = useState([]);
  
  // Face verification settings
  const [faceVerificationEnabled, setFaceVerificationEnabled] = useState(false);
  const [togglingFaceVerification, setTogglingFaceVerification] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchAtestadosDashboard();
    fetchActiveVacations();
    fetchCompanySettings();
  }, []);
  
  const fetchCompanySettings = async () => {
    try {
      const response = await axios.get(`${API}/company/info`);
      setFaceVerificationEnabled(response.data.face_verification_enabled || false);
    } catch (error) {
      console.error("Erro ao carregar configurações:", error);
    }
  };
  
  const toggleFaceVerification = async () => {
    setTogglingFaceVerification(true);
    try {
      const newValue = !faceVerificationEnabled;
      await axios.put(`${API}/company/settings`, { face_verification_enabled: newValue });
      setFaceVerificationEnabled(newValue);
      toast.success(newValue 
        ? "✅ Verificação facial ATIVADA! Os funcionários precisarão confirmar identidade ao bater ponto."
        : "❌ Verificação facial desativada."
      );
    } catch (error) {
      toast.error("Erro ao atualizar configuração");
    } finally {
      setTogglingFaceVerification(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error("Erro ao carregar estatísticas");
    } finally {
      setLoading(false);
    }
  };

  const fetchAtestadosDashboard = async () => {
    try {
      const response = await axios.get(`${API}/medical-leaves/summary/dashboard`);
      setAtestadosDashboard(response.data);
    } catch (error) {
      console.error("Erro ao carregar atestados");
    }
  };

  const fetchActiveVacations = async () => {
    try {
      const response = await axios.get(`${API}/vacations/active`);
      setActiveVacations(response.data.employees_on_vacation || []);
    } catch (error) {
      console.error("Erro ao carregar férias:", error);
    }
  };

  const openAttendanceList = (type) => {
    setAttendanceType(type);
    setShowAttendanceModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1CA1BA]"></div>
      </div>
    );
  }

  // Listas de funcionários presentes, ausentes e todos
  const presentEmployees = stats?.present_employees || [];
  const absentEmployees = stats?.absent_employees || [];
  const allEmployees = [...presentEmployees, ...absentEmployees];
  const todayRecords = stats?.today_records || [];

  return (
    <div className="space-y-6" data-testid="admin-dashboard-overview">
      <div>
        <h1 className="font-heading font-bold text-2xl text-slate-900">Dashboard</h1>
        <p className="text-slate-600">Visão geral do sistema de ponto</p>
      </div>

      {/* Stats Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card 
          className="border border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => openAttendanceList("all")}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Total Funcionários</p>
                <p className="font-heading font-bold text-3xl text-slate-900">{stats?.total_employees || 0}</p>
              </div>
              <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-ciaponto-cyan" />
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-2">Clique para ver a lista</p>
          </CardContent>
        </Card>

        <Card 
          className="border border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => openAttendanceList("present")}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Presentes Hoje</p>
                <p className="font-heading font-bold text-3xl text-green-600">{stats?.employees_present_today || 0}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-2">Clique para ver a lista</p>
          </CardContent>
        </Card>

        <Card 
          className="border border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => openAttendanceList("absent")}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Ausentes Hoje</p>
                <p className="font-heading font-bold text-3xl text-orange-600">{stats?.employees_absent_today || 0}</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-orange-600" />
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-2">Clique para ver a lista</p>
          </CardContent>
        </Card>

        <Card 
          className="border border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => openAttendanceList("records")}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Registros Hoje</p>
                <p className="font-heading font-bold text-3xl text-slate-900">{stats?.today_total_records || 0}</p>
              </div>
              <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-slate-600" />
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-2">Clique para ver a lista</p>
          </CardContent>
        </Card>
      </div>
      
      {/* Card de Configuração - Verificação Facial */}
      <Card className="border border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-indigo-500 rounded-lg">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-indigo-900">Verificação Facial (AWS Rekognition)</h3>
                <p className="text-sm text-indigo-600">
                  {faceVerificationEnabled 
                    ? "Os funcionários precisam confirmar identidade ao bater ponto"
                    : "Ative para aumentar a segurança do registro de ponto"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-sm font-medium ${faceVerificationEnabled ? 'text-green-600' : 'text-slate-500'}`}>
                {faceVerificationEnabled ? 'ATIVO' : 'INATIVO'}
              </span>
              <button
                onClick={toggleFaceVerification}
                disabled={togglingFaceVerification}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  faceVerificationEnabled ? 'bg-green-500' : 'bg-slate-300'
                } ${togglingFaceVerification ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    faceVerificationEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
          {faceVerificationEnabled && (
            <div className="mt-3 p-2 bg-green-100 rounded-lg flex items-center gap-2 text-sm text-green-700">
              <CheckCircle className="w-4 h-4" />
              <span>A foto tirada será comparada com a foto de cadastro usando IA (~$0.001/verificação)</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Lista */}
      <Dialog open={showAttendanceModal} onOpenChange={setShowAttendanceModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {attendanceType === "present" && (
                <>
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Funcionários Presentes Hoje
                </>
              )}
              {attendanceType === "absent" && (
                <>
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                  Funcionários Ausentes Hoje
                </>
              )}
              {attendanceType === "all" && (
                <>
                  <Users className="w-5 h-5 text-ciaponto-cyan" />
                  Todos os Funcionários
                </>
              )}
              {attendanceType === "records" && (
                <>
                  <Clock className="w-5 h-5 text-slate-600" />
                  Registros de Ponto Hoje
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {attendanceType === "present" && `${presentEmployees.length} funcionário(s) registraram ponto hoje`}
              {attendanceType === "absent" && `${absentEmployees.length} funcionário(s) não registraram ponto hoje`}
              {attendanceType === "all" && `${allEmployees.length} funcionário(s) cadastrados`}
              {attendanceType === "records" && `${todayRecords.length} registro(s) de ponto hoje`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="max-h-96 overflow-y-auto">
            {/* Lista de Presentes */}
            {attendanceType === "present" && (
              presentEmployees.length > 0 ? (
                <div className="space-y-2">
                  {presentEmployees.map((emp, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                      <div className="w-10 h-10 rounded-full bg-green-200 flex items-center justify-center overflow-hidden">
                        {emp.profile_photo_url ? (
                          <img src={emp.profile_photo_url} alt={emp.name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-green-700 font-medium">{emp.name?.charAt(0)}</span>
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{emp.name}</p>
                        <p className="text-xs text-slate-500">{emp.position || emp.department}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-green-600 font-medium">
                          {emp.first_entry ? `Entrada: ${new Date(emp.first_entry).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : ""}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>Nenhum funcionário registrou ponto hoje</p>
                </div>
              )
            )}
            
            {/* Lista de Ausentes */}
            {attendanceType === "absent" && (
              absentEmployees.length > 0 ? (
                <div className="space-y-2">
                  {absentEmployees.map((emp, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                      <div className="w-10 h-10 rounded-full bg-orange-200 flex items-center justify-center overflow-hidden">
                        {emp.profile_photo_url ? (
                          <img src={emp.profile_photo_url} alt={emp.name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-orange-700 font-medium">{emp.name?.charAt(0)}</span>
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{emp.name}</p>
                        <p className="text-xs text-slate-500">{emp.position || emp.department}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">Ausente</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>Todos os funcionários estão presentes!</p>
                </div>
              )
            )}
            
            {/* Lista de Todos os Funcionários */}
            {attendanceType === "all" && (
              allEmployees.length > 0 ? (
                <div className="space-y-2">
                  {allEmployees.map((emp, index) => {
                    const isPresent = presentEmployees.some(p => p.id === emp.id);
                    return (
                      <div key={index} className={`flex items-center gap-3 p-3 rounded-lg ${isPresent ? 'bg-green-50' : 'bg-slate-50'}`}>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center overflow-hidden ${isPresent ? 'bg-green-200' : 'bg-slate-200'}`}>
                          {emp.profile_photo_url ? (
                            <img src={emp.profile_photo_url} alt={emp.name} className="w-full h-full object-cover" />
                          ) : (
                            <span className={`font-medium ${isPresent ? 'text-green-700' : 'text-slate-700'}`}>{emp.name?.charAt(0)}</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-slate-900">{emp.name}</p>
                          <p className="text-xs text-slate-500">{emp.position || emp.department}</p>
                        </div>
                        <div className="text-right">
                          {isPresent ? (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Presente</span>
                          ) : (
                            <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">Ausente</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>Nenhum funcionário cadastrado</p>
                </div>
              )
            )}
            
            {/* Lista de Registros de Hoje */}
            {attendanceType === "records" && (
              todayRecords.length > 0 ? (
                <div className="space-y-2">
                  {todayRecords.map((record, index) => (
                    <div key={index} className={`flex items-center gap-3 p-3 rounded-lg ${record.record_type === 'entry' ? 'bg-green-50' : 'bg-red-50'}`}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${record.record_type === 'entry' ? 'bg-green-200' : 'bg-red-200'}`}>
                        {record.record_type === 'entry' ? (
                          <ArrowRight className="w-5 h-5 text-green-700" />
                        ) : (
                          <ArrowLeft className="w-5 h-5 text-red-700" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{record.employee_name || 'Funcionário'}</p>
                        <p className="text-xs text-slate-500">
                          {record.record_type === 'entry' ? 'Entrada' : 'Saída'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-slate-700">
                          {new Date(record.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Clock className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>Nenhum registro de ponto hoje</p>
                </div>
              )
            )}
          </div>
          
          <div className="flex justify-end pt-4">
            <Button variant="outline" onClick={() => setShowAttendanceModal(false)}>
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Recent Records */}
      <Card className="border border-slate-200">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Registros Recentes</CardTitle>
          <CardDescription>Últimos pontos registrados</CardDescription>
        </CardHeader>
        <CardContent>
          {stats?.recent_records?.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_records.map((record, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      record.record_type === "entry" ? "bg-green-100" : "bg-red-100"
                    }`}>
                      {record.record_type === "entry" ? (
                        <TrendingUp className="w-5 h-5 text-green-600" />
                      ) : (
                        <TrendingDown className="w-5 h-5 text-red-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{record.employee_name}</p>
                      <p className="text-sm text-slate-500">
                        {record.record_type === "entry" ? "Entrada" : "Saída"} • {new Date(record.timestamp).toLocaleString("pt-BR")}
                      </p>
                    </div>
                  </div>
                  {record.latitude && (
                    <div className="flex items-center gap-1 text-sm text-slate-500">
                      <MapPin className="w-4 h-4" />
                      <span>Localizado</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-slate-500 py-8">Nenhum registro encontrado</p>
          )}
        </CardContent>
      </Card>

      {/* Card de Atestados Recentes */}
      {atestadosDashboard && atestadosDashboard.summary.total_leaves > 0 && (
        <Card className="border-l-4 border-l-red-500">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Stethoscope className="w-5 h-5 text-red-500" />
              Atestados Recentes
            </CardTitle>
            <CardDescription>Funcionários com atestados nos últimos 30 dias</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 mb-4 text-center">
              <div className="bg-red-50 rounded-lg p-3">
                <p className="text-2xl font-bold text-red-600">{atestadosDashboard.summary.total_leaves}</p>
                <p className="text-xs text-red-500">Total de Atestados</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-3">
                <p className="text-2xl font-bold text-amber-600">{atestadosDashboard.summary.total_hours}h</p>
                <p className="text-xs text-amber-500">Horas Dispensadas</p>
              </div>
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-2xl font-bold text-blue-600">{atestadosDashboard.summary.employees_affected}</p>
                <p className="text-xs text-blue-500">Funcionários</p>
              </div>
            </div>
            
            <div className="space-y-2">
              {atestadosDashboard.employees.slice(0, 5).map((emp, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                      <Stethoscope className="w-4 h-4 text-red-500" />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-slate-900">{emp.employee_name}</p>
                      <p className="text-xs text-slate-500">
                        {emp.leaves.length} atestado(s)
                      </p>
                    </div>
                  </div>
                  <span className="text-sm font-semibold text-amber-600">{emp.total_hours}h</span>
                </div>
              ))}
            </div>
            
            <Link 
              to="/admin/atestados" 
              className="mt-4 flex items-center justify-center gap-2 text-sm text-ciaponto-cyan hover:text-ciaponto-navy"
            >
              Ver todos os atestados
              <ArrowRight className="w-4 h-4" />
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Card de Funcionários de Férias */}
      {activeVacations.length > 0 && (
        <Card className="border-l-4 border-l-green-500">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Palmtree className="w-5 h-5 text-green-500" />
              Funcionários de Férias
            </CardTitle>
            <CardDescription>{activeVacations.length} funcionário(s) de férias atualmente</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {activeVacations.slice(0, 5).map((v, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-green-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-green-200 flex items-center justify-center">
                      {v.employee_photo ? (
                        <img src={v.employee_photo} alt="" className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <Palmtree className="w-4 h-4 text-green-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-sm text-slate-900">{v.employee_name}</p>
                      <p className="text-xs text-slate-500">
                        {v.employee_position || "Funcionário"}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-semibold text-green-600">{v.days_remaining} dias</span>
                    <p className="text-xs text-slate-500">restantes</p>
                  </div>
                </div>
              ))}
            </div>
            
            <Link 
              to="/admin/ferias" 
              className="mt-4 flex items-center justify-center gap-2 text-sm text-ciaponto-cyan hover:text-ciaponto-navy"
            >
              Gerenciar férias
              <ArrowRight className="w-4 h-4" />
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Employees Component
function EmployeesPage() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [geofenceDialogOpen, setGeofenceDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  // Estados para cerca eletrônica
  const [enableGeofence, setEnableGeofence] = useState(false);
  const [geofenceLocation, setGeofenceLocation] = useState(null);
  const [geofenceRadius, setGeofenceRadius] = useState(500); // metros
  
  // Estado para feriados ativos
  const [feriadosAtivos, setFeriadosAtivos] = useState([]);
  
  // Estado para atestados do funcionário selecionado
  const [employeeAtestados, setEmployeeAtestados] = useState([]);
  
  // Estado para abonos de faltas
  const [employeeAbonos, setEmployeeAbonos] = useState({});
  
  // Estado para ajustes manuais de banco de horas
  const [employeeBankHoursAdjustment, setEmployeeBankHoursAdjustment] = useState(0);
  
  // Estado para histórico de horas
  const [employeeHoursHistory, setEmployeeHoursHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Modal de adicionar atestado rápido
  const [quickAtestadoDialogOpen, setQuickAtestadoDialogOpen] = useState(false);
  const [quickAtestadoData, setQuickAtestadoData] = useState({
    date: "",
    leave_type: "atestado_medico",
    reason: ""
  });
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    cpf: "", // CPF do funcionário
    position: "",
    department: "",
    employee_type: "funcionario", // funcionario, estagiario, terceirizado
    password: "", // Senha do funcionário
    work_hours_per_day: 8,
    weekly_hours: 44, // CLT padrão: 44h semanais = 220h mensais
    work_schedule_type: "standard", // standard ou 44h_semana_comercial
    profile_photo_url: "",
    // Cerca Eletrônica
    geofence_enabled: false,
    geofence_latitude: null,
    geofence_longitude: null,
    geofence_radius: 500, // metros
    geofence_address: ""
  });
  
  // Estado para modal de edição de funcionário
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  
  // Estados para câmera
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  
  // Estado para visualizar foto ampliada
  const [viewingPhoto, setViewingPhoto] = useState(null);
  
  // Estado para modal de detalhes do funcionário
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [employeeDetails, setEmployeeDetails] = useState(null);
  const [employeeRecords, setEmployeeRecords] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState("month"); // week, month, custom
  
  // Estados para edição de cerca eletrônica
  const [geofenceFormData, setGeofenceFormData] = useState({
    geofence_enabled: false,
    geofence_latitude: null,
    geofence_longitude: null,
    geofence_radius: 500,
    geofence_address: ""
  });

  // Iniciar câmera
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } } 
      });
      setCameraStream(stream);
      setShowCamera(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (error) {
      console.error("Erro ao acessar câmera:", error);
      toast.error("Não foi possível acessar a câmera. Verifique as permissões.");
    }
  };

  // Parar câmera
  const stopCamera = useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  }, [cameraStream]);

  // Verificar qualidade da imagem (brilho e contraste)
  const checkImageQuality = (canvas) => {
    const context = canvas.getContext("2d");
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    
    let totalBrightness = 0;
    let pixelCount = 0;
    
    // Calcular brilho médio (amostrar cada 10 pixels para performance)
    for (let i = 0; i < data.length; i += 40) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      // Fórmula de luminosidade percebida
      const brightness = (0.299 * r + 0.587 * g + 0.114 * b);
      totalBrightness += brightness;
      pixelCount++;
    }
    
    const avgBrightness = totalBrightness / pixelCount;
    
    // Verificar se está muito escuro (< 30) ou muito claro (> 240)
    // Valores mais permissivos para não rejeitar muitas fotos
    if (avgBrightness < 30) {
      return { valid: false, message: "Foto muito escura. Procure um local mais iluminado." };
    }
    if (avgBrightness > 240) {
      return { valid: false, message: "Foto muito clara/estourada. Evite luz direta na câmera." };
    }
    
    return { valid: true, brightness: avgBrightness };
  };

  // Capturar foto da câmera
  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Verificar qualidade da imagem
    const quality = checkImageQuality(canvas);
    if (!quality.valid) {
      toast.error(quality.message);
      return; // Não captura a foto, mantém câmera aberta
    }
    
    canvas.toBlob(async (blob) => {
      if (!blob) {
        toast.error("Erro ao capturar foto");
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(blob);
      
      setUploadingPhoto(true);
      stopCamera();
      
      try {
        // Converter blob para File com tipo correto
        const file = new File([blob], "profile_photo.jpg", { type: "image/jpeg" });
        
        const uploadData = new FormData();
        uploadData.append("file", file);
        
        console.log("Iniciando upload para:", `${API}/upload-image`);
        console.log("File size:", file.size, "bytes, type:", file.type);
        
        // O axios já tem o token configurado globalmente via AuthContext
        const response = await axios.post(`${API}/upload-image`, uploadData, {
          timeout: 30000 // 30 segundos de timeout
        });
        
        console.log("Upload response:", response.data);
        const photoUrl = response.data.image_url || response.data.url;
        
        if (photoUrl) {
          console.log("Photo URL obtida:", photoUrl);
          setFormData(prev => ({ ...prev, profile_photo_url: photoUrl }));
          toast.success("Foto capturada com sucesso!");
        } else {
          console.error("URL da foto não encontrada na resposta:", response.data);
          toast.error("Erro: URL da foto não recebida");
          setPhotoPreview(null);
        }
      } catch (error) {
        console.error("Erro upload foto:", error);
        console.error("Error response:", error.response?.data);
        console.error("Error status:", error.response?.status);
        
        let errorMsg = "Erro ao enviar foto";
        if (error.response?.data?.detail) {
          errorMsg = error.response.data.detail;
        } else if (error.message) {
          errorMsg = error.message;
        }
        
        toast.error(errorMsg);
        setPhotoPreview(null);
      } finally {
        setUploadingPhoto(false);
      }
    }, "image/jpeg", 0.9);
  };

  // Resetar foto
  const resetPhoto = () => {
    setPhotoPreview(null);
    setFormData(prev => ({ ...prev, profile_photo_url: "" }));
  };
  
  // Dias da semana para escala
  const weekDays = [
    { id: 0, name: "Dom", fullName: "Domingo" },
    { id: 1, name: "Seg", fullName: "Segunda" },
    { id: 2, name: "Ter", fullName: "Terça" },
    { id: 3, name: "Qua", fullName: "Quarta" },
    { id: 4, name: "Qui", fullName: "Quinta" },
    { id: 5, name: "Sex", fullName: "Sexta" },
    { id: 6, name: "Sáb", fullName: "Sábado" }
  ];
  
  const [selectedWorkDays, setSelectedWorkDays] = useState([1, 2, 3, 4, 5, 6]); // Padrão 6x1
  const [weekendRotation, setWeekendRotation] = useState("none"); // none, odd, even
  const [selectedScheduleType, setSelectedScheduleType] = useState("standard"); // standard ou 44h_semana_comercial

  useEffect(() => {
    fetchEmployees();
    fetchFeriados();
  }, []);

  const fetchFeriados = async () => {
    try {
      const currentYear = new Date().getFullYear();
      const response = await axios.get(`${API}/holidays/company?year=${currentYear}`);
      const ativos = (response.data.feriados || [])
        .filter(f => f.ativo)
        .map(f => f.data);
      setFeriadosAtivos(ativos);
    } catch (error) {
      console.log("Erro ao buscar feriados:", error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (error) {
      toast.error("Erro ao carregar funcionários");
    } finally {
      setLoading(false);
    }
  };

  // Upload foto de perfil
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Preview local
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);
    
    // Upload para Cloudflare
    setUploadingPhoto(true);
    try {
      const uploadData = new FormData();
      uploadData.append("file", file);
      
      // O axios já tem o token configurado globalmente via AuthContext
      const response = await axios.post(`${API}/upload-image`, uploadData, {
        timeout: 30000
      });
      
      // Backend retorna image_url
      const photoUrl = response.data.image_url || response.data.url;
      setFormData({ ...formData, profile_photo_url: photoUrl });
      toast.success("Foto enviada com sucesso!");
    } catch (error) {
      console.error("Erro upload:", error);
      toast.error(error.response?.data?.detail || "Erro ao enviar foto");
      setPhotoPreview(null);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.profile_photo_url) {
      toast.error("A foto de perfil é obrigatória!");
      return;
    }
    try {
      await axios.post(`${API}/employees`, formData);
      toast.success("Funcionário cadastrado com sucesso!");
      setDialogOpen(false);
      setFormData({ 
        name: "", 
        email: "", 
        phone: "",
        cpf: "",
        position: "", 
        department: "",
        employee_type: "funcionario",
        password: "",
        work_hours_per_day: 8,
        weekly_hours: 44,
        work_schedule_type: "standard",
        profile_photo_url: "",
        geofence_enabled: false,
        geofence_latitude: null,
        geofence_longitude: null,
        geofence_radius: 500,
        geofence_address: ""
      });
      setPhotoPreview(null);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao cadastrar funcionário");
    }
  };

  // Abrir modal de edição do funcionário
  const openEditDialog = (employee) => {
    setEditFormData({
      id: employee.id,
      name: employee.name || "",
      email: employee.email || "",
      phone: employee.phone || "",
      cpf: employee.cpf || "",
      position: employee.position || "",
      department: employee.department || "",
      employee_type: employee.employee_type || "funcionario",
      password: "", // Deixar em branco - só atualiza se preenchido
      work_hours_per_day: employee.work_hours_per_day || 8,
      weekly_hours: employee.weekly_hours || 44,
      work_schedule_type: employee.work_schedule_type || "standard",
      profile_photo_url: employee.profile_photo_url || "",
      is_active: employee.is_active !== false,
      ponto_liberado: employee.ponto_liberado || false,  // Ponto liberado pela empresa
      geofence_enabled: employee.geofence_enabled || false,
      geofence_latitude: employee.geofence_latitude || null,
      geofence_longitude: employee.geofence_longitude || null,
      geofence_radius: employee.geofence_radius || 500
    });
    setEditDialogOpen(true);
  };

  // Salvar edição do funcionário
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editFormData) return;
    
    try {
      // Preparar dados para envio (remover campos vazios)
      const updateData = { ...editFormData };
      delete updateData.id;
      
      // Só enviar senha se foi preenchida
      if (!updateData.password) {
        delete updateData.password;
      }
      
      await axios.put(`${API}/employees/${editFormData.id}`, updateData);
      toast.success("Funcionário atualizado com sucesso!");
      setEditDialogOpen(false);
      setEditFormData(null);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar funcionário");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este funcionário?")) return;
    try {
      await axios.delete(`${API}/employees/${id}`);
      toast.success("Funcionário excluído");
      fetchEmployees();
    } catch (error) {
      toast.error("Erro ao excluir funcionário");
    }
  };
  
  // Abrir modal de escala
  const openScheduleDialog = (employee) => {
    setSelectedEmployee(employee);
    setSelectedWorkDays(employee.work_days || [1, 2, 3, 4, 5, 6]);
    setWeekendRotation(employee.weekend_rotation || "none");
    setSelectedScheduleType(employee.work_schedule_type || "standard");
    setScheduleDialogOpen(true);
  };
  
  // Toggle dia na escala
  const toggleWorkDay = (dayId) => {
    if (selectedWorkDays.includes(dayId)) {
      setSelectedWorkDays(selectedWorkDays.filter(d => d !== dayId));
    } else {
      setSelectedWorkDays([...selectedWorkDays, dayId].sort());
    }
  };
  
  // Quando muda o tipo de escala, ajustar os dias automaticamente
  const handleScheduleTypeChange = (newType) => {
    setSelectedScheduleType(newType);
    if (newType === "44h_semana_comercial") {
      // Escala 44h comercial: seg-sex 8h + sáb 4h
      setSelectedWorkDays([1, 2, 3, 4, 5, 6]);
    }
  };
  
  // Salvar escala
  const handleSaveSchedule = async () => {
    if (!selectedEmployee) return;
    if (selectedWorkDays.length === 0) {
      toast.error("Selecione pelo menos um dia de trabalho");
      return;
    }
    try {
      // Calcular weekly_hours baseado no tipo de escala
      let weeklyHours = 44;
      if (selectedScheduleType === "44h_semana_comercial") {
        weeklyHours = 44; // 8h * 5 + 4h = 44h
      } else {
        weeklyHours = selectedWorkDays.length * 8; // Padrão: 8h por dia
      }
      
      await axios.put(`${API}/employees/${selectedEmployee.id}`, {
        work_days: selectedWorkDays,
        weekend_rotation: weekendRotation,
        work_schedule_type: selectedScheduleType,
        weekly_hours: weeklyHours
      });
      toast.success("Escala atualizada com sucesso!");
      setScheduleDialogOpen(false);
      fetchEmployees();
    } catch (error) {
      toast.error("Erro ao atualizar escala");
    }
  };

  // Abrir modal de edição da cerca eletrônica
  const openGeofenceDialog = (employee) => {
    setSelectedEmployee(employee);
    setGeofenceFormData({
      geofence_enabled: employee.geofence_enabled || false,
      geofence_latitude: employee.geofence_latitude || null,
      geofence_longitude: employee.geofence_longitude || null,
      geofence_radius: employee.geofence_radius || 500,
      geofence_address: employee.geofence_address || ""
    });
    setGeofenceDialogOpen(true);
  };

  // Salvar cerca eletrônica
  const handleSaveGeofence = async () => {
    if (!selectedEmployee) return;
    
    // Validar se a cerca está habilitada mas não tem coordenadas
    if (geofenceFormData.geofence_enabled && (!geofenceFormData.geofence_latitude || !geofenceFormData.geofence_longitude)) {
      toast.error("Preencha a latitude e longitude para ativar a cerca eletrônica");
      return;
    }
    
    try {
      await axios.put(`${API}/employees/${selectedEmployee.id}`, {
        geofence_enabled: geofenceFormData.geofence_enabled,
        geofence_latitude: geofenceFormData.geofence_latitude,
        geofence_longitude: geofenceFormData.geofence_longitude,
        geofence_radius: geofenceFormData.geofence_radius,
        geofence_address: geofenceFormData.geofence_address
      });
      toast.success("Cerca eletrônica atualizada com sucesso!");
      setGeofenceDialogOpen(false);
      fetchEmployees();
    } catch (error) {
      toast.error("Erro ao atualizar cerca eletrônica");
    }
  };

  // Obter localização atual para cerca
  const getCurrentLocationForGeofence = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGeofenceFormData({
            ...geofenceFormData,
            geofence_latitude: position.coords.latitude,
            geofence_longitude: position.coords.longitude
          });
          toast.success("Localização obtida com sucesso!");
        },
        () => toast.error("Não foi possível obter a localização")
      );
    } else {
      toast.error("Geolocalização não suportada pelo navegador");
    }
  };
  
  // Formatar escala para exibição
  const formatSchedule = (workDays, rotation, scheduleType) => {
    if (!workDays || workDays.length === 0) return "Não definida";
    
    // Se é escala 44h comercial, mostrar de forma especial
    if (scheduleType === "44h_semana_comercial") {
      let base = "44h Comercial (8h Seg-Sex + 4h Sáb)";
      if (rotation === "odd") return `${base} + FDS ímpares`;
      if (rotation === "even") return `${base} + FDS pares`;
      return base;
    }
    
    let base = "";
    if (workDays.length === 7) base = "Todos os dias";
    else if (JSON.stringify([...workDays].sort()) === JSON.stringify([1, 2, 3, 4, 5, 6])) base = "6x1 (Seg-Sáb)";
    else if (JSON.stringify([...workDays].sort()) === JSON.stringify([1, 2, 3, 4, 5])) base = "5x2 (Seg-Sex)";
    else {
      const dayNames = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
      base = workDays.map(d => dayNames[d]).join(", ");
    }
    
    // Adicionar info de rotação de fim de semana
    if (rotation === "odd") return `${base} + FDS ímpares`;
    if (rotation === "even") return `${base} + FDS pares`;
    return base;
  };

  // Formatar tipo de escala para exibição curta
  const formatScheduleType = (type) => {
    switch (type) {
      case "44h_semana_comercial":
        return { label: "44h Comercial", description: "8h Seg-Sex + 4h Sáb", color: "bg-amber-100 text-amber-700" };
      default:
        return { label: "Padrão", description: "Horas iguais todos os dias", color: "bg-slate-100 text-slate-600" };
    }
  };

  // Abrir modal de detalhes do funcionário
  // Estado para saldo mensal do funcionário selecionado (vindo do backend)
  const [employeeMonthlyBalance, setEmployeeMonthlyBalance] = useState(null);
  
  const openEmployeeDetails = async (employee) => {
    setEmployeeDetails(employee);
    setDetailsDialogOpen(true);
    setLoadingDetails(true);
    setEmployeeAtestados([]); // Limpar atestados anteriores
    setEmployeeAbonos({}); // Limpar abonos anteriores
    setEmployeeBankHoursAdjustment(0); // Limpar ajustes de banco de horas
    setEmployeeMonthlyBalance(null); // Limpar saldo anterior
    
    try {
      // Buscar registros de ponto, atestados, abonos e SALDO MENSAL do backend
      const currentDate = new Date();
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      
      // Calcular datas de início e fim do mês atual
      const startOfMonth = `${year}-${String(month).padStart(2, '0')}-01`;
      const endOfMonth = month === 12 
        ? `${year + 1}-01-01` 
        : `${year}-${String(month + 1).padStart(2, '0')}-01`;
      
      // Buscar dados principais (críticos) + SALDO MENSAL DO BACKEND
      const [recordsResponse, atestadosResponse, abonosResponse, monthlyBalanceResponse] = await Promise.all([
        axios.get(`${API}/time-records?employee_id=${employee.id}&start_date=${startOfMonth}&end_date=${endOfMonth}`),
        axios.get(`${API}/medical-leaves?employee_id=${employee.id}&month=${month}&year=${year}`),
        axios.get(`${API}/absence-adjustments/employee/${employee.id}/dates?month=${month}&year=${year}`),
        axios.get(`${API}/employees/${employee.id}/monthly-balance?month=${month}&year=${year}`)
      ]);
      
      // Os registros vêm diretamente como array do /time-records
      setEmployeeRecords(recordsResponse.data || []);
      setEmployeeAtestados(atestadosResponse.data || []);
      setEmployeeAbonos(abonosResponse.data?.adjustment_dates || {});
      
      // USAR O SALDO DO BACKEND (fonte única de verdade)
      setEmployeeMonthlyBalance(monthlyBalanceResponse.data || null);
      
      // Buscar bank-hours (não crítico)
      try {
        const bankHoursResponse = await axios.get(`${API}/employees/${employee.id}/bank-hours`);
        setEmployeeBankHoursAdjustment(bankHoursResponse.data?.manual_adjustment_balance || 0);
      } catch (e) {
        console.log("Erro ao buscar bank-hours (não crítico):", e);
        setEmployeeBankHoursAdjustment(0);
      }
      
    } catch (error) {
      console.error("Erro ao buscar dados principais:", error);
      setEmployeeRecords([]);
      setEmployeeAtestados([]);
      setEmployeeAbonos({});
      setEmployeeBankHoursAdjustment(0);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Abonar uma falta (remover da contagem)
  const handleAbonarFalta = async (employee, dateStr) => {
    try {
      await axios.post(`${API}/absence-adjustments`, {
        employee_id: employee.id,
        date: dateStr,
        adjustment_type: "abono",
        reason: "Abonado pelo administrador"
      });
      toast.success("Falta abonada com sucesso!");
      // Recarregar dados do funcionário
      openEmployeeDetails(employee);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao abonar falta");
    }
  };

  // Remover abono
  const handleRemoverAbono = async (employee, abonoId) => {
    try {
      await axios.delete(`${API}/absence-adjustments/${abonoId}`);
      toast.success("Abono removido!");
      openEmployeeDetails(employee);
    } catch (error) {
      toast.error("Erro ao remover abono");
    }
  };

  // Adicionar atestado rápido para uma falta
  const handleAddQuickAtestado = (employee, dateStr, dayOfWeek) => {
    setQuickAtestadoData({
      employee_id: employee.id,
      employee_name: employee.name,
      date: dateStr,
      dayOfWeek: dayOfWeek,
      leave_type: "atestado_medico",
      reason: ""
    });
    setQuickAtestadoDialogOpen(true);
  };

  // Salvar atestado rápido
  const handleSaveQuickAtestado = async () => {
    try {
      await axios.post(`${API}/medical-leaves`, {
        employee_id: quickAtestadoData.employee_id,
        leave_type: quickAtestadoData.leave_type,
        start_date: quickAtestadoData.date,
        end_date: quickAtestadoData.date,
        reason: quickAtestadoData.reason
      });
      toast.success("Atestado cadastrado com sucesso!");
      setQuickAtestadoDialogOpen(false);
      // Recarregar dados do funcionário
      if (employeeDetails) {
        openEmployeeDetails(employeeDetails);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao cadastrar atestado");
    }
  };

  // Verificar se uma data está coberta por atestado
  const isDateCoveredByAtestado = (date, atestados) => {
    // Usar formato local (YYYY-MM-DD) sem conversão UTC
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    for (const atestado of atestados) {
      const startDate = atestado.start_date;
      const endDate = atestado.end_date || atestado.start_date;
      if (dateStr >= startDate && dateStr <= endDate) {
        return atestado;
      }
    }
    return null;
  };

  // NOTA: A função calculateHours foi removida
  // Todos os cálculos de horas agora vêm do backend via endpoint /monthly-balance
  // Esta é a fonte única de verdade para evitar inconsistências entre Admin e Employee

  // Gerar PDF do relatório individual - USANDO DADOS DO BACKEND
  const generateEmployeePDF = (employee, records) => {
    const doc = new jsPDF();
    
    // USAR DADOS DO BACKEND (fonte única de verdade)
    const backendData = employeeMonthlyBalance;
    const workedHours = backendData?.worked_hours || 0;
    const expectedHours = backendData?.expected_hours || 0;
    const balance = backendData?.balance || 0;
    const daysWorked = backendData?.days_worked || 0;
    const expectedDays = backendData?.expected_days || 0;
    const absences = backendData?.absences || 0;
    const isPontoLiberado = backendData?.ponto_liberado !== false;
    
    // Formatar valores
    const workedFormatted = `${Math.floor(workedHours)}h ${Math.round((workedHours % 1) * 60)}min`;
    const expectedFormatted = isPontoLiberado ? `${expectedHours}h` : "Ponto não liberado";
    const balanceFormatted = isPontoLiberado 
      ? (balance >= 0 ? formatHoursMinutes(balance, true) : formatHoursMinutes(balance, true))
      : "N/A";
    
    // Título
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("Relatório Individual de Ponto", 105, 20, { align: "center" });
    
    // Período
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const today = new Date();
    const monthNames = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", 
                        "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
    doc.text(`Período: ${monthNames[today.getMonth()]} de ${today.getFullYear()}`, 105, 28, { align: "center" });
    
    // Dados do funcionário
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Dados do Funcionário", 14, 42);
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const employeeTypeLabels = { funcionario: "Funcionário", estagiario: "Estagiário", terceirizado: "Terceirizado" };
    
    doc.text(`Nome: ${employee.name}`, 14, 52);
    doc.text(`Email: ${employee.email}`, 14, 58);
    doc.text(`Telefone: ${employee.phone || "Não informado"}`, 14, 64);
    doc.text(`Cargo: ${employee.position}`, 14, 70);
    doc.text(`Departamento: ${employee.department}`, 14, 76);
    doc.text(`Tipo: ${employeeTypeLabels[employee.employee_type] || "Funcionário"}`, 14, 82);
    doc.text(`CPF: ${employee.cpf || "Não informado"}`, 14, 88);
    
    // Resumo de horas - USANDO DADOS DO BACKEND
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Resumo de Horas", 14, 102);
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Dias trabalhados: ${daysWorked} de ${expectedDays} esperados`, 14, 112);
    doc.text(`Horas trabalhadas: ${workedFormatted}`, 14, 118);
    doc.text(`Horas esperadas: ${expectedFormatted}`, 14, 124);
    doc.text(`Saldo: ${balanceFormatted}`, 14, 130);
    if (backendData?.manual_adjustment && backendData.manual_adjustment !== 0) {
      doc.text(`Ajustes manuais: ${backendData.manual_adjustment > 0 ? '+' : ''}${backendData.manual_adjustment}h`, 14, 136);
    }
    
    // SEÇÃO DE FALTAS - DESTAQUE EM VERMELHO
    let currentY = backendData?.manual_adjustment && backendData.manual_adjustment !== 0 ? 150 : 144;
    
    if (absences > 0) {
      // Título da seção de faltas
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(220, 38, 38); // Vermelho
      doc.text(`⚠ FALTAS REGISTRADAS (${absences})`, 14, currentY);
      
      // Aviso para o contador
      doc.setFontSize(9);
      doc.setFont("helvetica", "italic");
      doc.text("ATENÇÃO CONTABILIDADE: Existem dias sem registro de ponto no período.", 14, currentY + 8);
      
      currentY += 20;
    } else {
      // Sem faltas
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(22, 163, 74); // Verde
      doc.text("✓ Nenhuma falta registrada no período.", 14, currentY);
      currentY += 15;
    }
    
    // Resetar cor para preto
    doc.setTextColor(0, 0, 0);
    
    // Tabela de registros
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Registros de Ponto", 14, currentY);
    
    // Preparar dados para tabela
    const tableData = records.slice(0, 50).map(record => [
      new Date(record.timestamp).toLocaleDateString('pt-BR'),
      new Date(record.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
      record.record_type === 'entry' ? 'Entrada' : 'Saída',
      record.geofence_status === 'inside' ? 'Dentro' : 
        record.geofence_status === 'outside' ? 'Fora' : '-'
    ]);
    
    autoTable(doc, {
      startY: currentY + 6,
      head: [['Data', 'Hora', 'Tipo', 'Cerca']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [14, 165, 233] },
      styles: { fontSize: 9 }
    });
    
    // Rodapé
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, 14, 285);
      doc.text(`Página ${i} de ${pageCount}`, 196, 285, { align: "right" });
    }
    
    // Salvar
    doc.save(`relatorio_${employee.name.replace(/\s+/g, '_')}_${today.getMonth() + 1}_${today.getFullYear()}.pdf`);
    toast.success("PDF gerado com sucesso!");
  };

  // Baixar Termo de Ciência - Uso do Celular
  const downloadTermoCelular = async (employee) => {
    try {
      toast.loading("Gerando Termo de Ciência...", { id: "termo-loading" });
      
      const response = await axios.get(`${API}/employees/${employee.id}/termo-celular/pdf`, {
        responseType: 'blob'
      });
      
      // Criar URL do blob e fazer download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Termo_Celular_${employee.name.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Termo de Ciência baixado com sucesso!", { id: "termo-loading" });
    } catch (error) {
      console.error("Erro ao baixar termo:", error);
      toast.error(error.response?.data?.detail || "Erro ao gerar Termo de Ciência", { id: "termo-loading" });
    }
  };

  // Função para gerar PDF do histórico de horas com gráfico
  const generateHistoryPDF = (employee, historyData) => {
    if (!historyData || !historyData.history || historyData.history.length === 0) {
      toast.error("Sem dados de histórico para gerar relatório");
      return;
    }
    
    const doc = new jsPDF();
    
    // Cores
    const primaryColor = [0, 120, 167];
    const secondaryColor = [100, 116, 139];
    const positiveColor = [34, 197, 94];
    const negativeColor = [239, 68, 68];
    
    // Cabeçalho
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 210, 35, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("Relatório de Banco de Horas", 105, 15, { align: "center" });
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(employee.name, 105, 23, { align: "center" });
    doc.setFontSize(10);
    doc.text(`Emitido em: ${new Date().toLocaleDateString('pt-BR')}`, 105, 30, { align: "center" });
    
    let yPos = 45;
    
    // Resumo geral
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Resumo Geral", 14, yPos);
    yPos += 8;
    
    // Cards de resumo
    const cardWidth = 45;
    const cardHeight = 20;
    const cards = [
      { label: "Total Trabalhadas", value: `${historyData.total_worked_hours}h`, color: [59, 130, 246] },
      { label: "Total Esperadas", value: `${historyData.total_expected_hours}h`, color: [100, 116, 139] },
      { label: "Total Faltas", value: `${historyData.total_absences}`, color: [239, 68, 68] },
      { label: "Saldo Final", value: `${historyData.total_balance >= 0 ? '+' : ''}${historyData.total_balance}h`, 
        color: historyData.total_balance >= 0 ? positiveColor : negativeColor }
    ];
    
    cards.forEach((card, i) => {
      const x = 14 + (i * 48);
      doc.setFillColor(...card.color);
      doc.roundedRect(x, yPos, cardWidth, cardHeight, 2, 2, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.text(card.label, x + cardWidth/2, yPos + 7, { align: "center" });
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(card.value, x + cardWidth/2, yPos + 15, { align: "center" });
    });
    
    yPos += 30;
    
    // Gráfico de barras do saldo mensal
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Evolução do Saldo Mensal", 14, yPos);
    yPos += 10;
    
    const chartWidth = 180;
    const chartHeight = 50;
    const chartX = 14;
    const chartY = yPos;
    
    // Fundo do gráfico
    doc.setFillColor(248, 250, 252);
    doc.rect(chartX, chartY, chartWidth, chartHeight, 'F');
    
    // Linha zero
    const zeroY = chartY + chartHeight / 2;
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.line(chartX, zeroY, chartX + chartWidth, zeroY);
    
    // Barras
    const barWidth = Math.min(15, (chartWidth - 20) / historyData.history.length);
    const maxBalance = Math.max(...historyData.history.map(h => Math.abs(h.accumulated_balance)), 10);
    const scale = (chartHeight / 2 - 5) / maxBalance;
    
    historyData.history.forEach((month, i) => {
      const x = chartX + 10 + (i * (barWidth + 2));
      const barHeight = Math.abs(month.accumulated_balance) * scale;
      
      if (month.accumulated_balance >= 0) {
        doc.setFillColor(...positiveColor);
        doc.rect(x, zeroY - barHeight, barWidth, barHeight, 'F');
      } else {
        doc.setFillColor(...negativeColor);
        doc.rect(x, zeroY, barWidth, barHeight, 'F');
      }
      
      // Label do mês
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(6);
      doc.setFont("helvetica", "normal");
      doc.text(month.month_name, x + barWidth/2, chartY + chartHeight + 4, { align: "center" });
    });
    
    yPos = chartY + chartHeight + 15;
    
    // Tabela de histórico
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Detalhamento Mensal", 14, yPos);
    yPos += 8;
    
    // Cabeçalho da tabela
    const headers = ["Mês/Ano", "Trabalhadas", "Esperadas", "Faltas", "Atestados", "Ajustes", "Saldo", "Acumulado"];
    const colWidths = [25, 22, 22, 18, 22, 20, 22, 25];
    
    doc.setFillColor(...primaryColor);
    doc.rect(14, yPos, 182, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    
    let colX = 14;
    headers.forEach((header, i) => {
      doc.text(header, colX + colWidths[i]/2, yPos + 5.5, { align: "center" });
      colX += colWidths[i];
    });
    
    yPos += 8;
    
    // Dados da tabela
    doc.setFont("helvetica", "normal");
    historyData.history.forEach((month, idx) => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
      
      // Fundo alternado
      if (idx % 2 === 0) {
        doc.setFillColor(248, 250, 252);
        doc.rect(14, yPos, 182, 7, 'F');
      }
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(7);
      
      const row = [
        month.period,
        `${month.worked_hours}h`,
        `${month.expected_hours}h`,
        month.absences.toString(),
        month.medical_leaves.toString(),
        month.adjustments !== 0 ? `${month.adjustments > 0 ? '+' : ''}${month.adjustments}h` : '-',
        `${month.month_balance >= 0 ? '+' : ''}${month.month_balance}h`,
        `${month.accumulated_balance >= 0 ? '+' : ''}${month.accumulated_balance}h`
      ];
      
      colX = 14;
      row.forEach((cell, i) => {
        // Cor especial para saldo e acumulado
        if (i === 6) {
          doc.setTextColor(month.month_balance >= 0 ? 34 : 239, month.month_balance >= 0 ? 197 : 68, month.month_balance >= 0 ? 94 : 68);
        } else if (i === 7) {
          doc.setTextColor(month.accumulated_balance >= 0 ? 34 : 239, month.accumulated_balance >= 0 ? 197 : 68, month.accumulated_balance >= 0 ? 94 : 68);
          doc.setFont("helvetica", "bold");
        } else {
          doc.setTextColor(0, 0, 0);
          doc.setFont("helvetica", "normal");
        }
        doc.text(cell, colX + colWidths[i]/2, yPos + 5, { align: "center" });
        colX += colWidths[i];
      });
      
      yPos += 7;
    });
    
    // Rodapé
    yPos += 10;
    if (yPos > 270) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setDrawColor(200, 200, 200);
    doc.line(14, yPos, 196, yPos);
    yPos += 5;
    
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(8);
    doc.setFont("helvetica", "italic");
    doc.text(`Período: Desde ${historyData.ponto_liberado_at ? new Date(historyData.ponto_liberado_at).toLocaleDateString('pt-BR') : 'N/A'} até ${new Date().toLocaleDateString('pt-BR')}`, 14, yPos);
    doc.text(`Total de ajustes manuais: ${historyData.total_adjustments > 0 ? '+' : ''}${historyData.total_adjustments}h`, 14, yPos + 5);
    
    // Salvar
    doc.save(`Historico_Banco_Horas_${employee.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success("Relatório de histórico exportado com sucesso!");
  };

  // Formatar tipo de colaborador
  const formatEmployeeType = (type) => {
    const types = {
      funcionario: { label: "Funcionário", color: "bg-cyan-100 text-ciaponto-navy", icon: Briefcase },
      estagiario: { label: "Estagiário", color: "bg-green-100 text-green-700", icon: GraduationCap },
      terceirizado: { label: "Terceirizado", color: "bg-orange-100 text-orange-700", icon: Users }
    };
    return types[type] || types.funcionario;
  };

  const filteredEmployees = employees.filter(emp =>
    emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.department.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">Funcionários</h1>
          <p className="text-slate-600">Gerencie sua equipe</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark" data-testid="add-employee-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Novo Funcionário
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md max-h-[85vh] flex flex-col p-0">
            <DialogHeader className="px-4 pt-4 pb-2 flex-shrink-0">
              <DialogTitle className="font-heading">Cadastrar Funcionário</DialogTitle>
              <DialogDescription>
                Preencha os dados do novo funcionário.
              </DialogDescription>
            </DialogHeader>
            
            {/* Área de scroll do conteúdo */}
            <div className="flex-1 overflow-y-auto px-4 pb-2">
            {/* Modal de Câmera com Instruções de Qualidade */}
            {showCamera && (
              <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl p-4 w-full max-w-md">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium text-slate-900">Foto do Funcionário</h3>
                    <button type="button" onClick={stopCamera} className="p-1 hover:bg-slate-100 rounded-full">
                      <X className="w-5 h-5 text-slate-500" />
                    </button>
                  </div>
                  
                  {/* Instruções de qualidade */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                    <p className="text-xs font-medium text-amber-800 mb-2">📸 Requisitos para foto válida:</p>
                    <ul className="text-xs text-amber-700 space-y-1">
                      <li>✓ <strong>Local bem iluminado</strong> (evite sombras)</li>
                      <li>✓ <strong>Rosto centralizado</strong> no círculo</li>
                      <li>✓ <strong>Olhando para a câmera</strong></li>
                      <li>✓ <strong>Sem óculos escuros</strong> ou bonés</li>
                      <li>✓ <strong>Foto nítida</strong> (não tremer)</li>
                    </ul>
                  </div>
                  
                  <div className="relative rounded-xl overflow-hidden bg-black aspect-[4/3]">
                    <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover transform scale-x-[-1]" />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="w-48 h-48 border-4 border-white/50 rounded-full">
                        <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-white/90 px-2 py-1 rounded text-xs font-medium">
                          Enquadre o rosto aqui
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-sm text-slate-600 text-center my-3">
                    Esta foto será usada para verificar identidade no registro de ponto
                  </p>
                  
                  <Button type="button" onClick={capturePhoto} className="w-full bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark">
                    <Camera className="w-4 h-4 mr-2" />
                    Capturar Foto
                  </Button>
                </div>
              </div>
            )}
            
            {/* Canvas oculto para captura */}
            <canvas ref={canvasRef} className="hidden" />
            
            <form id="employee-form" onSubmit={handleSubmit} className="space-y-3">
              {/* Foto de Perfil - Layout melhorado para mobile */}
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                <Label className="text-sm font-medium mb-2 block">Foto de Perfil *</Label>
                <div className="flex items-center gap-3">
                  {/* Preview da foto */}
                  <div className="relative flex-shrink-0">
                    {photoPreview || formData.profile_photo_url ? (
                      <img 
                        src={photoPreview || formData.profile_photo_url} 
                        alt="Preview" 
                        className="w-14 h-14 rounded-full object-cover border-3 border-[#1CA1BA] shadow-md cursor-pointer"
                        onClick={() => setViewingPhoto(photoPreview || formData.profile_photo_url)}
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center border-2 border-dashed border-slate-300">
                        <Camera className="w-5 h-5 text-slate-400" />
                      </div>
                    )}
                    {uploadingPhoto && (
                      <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      </div>
                    )}
                  </div>
                  
                  {/* Botões */}
                  <div className="flex-1 flex flex-wrap gap-2">
                    <Button type="button" onClick={startCamera} className="bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark text-xs px-3 py-1.5 h-auto" size="sm">
                      <Camera className="w-3.5 h-3.5 mr-1.5" />
                      Câmera
                    </Button>
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setUploadingPhoto(true);
                            try {
                              const formDataUpload = new FormData();
                              formDataUpload.append("file", file);
                              const response = await axios.post(`${API}/upload-image`, formDataUpload, {
                                headers: { "Content-Type": "multipart/form-data" }
                              });
                              setFormData(prev => ({ ...prev, profile_photo_url: response.data.image_url }));
                              setPhotoPreview(URL.createObjectURL(file));
                              toast.success("Foto enviada!");
                            } catch (error) {
                              toast.error("Erro ao enviar foto");
                            } finally {
                              setUploadingPhoto(false);
                            }
                          }
                        }}
                      />
                      <span className="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-md hover:bg-white bg-white">
                        <Upload className="w-3.5 h-3.5 mr-1.5" />
                        Arquivo
                      </span>
                    </label>
                    {(photoPreview || formData.profile_photo_url) && (
                      <Button type="button" onClick={resetPhoto} variant="ghost" size="sm" className="text-red-500 hover:text-red-600 hover:bg-red-50 px-2 py-1.5 h-auto">
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Nome */}
              <div className="space-y-1">
                <Label className="text-xs font-medium text-slate-600">Nome completo *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Nome do colaborador"
                  data-testid="employee-name-input"
                  className="h-9 text-sm"
                />
              </div>

              {/* Email e Telefone */}
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-slate-600">Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    placeholder="email@empresa.com"
                    data-testid="employee-email-input"
                    className="h-9 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-slate-600">Telefone *</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    placeholder="(00) 00000-0000"
                    data-testid="employee-phone-input"
                    className="h-9 text-sm"
                  />
                </div>
              </div>

              {/* CPF */}
              <div className="space-y-1">
                <Label className="text-xs font-medium text-slate-600">CPF</Label>
                <Input
                  value={formData.cpf}
                  onChange={(e) => {
                    // Formatar CPF: 000.000.000-00
                    let value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 11) {
                      value = value.replace(/(\d{3})(\d)/, '$1.$2');
                      value = value.replace(/(\d{3})(\d)/, '$1.$2');
                      value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
                    }
                    setFormData({ ...formData, cpf: value });
                  }}
                  placeholder="000.000.000-00"
                  data-testid="employee-cpf-input"
                  className="h-9 text-sm"
                  maxLength={14}
                />
              </div>

              {/* Cargo e Departamento */}
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-slate-600">Cargo *</Label>
                  <Input
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    required
                    placeholder="Ex: Técnico"
                    data-testid="employee-position-input"
                    className="h-9 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-slate-600">Departamento *</Label>
                  <Input
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    required
                    placeholder="Ex: Suporte"
                    data-testid="employee-department-input"
                    className="h-9 text-sm"
                  />
                </div>
              </div>
              
              {/* Tipo de Colaborador - Compacto */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600">Tipo *</Label>
                <div className="grid grid-cols-3 gap-1.5">
                  <label 
                    className={`flex items-center justify-center py-2 px-1 rounded-md border-2 cursor-pointer transition-all ${
                      formData.employee_type === "funcionario" 
                        ? "border-[#1CA1BA] bg-sky-50 text-ciaponto-navy" 
                        : "border-slate-200 hover:border-slate-300 bg-white"
                    }`}
                  >
                    <input
                      type="radio"
                      name="employee_type"
                      value="funcionario"
                      checked={formData.employee_type === "funcionario"}
                      onChange={(e) => setFormData({ ...formData, employee_type: e.target.value })}
                      className="sr-only"
                    />
                    <span className="text-xs font-medium">Funcionário</span>
                  </label>
                  <label 
                    className={`flex items-center justify-center py-2 px-1 rounded-md border-2 cursor-pointer transition-all ${
                      formData.employee_type === "estagiario" 
                        ? "border-green-500 bg-green-50 text-green-700" 
                        : "border-slate-200 hover:border-slate-300 bg-white"
                    }`}
                  >
                    <input
                      type="radio"
                      name="employee_type"
                      value="estagiario"
                      checked={formData.employee_type === "estagiario"}
                      onChange={(e) => setFormData({ ...formData, employee_type: e.target.value })}
                      className="sr-only"
                    />
                    <span className="text-xs font-medium">Estagiário</span>
                  </label>
                  <label 
                    className={`flex items-center justify-center py-2 px-1 rounded-md border-2 cursor-pointer transition-all ${
                      formData.employee_type === "terceirizado" 
                        ? "border-orange-500 bg-orange-50 text-orange-700" 
                        : "border-slate-200 hover:border-slate-300 bg-white"
                    }`}
                  >
                    <input
                      type="radio"
                      name="employee_type"
                      value="terceirizado"
                      checked={formData.employee_type === "terceirizado"}
                      onChange={(e) => setFormData({ ...formData, employee_type: e.target.value })}
                      className="sr-only"
                    />
                    <span className="text-xs font-medium">Terceirizado</span>
                  </label>
                </div>
              </div>

              {/* Senha e Horas - Compacto */}
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1 bg-slate-50 p-2 rounded-md border border-slate-200">
                  <Label className="text-xs font-medium text-slate-600 flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    Senha
                  </Label>
                  <Input
                    type="password"
                    placeholder="Mín. 6 caracteres"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    data-testid="employee-password-input"
                    className="h-8 text-sm"
                  />
                  <p className="text-[10px] text-slate-500">Padrão: 123456</p>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-medium text-slate-600">Horas/semana</Label>
                  <Input
                    type="number"
                    value={formData.weekly_hours}
                    onChange={(e) => setFormData({ ...formData, weekly_hours: parseFloat(e.target.value) })}
                    required
                    data-testid="employee-hours-input"
                    className="h-9 text-sm"
                    placeholder="44"
                  />
                  <p className="text-[10px] text-slate-500">CLT: 44h = 220h/mês</p>
                </div>
              </div>
              
              {/* Cerca Eletrônica com Mapa - Ultra Compacta */}
              <div className="border-t pt-2 mt-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Target className="w-3.5 h-3.5 text-orange-500" />
                    <Label className="text-xs font-semibold">Cerca Eletrônica</Label>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.geofence_enabled}
                      onChange={(e) => setFormData({ ...formData, geofence_enabled: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-8 h-4 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-orange-500"></div>
                  </label>
                </div>
                
                {formData.geofence_enabled && (
                  <div className="space-y-2 bg-orange-50 p-2 rounded-md border border-orange-200 mt-2">
                    {/* Botão Localização + Raio em linha */}
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-6 text-[10px] px-2 flex-shrink-0"
                        onClick={() => {
                          if (navigator.geolocation) {
                            navigator.geolocation.getCurrentPosition(
                              (position) => {
                                setFormData({
                                  ...formData,
                                  geofence_latitude: position.coords.latitude,
                                  geofence_longitude: position.coords.longitude
                                });
                                toast.success("Local obtido!");
                              },
                              () => toast.error("Erro")
                            );
                          }
                        }}
                      >
                        <MapPin className="w-3 h-3 mr-1" />
                        Minha Loc.
                      </Button>
                      <div className="flex-1 flex items-center gap-2">
                        <span className="text-[10px] text-slate-600 whitespace-nowrap">Raio: {formData.geofence_radius}m</span>
                        <input
                          type="range"
                          min="50"
                          max="2000"
                          step="50"
                          value={formData.geofence_radius}
                          onChange={(e) => setFormData({ ...formData, geofence_radius: parseInt(e.target.value) })}
                          className="flex-1 h-1.5 bg-orange-200 rounded-lg appearance-none cursor-pointer accent-orange-500"
                        />
                      </div>
                    </div>
                    
                    {/* Mapa Compacto */}
                    <div className="rounded-md overflow-hidden border border-orange-200" style={{ height: "120px" }}>
                      <MapContainer
                        center={[
                          formData.geofence_latitude || -23.5505,
                          formData.geofence_longitude || -46.6333
                        ]}
                        zoom={14}
                        style={{ height: "100%", width: "100%" }}
                        key={`form-${formData.geofence_latitude}-${formData.geofence_longitude}`}
                      >
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        {formData.geofence_latitude && formData.geofence_longitude && (
                          <>
                            <Marker position={[formData.geofence_latitude, formData.geofence_longitude]} />
                            <Circle
                              center={[formData.geofence_latitude, formData.geofence_longitude]}
                              radius={formData.geofence_radius}
                              pathOptions={{ color: '#f97316', fillColor: '#fed7aa', fillOpacity: 0.4 }}
                            />
                          </>
                        )}
                        <MapClickHandler 
                          onMapClick={(lat, lng) => {
                            setFormData({
                              ...formData,
                              geofence_latitude: lat,
                              geofence_longitude: lng
                            });
                          }}
                        />
                      </MapContainer>
                    </div>
                    <p className="text-[9px] text-orange-600 text-center">📍 Clique no mapa para marcar o local</p>
                  </div>
                )}
              </div>
              
            </form>
            </div>{/* Fecha área de scroll */}
            
            {/* Footer fixo com botões */}
            <div className="flex-shrink-0 border-t bg-white px-4 py-3 flex gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                Cancelar
              </Button>
              <Button 
                type="submit" 
                form="employee-form"
                className="flex-1 bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark" 
                data-testid="submit-employee-btn"
              >
                Cadastrar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Buscar funcionário..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
          data-testid="search-employee-input"
        />
      </div>

      {/* Employees List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1CA1BA]"></div>
        </div>
      ) : filteredEmployees.length > 0 ? (
        <div className="grid gap-4">
          {filteredEmployees.map((employee) => (
            <Card 
              key={employee.id} 
              className="border border-slate-200 cursor-pointer hover:shadow-md hover:border-sky-200 transition-all" 
              data-testid={`employee-card-${employee.id}`}
              onClick={() => openEmployeeDetails(employee)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {employee.profile_photo_url ? (
                      <div className="relative">
                        <img 
                          src={employee.profile_photo_url} 
                          alt={employee.name}
                          className="w-12 h-12 rounded-full object-cover border-2 border-sky-200"
                        />
                      </div>
                    ) : (
                      <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center">
                        <span className="font-heading font-bold text-ciaponto-cyan">
                          {employee.name.split(" ").map(n => n[0]).join("").slice(0, 2)}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-slate-900">{employee.name}</p>
                      <p className="text-sm text-slate-500">{employee.position} • {employee.department}</p>
                      {/* Badge do tipo de colaborador */}
                      {employee.employee_type && (
                        <span className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${formatEmployeeType(employee.employee_type).color}`}>
                          {formatEmployeeType(employee.employee_type).label}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-4">
                    <div className="hidden sm:flex items-center gap-4 text-sm text-slate-500">
                      <div className="flex items-center gap-1">
                        <Mail className="w-4 h-4" />
                        {employee.email}
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      employee.is_active ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"
                    }`}>
                      {employee.is_active ? "Ativo" : "Inativo"}
                    </span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" onClick={(e) => e.stopPropagation()}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEmployeeDetails(employee); }}>
                          <Eye className="w-4 h-4 mr-2" />
                          Ver Detalhes
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditDialog(employee); }}>
                          <Pencil className="w-4 h-4 mr-2 text-ciaponto-cyan" />
                          Editar Dados
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openScheduleDialog(employee); }}>
                          <Calendar className="w-4 h-4 mr-2" />
                          Editar Escala
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openGeofenceDialog(employee); }}>
                          <Target className="w-4 h-4 mr-2 text-orange-500" />
                          Editar Cerca Eletrônica
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); downloadTermoCelular(employee); }} className="text-amber-600">
                          <FileText className="w-4 h-4 mr-2" />
                          Baixar Termo Celular
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDelete(employee.id); }} className="text-red-600">
                          <Trash2 className="w-4 h-4 mr-2" />
                          Excluir
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border border-slate-200">
          <CardContent className="p-12 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">Nenhum funcionário cadastrado</p>
            <p className="text-sm text-slate-500">Clique em &quot;Novo Funcionário&quot; para começar</p>
          </CardContent>
        </Card>
      )}
      
      {/* Modal de Edição de Escala */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Editar Escala de Trabalho</DialogTitle>
            <DialogDescription>
              {selectedEmployee?.name} - Selecione o tipo de escala
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* NOVO: Tipo de Escala */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Tipo de Escala
              </Label>
              <div className="grid grid-cols-1 gap-2">
                <button
                  type="button"
                  onClick={() => handleScheduleTypeChange("standard")}
                  className={`p-4 rounded-lg text-left transition-all border-2 ${
                    selectedScheduleType === "standard"
                      ? "border-[#1CA1BA] bg-sky-50"
                      : "border-slate-200 hover:border-slate-300 bg-white"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">Escala Padrão</p>
                      <p className="text-xs text-slate-500 mt-1">
                        Mesmo número de horas para todos os dias de trabalho
                      </p>
                    </div>
                    {selectedScheduleType === "standard" && (
                      <CheckCircle className="w-5 h-5 text-ciaponto-cyan" />
                    )}
                  </div>
                </button>
                
                <button
                  type="button"
                  onClick={() => handleScheduleTypeChange("44h_semana_comercial")}
                  className={`p-4 rounded-lg text-left transition-all border-2 ${
                    selectedScheduleType === "44h_semana_comercial"
                      ? "border-amber-500 bg-amber-50"
                      : "border-slate-200 hover:border-slate-300 bg-white"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">44h Comercial</p>
                      <p className="text-xs text-slate-500 mt-1">
                        <strong>8h de Seg-Sex + 4h no Sábado</strong> = 44h semanais
                      </p>
                      <p className="text-xs text-amber-600 mt-1">
                        Ideal para empresas que não trabalham sábado à tarde
                      </p>
                    </div>
                    {selectedScheduleType === "44h_semana_comercial" && (
                      <CheckCircle className="w-5 h-5 text-amber-500" />
                    )}
                  </div>
                </button>
              </div>
            </div>
            
            {/* Seletor de dias - só mostra se escala padrão */}
            {selectedScheduleType === "standard" && (
              <div className="space-y-3">
                <Label>Dias de trabalho</Label>
                <div className="grid grid-cols-7 gap-2">
                  {weekDays.map((day) => (
                    <button
                      key={day.id}
                      type="button"
                      onClick={() => toggleWorkDay(day.id)}
                      className={`p-3 rounded-lg text-center transition-all ${
                        selectedWorkDays.includes(day.id)
                          ? "bg-[#1CA1BA] text-white font-medium shadow-md"
                          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                      }`}
                    >
                      <span className="text-xs block">{day.name}</span>
                    </button>
                  ))}
                </div>
                <p className="text-sm text-slate-500">
                  {selectedWorkDays.length} dias × 8h = {selectedWorkDays.length * 8}h/semana
                </p>
              </div>
            )}
            
            {/* Info visual para escala 44h comercial */}
            {selectedScheduleType === "44h_semana_comercial" && (
              <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                <p className="text-sm font-medium text-amber-800 mb-2">Distribuição de horas:</p>
                <div className="grid grid-cols-6 gap-1 text-center">
                  {["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((day, idx) => (
                    <div key={day} className={`p-2 rounded ${idx < 5 ? "bg-cyan-100" : "bg-amber-200"}`}>
                      <p className="text-xs font-medium text-slate-700">{day}</p>
                      <p className="text-sm font-bold text-slate-900">{idx < 5 ? "8h" : "4h"}</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-amber-700 mt-2 text-center">
                  Total: 40h (Seg-Sex) + 4h (Sáb) = <strong>44h semanais</strong>
                </p>
              </div>
            )}
            
            {/* Rotação de fim de semana */}
            <div className="space-y-3">
              <Label>Fim de semana alternado</Label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setWeekendRotation("none")}
                  className={`p-3 rounded-lg text-center transition-all ${
                    weekendRotation === "none"
                      ? "bg-slate-700 text-white font-medium shadow-md"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  <span className="text-xs block">Sem rotação</span>
                </button>
                <button
                  type="button"
                  onClick={() => setWeekendRotation("odd")}
                  className={`p-3 rounded-lg text-center transition-all ${
                    weekendRotation === "odd"
                      ? "bg-orange-500 text-white font-medium shadow-md"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  <span className="text-xs block">FDS Ímpares</span>
                </button>
                <button
                  type="button"
                  onClick={() => setWeekendRotation("even")}
                  className={`p-3 rounded-lg text-center transition-all ${
                    weekendRotation === "even"
                      ? "bg-purple-500 text-white font-medium shadow-md"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  <span className="text-xs block">FDS Pares</span>
                </button>
              </div>
              <p className="text-xs text-slate-500">
                {weekendRotation === "none" && "Trabalha todos os fins de semana marcados acima"}
                {weekendRotation === "odd" && "Trabalha nos fins de semana das semanas 1, 3, 5... do mês"}
                {weekendRotation === "even" && "Trabalha nos fins de semana das semanas 2, 4... do mês"}
              </p>
            </div>
            
            {/* Resumo */}
            <div className={`p-3 rounded-lg border ${
              selectedScheduleType === "44h_semana_comercial" 
                ? "bg-amber-50 border-amber-200" 
                : "bg-sky-50 border-sky-200"
            }`}>
              <p className={`text-sm font-medium ${
                selectedScheduleType === "44h_semana_comercial" 
                  ? "text-amber-800" 
                  : "text-ciaponto-navy"
              }`}>
                Escala: {formatSchedule(selectedWorkDays, weekendRotation, selectedScheduleType)}
              </p>
              <p className={`text-sm mt-1 ${
                selectedScheduleType === "44h_semana_comercial" 
                  ? "text-amber-600" 
                  : "text-ciaponto-cyan"
              }`}>
                Meta mensal aproximada: {selectedScheduleType === "44h_semana_comercial" ? "44" : selectedWorkDays.length * 8} × 4.33 = {
                  selectedScheduleType === "44h_semana_comercial" 
                    ? "~190h" 
                    : `~${Math.round(selectedWorkDays.length * 8 * 4.33)}h`
                }
                {weekendRotation !== "none" && " (ajustado para rotação)"}
              </p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setScheduleDialogOpen(false)} 
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button 
              type="button"
              onClick={handleSaveSchedule}
              className="flex-1 bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark"
            >
              Salvar Escala
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Edição da Cerca Eletrônica com Mapa */}
      <Dialog open={geofenceDialogOpen} onOpenChange={setGeofenceDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Target className="w-5 h-5 text-orange-500" />
              Configurar Cerca Eletrônica
            </DialogTitle>
            <DialogDescription>
              {selectedEmployee?.name} - Clique no mapa para definir o local
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Toggle de ativação */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-900">Ativar Cerca Eletrônica</p>
                <p className="text-xs text-slate-500">Limita onde o funcionário pode bater ponto</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={geofenceFormData.geofence_enabled}
                  onChange={(e) => setGeofenceFormData({ ...geofenceFormData, geofence_enabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
              </label>
            </div>

            {geofenceFormData.geofence_enabled && (
              <div className="space-y-4">
                {/* Mapa Interativo */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">📍 Clique no mapa para marcar o local</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={getCurrentLocationForGeofence}
                    >
                      <MapPin className="w-4 h-4 mr-1" />
                      Minha Localização
                    </Button>
                  </div>
                  <div className="rounded-lg overflow-hidden border-2 border-orange-200" style={{ height: "300px" }}>
                    <MapContainer
                      center={[
                        geofenceFormData.geofence_latitude || -23.5505,
                        geofenceFormData.geofence_longitude || -46.6333
                      ]}
                      zoom={15}
                      style={{ height: "100%", width: "100%" }}
                      key={`${geofenceFormData.geofence_latitude}-${geofenceFormData.geofence_longitude}`}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      {geofenceFormData.geofence_latitude && geofenceFormData.geofence_longitude && (
                        <>
                          <Marker position={[geofenceFormData.geofence_latitude, geofenceFormData.geofence_longitude]} />
                          <Circle
                            center={[geofenceFormData.geofence_latitude, geofenceFormData.geofence_longitude]}
                            radius={geofenceFormData.geofence_radius}
                            pathOptions={{ color: '#f97316', fillColor: '#fed7aa', fillOpacity: 0.4 }}
                          />
                        </>
                      )}
                      <MapClickHandler 
                        onMapClick={(lat, lng) => {
                          setGeofenceFormData({
                            ...geofenceFormData,
                            geofence_latitude: lat,
                            geofence_longitude: lng
                          });
                        }}
                      />
                    </MapContainer>
                  </div>
                  {geofenceFormData.geofence_latitude && (
                    <p className="text-xs text-slate-500 text-center">
                      Local selecionado: {geofenceFormData.geofence_latitude.toFixed(6)}, {geofenceFormData.geofence_longitude.toFixed(6)}
                    </p>
                  )}
                </div>

                {/* Slider do raio */}
                <div className="space-y-2 bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <Label className="text-sm font-medium">
                    Raio da Cerca: <span className="text-orange-600 font-bold">{geofenceFormData.geofence_radius}m</span>
                  </Label>
                  <input
                    type="range"
                    min="50"
                    max="2000"
                    step="50"
                    value={geofenceFormData.geofence_radius}
                    onChange={(e) => setGeofenceFormData({ ...geofenceFormData, geofence_radius: parseInt(e.target.value) })}
                    className="w-full h-2 bg-orange-200 rounded-lg appearance-none cursor-pointer accent-orange-500"
                  />
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>50m</span>
                    <span>500m</span>
                    <span>1km</span>
                    <span>2km</span>
                  </div>
                </div>

                {/* Aviso */}
                <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                  <p className="text-xs text-amber-700">
                    <strong>⚠️</strong> O funcionário só poderá registrar ponto dentro do círculo laranja no mapa.
                  </p>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex gap-3 pt-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setGeofenceDialogOpen(false)} 
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button 
              type="button"
              onClick={handleSaveGeofence}
              className="flex-1 bg-orange-500 hover:bg-orange-600"
              disabled={geofenceFormData.geofence_enabled && (!geofenceFormData.geofence_latitude || !geofenceFormData.geofence_longitude)}
            >
              Salvar Cerca
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Edição de Funcionário */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-4 pt-4 pb-2 flex-shrink-0 border-b">
            <DialogTitle className="font-heading flex items-center gap-2">
              <Pencil className="w-5 h-5 text-ciaponto-cyan" />
              Editar Funcionário
            </DialogTitle>
            <DialogDescription>
              Atualize os dados do funcionário
            </DialogDescription>
          </DialogHeader>
          
          {editFormData && (
            <form onSubmit={handleEditSubmit} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {/* Nome */}
              <div className="space-y-1">
                <Label className="text-xs font-medium text-slate-600">Nome completo *</Label>
                <Input
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  required
                  placeholder="Nome do colaborador"
                  className="h-9 text-sm"
                />
              </div>

              {/* Email e Telefone */}
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-slate-600">Email *</Label>
                  <Input
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                    required
                    placeholder="email@empresa.com"
                    className="h-9 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-slate-600">Telefone *</Label>
                  <Input
                    value={editFormData.phone}
                    onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                    required
                    placeholder="(00) 00000-0000"
                    className="h-9 text-sm"
                  />
                </div>
              </div>

              {/* CPF */}
              <div className="space-y-1">
                <Label className="text-xs font-medium text-slate-600">CPF</Label>
                <Input
                  value={editFormData.cpf}
                  onChange={(e) => {
                    let value = e.target.value.replace(/\D/g, '');
                    if (value.length <= 11) {
                      value = value.replace(/(\d{3})(\d)/, '$1.$2');
                      value = value.replace(/(\d{3})(\d)/, '$1.$2');
                      value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
                    }
                    setEditFormData({ ...editFormData, cpf: value });
                  }}
                  placeholder="000.000.000-00"
                  className="h-9 text-sm"
                  maxLength={14}
                />
              </div>

              {/* Cargo e Departamento */}
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-slate-600">Cargo *</Label>
                  <Input
                    value={editFormData.position}
                    onChange={(e) => setEditFormData({ ...editFormData, position: e.target.value })}
                    required
                    placeholder="Ex: Técnico"
                    className="h-9 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-slate-600">Departamento *</Label>
                  <Input
                    value={editFormData.department}
                    onChange={(e) => setEditFormData({ ...editFormData, department: e.target.value })}
                    required
                    placeholder="Ex: Suporte"
                    className="h-9 text-sm"
                  />
                </div>
              </div>
              
              {/* Tipo de Colaborador */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600">Tipo de Colaborador</Label>
                <div className="grid grid-cols-3 gap-1.5">
                  <label 
                    className={`flex items-center justify-center py-2 px-1 rounded-md border-2 cursor-pointer transition-all ${
                      editFormData.employee_type === "funcionario" 
                        ? "border-[#1CA1BA] bg-sky-50 text-ciaponto-navy" 
                        : "border-slate-200 hover:border-slate-300 bg-white"
                    }`}
                  >
                    <input
                      type="radio"
                      name="edit_employee_type"
                      value="funcionario"
                      checked={editFormData.employee_type === "funcionario"}
                      onChange={(e) => setEditFormData({ ...editFormData, employee_type: e.target.value })}
                      className="sr-only"
                    />
                    <span className="text-xs font-medium">Funcionário</span>
                  </label>
                  <label 
                    className={`flex items-center justify-center py-2 px-1 rounded-md border-2 cursor-pointer transition-all ${
                      editFormData.employee_type === "estagiario" 
                        ? "border-green-500 bg-green-50 text-green-700" 
                        : "border-slate-200 hover:border-slate-300 bg-white"
                    }`}
                  >
                    <input
                      type="radio"
                      name="edit_employee_type"
                      value="estagiario"
                      checked={editFormData.employee_type === "estagiario"}
                      onChange={(e) => setEditFormData({ ...editFormData, employee_type: e.target.value })}
                      className="sr-only"
                    />
                    <span className="text-xs font-medium">Estagiário</span>
                  </label>
                  <label 
                    className={`flex items-center justify-center py-2 px-1 rounded-md border-2 cursor-pointer transition-all ${
                      editFormData.employee_type === "terceirizado" 
                        ? "border-orange-500 bg-orange-50 text-orange-700" 
                        : "border-slate-200 hover:border-slate-300 bg-white"
                    }`}
                  >
                    <input
                      type="radio"
                      name="edit_employee_type"
                      value="terceirizado"
                      checked={editFormData.employee_type === "terceirizado"}
                      onChange={(e) => setEditFormData({ ...editFormData, employee_type: e.target.value })}
                      className="sr-only"
                    />
                    <span className="text-xs font-medium">Terceirizado</span>
                  </label>
                </div>
              </div>

              {/* Nova Senha e Horas/Dia */}
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1 bg-amber-50 p-2 rounded-md border border-amber-200">
                  <Label className="text-xs font-medium text-amber-700 flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    Nova Senha
                  </Label>
                  <Input
                    type="password"
                    placeholder="Deixe vazio para manter"
                    value={editFormData.password}
                    onChange={(e) => setEditFormData({ ...editFormData, password: e.target.value })}
                    className="h-8 text-sm"
                  />
                  <p className="text-[10px] text-amber-600">Só preencha se quiser alterar</p>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs font-medium text-slate-600">Horas/Semana</Label>
                  <Input
                    type="number"
                    value={editFormData.weekly_hours}
                    onChange={(e) => setEditFormData({ ...editFormData, weekly_hours: parseFloat(e.target.value) })}
                    required
                    className="h-9 text-sm"
                    placeholder="44"
                  />
                  <p className="text-[10px] text-slate-500">CLT: 44h = 220h/mês</p>
                </div>
              </div>

              {/* Status Ativo/Inativo */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                <div>
                  <p className="font-medium text-sm text-slate-900">Status do Funcionário</p>
                  <p className="text-xs text-slate-500">Funcionários inativos não podem acessar o sistema</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editFormData.is_active}
                    onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                  <span className="ml-2 text-sm font-medium text-slate-700">
                    {editFormData.is_active ? "Ativo" : "Inativo"}
                  </span>
                </label>
              </div>

              {/* Ponto Liberado - Controle de Segurança */}
              <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div>
                  <p className="font-medium text-sm text-slate-900 flex items-center gap-2">
                    🔐 Ponto Liberado
                  </p>
                  <p className="text-xs text-amber-700">Funcionário só pode bater ponto se ativado</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editFormData.ponto_liberado}
                    onChange={(e) => setEditFormData({ ...editFormData, ponto_liberado: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                  <span className="ml-2 text-sm font-medium text-slate-700">
                    {editFormData.ponto_liberado ? "Liberado" : "Bloqueado"}
                  </span>
                </label>
              </div>
            </form>
          )}
          
          {/* Footer fixo com botões */}
          <div className="flex-shrink-0 border-t bg-white px-4 py-3 flex gap-2">
            <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1">
              Cancelar
            </Button>
            <Button 
              type="submit"
              form="edit-form"
              onClick={handleEditSubmit}
              className="flex-1 bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark"
            >
              <Save className="w-4 h-4 mr-2" />
              Salvar Alterações
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal para visualizar foto ampliada */}
      {viewingPhoto && (
        <div 
          className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4"
          onClick={() => setViewingPhoto(null)}
        >
          <div className="relative max-w-2xl w-full" onClick={e => e.stopPropagation()}>
            <button 
              onClick={() => setViewingPhoto(null)}
              className="absolute -top-12 right-0 p-2 text-white hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <img 
              src={viewingPhoto.url || viewingPhoto} 
              alt={viewingPhoto.name || "Foto do funcionário"} 
              className="w-full h-auto rounded-xl shadow-2xl"
            />
            {viewingPhoto.name && (
              <p className="text-white text-center mt-4 text-lg font-medium">{viewingPhoto.name}</p>
            )}
          </div>
        </div>
      )}

      {/* Modal de Detalhes do Funcionário */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-4 pt-4 pb-2 flex-shrink-0 border-b">
            <DialogTitle className="font-heading flex items-center gap-2">
              <Users className="w-5 h-5 text-ciaponto-cyan" />
              Detalhes do Funcionário
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Registros e saldo de horas de {employeeDetails?.name}
            </DialogDescription>
          </DialogHeader>
          
          {employeeDetails && (
            <div className="flex-1 overflow-y-auto px-4 py-4">
              {/* Cabeçalho com foto e dados principais */}
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 pb-4 border-b">
                {/* Foto de Perfil */}
                <div className="flex-shrink-0">
                  {employeeDetails.profile_photo_url ? (
                    <img 
                      src={employeeDetails.profile_photo_url}
                      alt={employeeDetails.name}
                      className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover border-4 border-sky-200 shadow-lg cursor-pointer hover:scale-105 transition-transform"
                      onClick={() => setViewingPhoto({ url: employeeDetails.profile_photo_url, name: employeeDetails.name })}
                    />
                  ) : (
                    <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-cyan-100 flex items-center justify-center border-4 border-sky-200">
                      <span className="text-3xl font-bold text-ciaponto-cyan">
                        {employeeDetails.name?.split(" ").map(n => n[0]).join("").slice(0, 2)}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Nome e info básica */}
                <div className="flex-1 text-center sm:text-left">
                  <h2 className="text-xl font-bold text-slate-900">{employeeDetails.name}</h2>
                  <p className="text-slate-600">{employeeDetails.position} • {employeeDetails.department}</p>
                  
                  {/* Badges */}
                  <div className="flex flex-wrap gap-2 mt-2 justify-center sm:justify-start">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      employeeDetails.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                    }`}>
                      {employeeDetails.is_active ? <CheckCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                      {employeeDetails.is_active ? "Ativo" : "Inativo"}
                    </span>
                    
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${formatEmployeeType(employeeDetails.employee_type).color}`}>
                      {employeeDetails.employee_type === "estagiario" && <GraduationCap className="w-3 h-3" />}
                      {employeeDetails.employee_type === "terceirizado" && <Users className="w-3 h-3" />}
                      {employeeDetails.employee_type === "funcionario" && <Briefcase className="w-3 h-3" />}
                      {formatEmployeeType(employeeDetails.employee_type).label}
                    </span>
                    
                    {employeeDetails.geofence_enabled && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                        <Target className="w-3 h-3" />
                        Cerca Ativa
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Dados Pessoais */}
              <div className="mt-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Dados Pessoais
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Email</p>
                      <p className="text-sm font-medium text-slate-900">{employeeDetails.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                    <Phone className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Telefone</p>
                      <p className="text-sm font-medium text-slate-900">{employeeDetails.phone || "Não informado"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                    <FileText className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">CPF</p>
                      <p className="text-sm font-medium text-slate-900">{employeeDetails.cpf || "Não informado"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Escala</p>
                      <p className="text-sm font-medium text-slate-900">{formatSchedule(employeeDetails.work_days, employeeDetails.weekend_rotation, employeeDetails.work_schedule_type)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                    <Clock className="w-4 h-4 text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Carga Semanal</p>
                      <p className="text-sm font-medium text-slate-900">{employeeDetails.weekly_hours || 44}h/sem</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Resumo de Horas do Mês */}
              <div className="mt-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Resumo de Horas (Mês Atual)
                </h3>
                
                {loadingDetails ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1CA1BA]"></div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Cards principais - USANDO DADOS DO BACKEND */}
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                      {(() => {
                        // USAR DADOS DO BACKEND (fonte única de verdade)
                        const backendData = employeeMonthlyBalance;
                        const workedHours = backendData ? backendData.worked_hours : 0;
                        const expectedHours = backendData ? backendData.expected_hours : 0;
                        const balance = backendData ? backendData.balance : 0;
                        const daysWorked = backendData ? backendData.days_worked : 0;
                        const expectedDays = backendData ? backendData.expected_days : 0;
                        const isPositive = balance >= 0;
                        const isPontoLiberado = backendData ? backendData.ponto_liberado : false;
                        
                        // Formatar valores
                        const workedFormatted = `${Math.floor(workedHours)}h ${Math.round((workedHours % 1) * 60)}min`;
                        const expectedFormatted = isPontoLiberado ? `${expectedHours}h` : "Ponto não liberado";
                        const balanceFormatted = isPontoLiberado 
                          ? (isPositive ? formatHoursMinutes(balance, true) : formatHoursMinutes(balance, true))
                          : "N/A";
                        
                        return (
                          <>
                            <div className="p-3 bg-sky-50 rounded-lg text-center">
                              <p className="text-xs text-ciaponto-cyan font-medium">Dias Trabalhados</p>
                              <p className="text-xl font-bold text-ciaponto-navy">{daysWorked}/{expectedDays}</p>
                            </div>
                            <div className="p-3 bg-green-50 rounded-lg text-center">
                              <p className="text-xs text-green-600 font-medium">Horas Trabalhadas</p>
                              <p className="text-xl font-bold text-green-700">{workedFormatted}</p>
                            </div>
                            <div className="p-3 bg-slate-50 rounded-lg text-center">
                              <p className="text-xs text-slate-600 font-medium">Horas Esperadas</p>
                              <p className="text-xl font-bold text-slate-700">{expectedFormatted}</p>
                            </div>
                            {/* Card de Ajustes */}
                            <div className={`p-3 rounded-lg text-center ${backendData?.manual_adjustment > 0 ? 'bg-blue-50 border border-blue-200' : 'bg-slate-50'}`}>
                              <p className={`text-xs font-medium ${backendData?.manual_adjustment > 0 ? 'text-blue-600' : 'text-slate-500'}`}>
                                Ajustes
                              </p>
                              <p className={`text-xl font-bold ${backendData?.manual_adjustment > 0 ? 'text-blue-700' : 'text-slate-400'}`}>
                                {backendData?.manual_adjustment > 0 ? `+${backendData.manual_adjustment}h` : '0h'}
                              </p>
                            </div>
                            <div className={`p-3 rounded-lg text-center ${isPositive ? 'bg-emerald-50' : 'bg-red-50'}`}>
                              <p className={`text-xs font-medium ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
                                {isPositive ? 'Saldo Positivo' : 'Horas Pendentes'}
                              </p>
                              <p className={`text-xl font-bold ${isPositive ? 'text-emerald-700' : 'text-red-700'}`}>{balanceFormatted}</p>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                    
                    {/* Informação sobre fonte dos dados */}
                    <div className="text-xs text-slate-400 text-center mt-2">
                      Dados calculados pelo sistema • Última atualização: {new Date().toLocaleTimeString("pt-BR")}
                    </div>
                  </div>
                )}
              </div>

              {/* Últimos Registros de Ponto */}
              <div className="mt-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Últimos Registros de Ponto
                </h3>
                
                {loadingDetails ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1CA1BA]"></div>
                  </div>
                ) : employeeRecords.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {employeeRecords.slice(0, 20).map((record, index) => (
                      <div 
                        key={index} 
                        className={`flex items-center justify-between p-2 rounded-lg ${
                          record.record_type === 'entry' ? 'bg-green-50' : 'bg-red-50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            record.record_type === 'entry' ? 'bg-green-200' : 'bg-red-200'
                          }`}>
                            {record.record_type === 'entry' ? (
                              <ArrowRight className="w-4 h-4 text-green-700" />
                            ) : (
                              <ArrowLeft className="w-4 h-4 text-red-700" />
                            )}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-900">
                              {record.record_type === 'entry' ? 'Entrada' : 'Saída'}
                            </p>
                            <p className="text-xs text-slate-500">
                              {new Date(record.timestamp).toLocaleDateString('pt-BR')}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-slate-700">
                            {new Date(record.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                          {record.geofence_status && (
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              record.geofence_status === 'inside' ? 'bg-green-100 text-green-700' : 
                              record.geofence_status === 'outside' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'
                            }`}>
                              {record.geofence_status === 'inside' ? 'Dentro' : 
                               record.geofence_status === 'outside' ? 'Fora' : 'Sem GPS'}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-slate-500">
                    <Clock className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">Nenhum registro de ponto encontrado</p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Footer fixo com botões */}
          <div className="flex-shrink-0 border-t bg-white px-4 py-3 flex gap-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setDetailsDialogOpen(false)} 
              className="flex-1"
            >
              Fechar
            </Button>
            <Button 
              type="button"
              variant="outline"
              onClick={() => employeeDetails && downloadTermoCelular(employeeDetails)}
              className="flex-1 text-amber-600 border-amber-300 hover:bg-amber-50"
              disabled={loadingDetails}
              title="Termo de Ciência - Uso do Celular"
            >
              <FileText className="w-4 h-4 mr-2" />
              Termo Celular
            </Button>
            <Button 
              type="button"
              onClick={() => employeeDetails && generateEmployeePDF(employeeDetails, employeeRecords)}
              className="flex-1 bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark"
              disabled={loadingDetails}
            >
              <Download className="w-4 h-4 mr-2" />
              Exportar PDF
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Modal de Atestado Rápido */}
      <Dialog open={quickAtestadoDialogOpen} onOpenChange={setQuickAtestadoDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Stethoscope className="w-5 h-5 text-blue-500" />
              Adicionar Atestado
            </DialogTitle>
            <DialogDescription>
              {quickAtestadoData.employee_name} - {quickAtestadoData.date} ({quickAtestadoData.dayOfWeek})
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Tipo de Dispensa</Label>
              <select
                value={quickAtestadoData.leave_type}
                onChange={(e) => setQuickAtestadoData({...quickAtestadoData, leave_type: e.target.value})}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="atestado_medico">Atestado Médico</option>
                <option value="licenca_maternidade">Licença Maternidade</option>
                <option value="licenca_paternidade">Licença Paternidade</option>
                <option value="luto">Licença Luto</option>
                <option value="casamento">Licença Casamento</option>
                <option value="outro">Outro</option>
              </select>
            </div>
            
            <div className="space-y-2">
              <Label>Motivo (opcional)</Label>
              <Input
                placeholder="Ex: Consulta médica, gripe, etc"
                value={quickAtestadoData.reason}
                onChange={(e) => setQuickAtestadoData({...quickAtestadoData, reason: e.target.value})}
              />
            </div>
            
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-sm text-blue-700">
                As horas deste dia serão creditadas automaticamente ao funcionário.
              </p>
            </div>
          </div>
          
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setQuickAtestadoDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveQuickAtestado} className="bg-blue-500 hover:bg-blue-600">
              <Plus className="w-4 h-4 mr-2" />
              Cadastrar Atestado
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Time Records Component
function TimeRecordsPage() {
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [filterEmployee, setFilterEmployee] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [recordsRes, employeesRes] = await Promise.all([
        axios.get(`${API}/time-records`),
        axios.get(`${API}/employees`)
      ]);
      setRecords(recordsRes.data || []);
      setEmployees(employeesRes.data || []);
    } catch (error) {
      toast.error("Erro ao carregar registros");
    } finally {
      setLoading(false);
    }
  };

  // Filtrar registros
  const filteredRecords = records.filter(record => {
    if (filterEmployee !== "all" && record.employee_id !== filterEmployee) return false;
    if (filterType !== "all" && record.record_type !== filterType) return false;
    if (searchTerm && !record.employee_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  // Agrupar registros por funcionário
  const recordsByEmployee = filteredRecords.reduce((acc, record) => {
    const empId = record.employee_id;
    if (!acc[empId]) {
      acc[empId] = {
        employee_id: empId,
        employee_name: record.employee_name,
        records: []
      };
    }
    acc[empId].records.push(record);
    return acc;
  }, {});

  const openRecordDetails = (record) => {
    setSelectedRecord(record);
    setDetailsOpen(true);
  };

  const openGoogleMaps = (lat, lng) => {
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, "_blank");
  };

  // Função para exportar registros para CSV
  const exportToCSV = () => {
    try {
      // Usar os registros filtrados ou todos se não houver filtro
      const dataToExport = filteredRecords.length > 0 ? filteredRecords : records;
      
      if (dataToExport.length === 0) {
        toast.error("Não há registros para exportar");
        return;
      }

      // Cabeçalhos do CSV
      const headers = [
        "ID",
        "Funcionário",
        "Funcionário ID",
        "Tipo",
        "Data/Hora (Timestamp Bruto)",
        "Data/Hora (Formatado)",
        "Latitude",
        "Longitude",
        "Endereço",
        "Dentro da Cerca",
        "Fonte",
        "Notas"
      ];

      // Converter registros para linhas CSV
      const rows = dataToExport.map(record => {
        // Formatar timestamp para exibição
        let formattedDate = "";
        try {
          const date = new Date(record.timestamp);
          formattedDate = date.toLocaleString("pt-BR", { timeZone: "America/Fortaleza" });
        } catch (e) {
          formattedDate = record.timestamp;
        }

        return [
          record.id || "",
          record.employee_name || "",
          record.employee_id || "",
          record.record_type === "entry" ? "Entrada" : "Saída",
          record.timestamp || "",
          formattedDate,
          record.latitude || record.location?.lat || "",
          record.longitude || record.location?.lng || "",
          record.address || "",
          record.within_geofence ? "Sim" : "Não",
          record.source || "app",
          record.notes || ""
        ];
      });

      // Criar conteúdo CSV
      const csvContent = [
        headers.join(";"),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(";"))
      ].join("\n");

      // Criar blob e fazer download
      const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `registros_ponto_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`${dataToExport.length} registros exportados com sucesso!`);
    } catch (error) {
      console.error("Erro ao exportar:", error);
      toast.error("Erro ao exportar registros");
    }
  };

  return (
    <div className="space-y-6" data-testid="time-records-page">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">Registros de Ponto</h1>
          <p className="text-slate-600">Todos os registros de entrada e saída - clique para ver detalhes</p>
        </div>
        <Button 
          onClick={exportToCSV}
          className="bg-green-600 hover:bg-green-700 text-white"
          data-testid="export-records-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Exportar CSV
        </Button>
      </div>

      {/* Filtros */}
      <Card className="border border-slate-200">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Buscar Funcionário</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Digite o nome..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Funcionário</label>
              <select
                value={filterEmployee}
                onChange={(e) => setFilterEmployee(e.target.value)}
                className="w-full p-2 border rounded-lg text-sm"
              >
                <option value="all">Todos os funcionários</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Tipo</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full p-2 border rounded-lg text-sm"
              >
                <option value="all">Todos</option>
                <option value="entry">Entrada</option>
                <option value="exit">Saída</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button 
                variant="outline" 
                onClick={() => {
                  setSearchTerm("");
                  setFilterEmployee("all");
                  setFilterType("all");
                }}
                className="w-full"
              >
                Limpar Filtros
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Resumo por Funcionário */}
      {filterEmployee === "all" && Object.keys(recordsByEmployee).length > 0 && (
        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">Registros por Funcionário</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.values(recordsByEmployee).map((emp) => (
                <div 
                  key={emp.employee_id} 
                  className="p-4 border rounded-lg hover:bg-slate-50 cursor-pointer"
                  onClick={() => setFilterEmployee(emp.employee_id)}
                >
                  <p className="font-medium text-slate-900">{emp.employee_name}</p>
                  <p className="text-sm text-slate-500">{emp.records.length} registros</p>
                  <div className="flex gap-2 mt-2">
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                      {emp.records.filter(r => r.record_type === "entry").length} entradas
                    </span>
                    <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                      {emp.records.filter(r => r.record_type === "exit").length} saídas
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1CA1BA]"></div>
        </div>
      ) : filteredRecords.length > 0 ? (
        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg">
              {filterEmployee !== "all" 
                ? `Registros de ${employees.find(e => e.id === filterEmployee)?.name || "Funcionário"}`
                : `Todos os Registros (${filteredRecords.length})`
              }
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-4 font-medium text-slate-600">Funcionário</th>
                    <th className="text-left p-4 font-medium text-slate-600">Tipo</th>
                    <th className="text-left p-4 font-medium text-slate-600">Data/Hora</th>
                    <th className="text-left p-4 font-medium text-slate-600">Foto</th>
                    <th className="text-left p-4 font-medium text-slate-600">Localização</th>
                    <th className="text-left p-4 font-medium text-slate-600">Cerca</th>
                    <th className="text-left p-4 font-medium text-slate-600">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((record) => (
                    <tr key={record.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-4 font-medium text-slate-900">{record.employee_name}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          record.record_type === "entry" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                        }`}>
                          {record.record_type === "entry" ? "Entrada" : "Saída"}
                        </span>
                      </td>
                      <td className="p-4 text-slate-600">
                        {new Date(record.timestamp).toLocaleString("pt-BR")}
                      </td>
                      <td className="p-4">
                        {record.photo_url ? (
                          <img 
                            src={record.photo_url} 
                            alt="Foto do registro"
                            className="w-10 h-10 rounded-lg object-cover cursor-pointer hover:opacity-80 border border-slate-200"
                            onClick={() => openRecordDetails(record)}
                          />
                        ) : (
                          <span className="text-slate-400 text-sm">Sem foto</span>
                        )}
                      </td>
                      <td className="p-4">
                        {record.latitude ? (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-green-600 hover:text-green-700 p-0 h-auto"
                            onClick={() => openGoogleMaps(record.latitude, record.longitude)}
                          >
                            <MapPin className="w-4 h-4 mr-1" />
                            Ver no Mapa
                          </Button>
                        ) : (
                          <span className="text-slate-400 text-sm">Sem GPS</span>
                        )}
                      </td>
                      <td className="p-4">
                        {record.geofence_status === "inside" && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700" title={`Distância: ${record.geofence_distance}m`}>
                            ✓ Dentro
                          </span>
                        )}
                        {record.geofence_status === "outside" && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700" title={`Distância: ${record.geofence_distance}m`}>
                            ✗ Fora ({record.geofence_distance}m)
                          </span>
                        )}
                        {record.geofence_status === "no_gps" && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                            Sem GPS
                          </span>
                        )}
                        {(!record.geofence_status || record.geofence_status === "not_configured") && (
                          <span className="text-slate-400 text-xs">-</span>
                        )}
                      </td>
                      <td className="p-4">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openRecordDetails(record)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Detalhes
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border border-slate-200">
          <CardContent className="p-12 text-center">
            <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">Nenhum registro encontrado</p>
          </CardContent>
        </Card>
      )}

      {/* Modal de Detalhes do Registro */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-4 pt-4 pb-2 flex-shrink-0 border-b">
            <DialogTitle className="font-heading text-lg">Detalhes do Registro de Ponto</DialogTitle>
            <DialogDescription className="text-sm">
              Informações completas do registro com foto e localização
            </DialogDescription>
          </DialogHeader>
          {selectedRecord && (
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
              {/* Info básica - Responsivo */}
              <div className="grid grid-cols-2 gap-2 sm:gap-4">
                <div className="bg-slate-50 p-3 sm:p-4 rounded-lg">
                  <p className="text-xs sm:text-sm text-slate-500 mb-1">Funcionário</p>
                  <p className="font-medium text-slate-900 text-sm sm:text-base truncate">{selectedRecord.employee_name}</p>
                </div>
                <div className="bg-slate-50 p-3 sm:p-4 rounded-lg">
                  <p className="text-xs sm:text-sm text-slate-500 mb-1">Tipo</p>
                  <span className={`inline-block px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${
                    selectedRecord.record_type === "entry" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                  }`}>
                    {selectedRecord.record_type === "entry" ? "Entrada" : "Saída"}
                  </span>
                </div>
                <div className="bg-slate-50 p-3 sm:p-4 rounded-lg">
                  <p className="text-xs sm:text-sm text-slate-500 mb-1">Data/Hora</p>
                  <p className="font-medium text-slate-900 text-sm sm:text-base">
                    {new Date(selectedRecord.timestamp).toLocaleString("pt-BR")}
                  </p>
                </div>
                <div className="bg-slate-50 p-3 sm:p-4 rounded-lg">
                  <p className="text-xs sm:text-sm text-slate-500 mb-1">Origem</p>
                  <p className="font-medium text-slate-900 text-sm sm:text-base capitalize">{selectedRecord.source || "web"}</p>
                </div>
              </div>

              {/* Foto - Responsivo */}
              <div className="border rounded-lg p-3 sm:p-4">
                <p className="text-xs sm:text-sm font-medium text-slate-700 mb-2 sm:mb-3 flex items-center gap-2">
                  <Camera className="w-4 h-4" />
                  Foto do Registro
                </p>
                {selectedRecord.photo_url ? (
                  <div className="relative">
                    <img 
                      src={selectedRecord.photo_url} 
                      alt="Foto do registro"
                      className="w-full max-h-48 sm:max-h-64 object-contain rounded-lg bg-slate-100"
                    />
                    <a 
                      href={selectedRecord.photo_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="absolute top-2 right-2 bg-white/90 hover:bg-white px-2 sm:px-3 py-1 rounded-lg text-xs sm:text-sm font-medium text-slate-700 shadow"
                    >
                      Abrir
                    </a>
                  </div>
                ) : (
                  <div className="bg-slate-100 rounded-lg p-6 sm:p-8 text-center">
                    <Camera className="w-10 h-10 sm:w-12 sm:h-12 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500 text-sm">Sem foto disponível</p>
                  </div>
                )}
              </div>

              {/* Localização - Responsivo */}
              <div className="border rounded-lg p-3 sm:p-4">
                <p className="text-xs sm:text-sm font-medium text-slate-700 mb-2 sm:mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Localização GPS
                </p>
                {selectedRecord.latitude ? (
                  <div className="space-y-2 sm:space-y-3">
                    <div className="bg-slate-50 p-2 sm:p-3 rounded-lg">
                      <p className="text-xs text-slate-500">Coordenadas</p>
                      <p className="font-mono text-slate-900 text-xs sm:text-sm break-all">
                        {selectedRecord.latitude.toFixed(6)}, {selectedRecord.longitude.toFixed(6)}
                      </p>
                    </div>
                    <div className="relative rounded-lg overflow-hidden border border-slate-200">
                      <iframe
                        src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${selectedRecord.latitude},${selectedRecord.longitude}&zoom=16`}
                        width="100%"
                        height="150"
                        style={{ border: 0 }}
                        allowFullScreen=""
                        loading="lazy"
                        title="Localização do registro"
                        className="sm:h-[200px]"
                      />
                    </div>
                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700 h-10 text-sm"
                      onClick={() => openGoogleMaps(selectedRecord.latitude, selectedRecord.longitude)}
                    >
                      <MapPin className="w-4 h-4 mr-2" />
                      Abrir no Google Maps
                    </Button>
                  </div>
                ) : (
                  <div className="bg-slate-100 rounded-lg p-6 sm:p-8 text-center">
                    <MapPin className="w-10 h-10 sm:w-12 sm:h-12 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500 text-sm">Sem localização GPS disponível</p>
                  </div>
                )}
              </div>

              {/* Cerca Eletrônica - Responsivo */}
              {selectedRecord.geofence_status && selectedRecord.geofence_status !== "not_configured" && (
                <div className={`border rounded-lg p-3 sm:p-4 ${
                  selectedRecord.geofence_status === "inside" ? "border-green-200 bg-green-50" :
                  selectedRecord.geofence_status === "outside" ? "border-red-200 bg-red-50" :
                  "border-amber-200 bg-amber-50"
                }`}>
                  <p className="text-xs sm:text-sm font-medium text-slate-700 mb-2 sm:mb-3 flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    Cerca Eletrônica
                  </p>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                    {selectedRecord.geofence_status === "inside" && (
                      <>
                        <span className="inline-flex items-center gap-1 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-green-100 text-green-700">
                          <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                          Dentro da área permitida
                        </span>
                        {selectedRecord.geofence_distance && (
                          <span className="text-xs sm:text-sm text-green-600">
                            Distância: {selectedRecord.geofence_distance}m
                          </span>
                        )}
                      </>
                    )}
                    {selectedRecord.geofence_status === "outside" && (
                      <>
                        <span className="inline-flex items-center gap-1 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-red-100 text-red-700">
                          <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4" />
                          Fora da área permitida
                        </span>
                        {selectedRecord.geofence_distance && (
                          <span className="text-xs sm:text-sm text-red-600">
                            Distância: {selectedRecord.geofence_distance}m
                          </span>
                        )}
                      </>
                    )}
                    {selectedRecord.geofence_status === "no_gps" && (
                      <span className="inline-flex items-center gap-1 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium bg-amber-100 text-amber-700">
                        <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4" />
                        GPS não disponível
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Observações - Responsivo */}
              {selectedRecord.notes && (
                <div className="border rounded-lg p-3 sm:p-4">
                  <p className="text-xs sm:text-sm font-medium text-slate-700 mb-2">Observações</p>
                  <p className="text-slate-600 text-sm">{selectedRecord.notes}</p>
                </div>
              )}
            </div>
          )}
          
          {/* Footer fixo com botão fechar */}
          <div className="flex-shrink-0 border-t bg-white px-4 py-3">
            <Button 
              variant="outline" 
              onClick={() => setDetailsOpen(false)}
              className="w-full"
            >
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Reports Component
function ReportsPage() {
  const { token } = useAuth();
  const [report, setReport] = useState(null);
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodType, setPeriodType] = useState("monthly"); // daily, weekly, monthly
  const [selectedEmployee, setSelectedEmployee] = useState("all");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedEmpDetails, setSelectedEmpDetails] = useState(null);
  
  // Feriados ativos para cálculos
  const [feriadosAtivos, setFeriadosAtivos] = useState([]);
  
  // Estados para ajuste de banco de horas
  const [bankHoursOpen, setBankHoursOpen] = useState(false);
  const [bankHoursData, setBankHoursData] = useState(null);
  const [bankHoursLoading, setBankHoursLoading] = useState(false);
  const [adjustmentForm, setAdjustmentForm] = useState({
    type: "credit",
    hours: "",
    reason: "",
    month: new Date().getMonth() + 1,  // Mês atual (1-12)
    year: new Date().getFullYear()      // Ano atual
  });
  const [adjusting, setAdjusting] = useState(false);
  
  // Estado para armazenar ajustes de banco de horas de todos os funcionários
  const [allBankHoursAdjustments, setAllBankHoursAdjustments] = useState({});
  
  // Estado para armazenar saldos mensais do backend (fonte única de verdade)
  const [monthlyBalances, setMonthlyBalances] = useState({});
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [detailedReportOpen, setDetailedReportOpen] = useState(false);
  const [detailedReportEmployee, setDetailedReportEmployee] = useState(null);
  
  // Função para abrir relatório detalhado
  const openDetailedReport = (employee) => {
    setDetailedReportEmployee(employee);
    setDetailedReportOpen(true);
  };
  
  // Buscar saldos mensais do backend para todos os funcionários
  const fetchMonthlyBalances = async () => {
    if (employees.length === 0) return;
    setLoadingBalances(true);
    
    try {
      const balancePromises = employees
        .filter(emp => emp.ponto_liberado)
        .map(emp => 
          axios.get(`${API}/employees/${emp.id}/monthly-balance?month=${month}&year=${year}`)
            .then(res => ({ id: emp.id, data: res.data }))
            .catch(() => ({ id: emp.id, data: null }))
        );
      
      const results = await Promise.all(balancePromises);
      const balancesMap = {};
      results.forEach(result => {
        if (result.data) {
          balancesMap[result.id] = result.data;
        }
      });
      setMonthlyBalances(balancesMap);
    } catch (error) {
      console.error("Erro ao buscar saldos mensais:", error);
    } finally {
      setLoadingBalances(false);
    }
  };
  
  // Buscar saldos quando mês/ano mudar ou funcionários carregarem
  useEffect(() => {
    if (employees.length > 0) {
      fetchMonthlyBalances();
    }
  }, [employees, month, year]);

  // Função para formatar data no formato YYYY-MM-DD (fuso local)
  const formatDateKeyReport = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // NOTA: A função calculateHoursForReport foi removida
  // Todos os cálculos de horas agora vêm do backend via endpoint /monthly-balance

  useEffect(() => {
    fetchEmployees();
    fetchFeriados();
  }, []);

  useEffect(() => {
    fetchReport();
    fetchRecords();
  }, [month, year, periodType, selectedDate, selectedEmployee]);

  const fetchFeriados = async () => {
    try {
      const response = await axios.get(`${API}/holidays/company`);
      // Extrair datas dos feriados ativos
      const today = new Date();
      const holidaysData = response.data?.feriados || response.data || [];
      const ativos = holidaysData
        .filter(f => f.is_active && new Date(f.date) >= new Date(today.getFullYear(), 0, 1))
        .map(f => f.date);
      setFeriadosAtivos(ativos);
    } catch (error) {
      console.error("Erro ao carregar feriados:", error);
      setFeriadosAtivos([]);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
      // Após carregar funcionários, carregar ajustes de banco de horas
      fetchAllBankHoursAdjustments(response.data);
    } catch (error) {
      console.error("Erro ao carregar funcionários");
    }
  };

  // Carregar ajustes de banco de horas de todos os funcionários
  const fetchAllBankHoursAdjustments = async (employeesList) => {
    try {
      const adjustmentsMap = {};
      for (const emp of employeesList) {
        try {
          const response = await axios.get(`${API}/employees/${emp.id}/bank-hours`);
          adjustmentsMap[emp.id] = response.data.manual_adjustment_balance || 0;
        } catch (err) {
          adjustmentsMap[emp.id] = 0;
        }
      }
      setAllBankHoursAdjustments(adjustmentsMap);
    } catch (error) {
      console.error("Erro ao carregar ajustes de banco de horas");
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/summary?month=${month}&year=${year}`);
      setReport(response.data);
    } catch (error) {
      // Silenciar erro - os dados são carregados de outras fontes
      console.log("Relatório de resumo não disponível");
    } finally {
      setLoading(false);
    }
  };

  const fetchRecords = async () => {
    try {
      const response = await axios.get(`${API}/time-records`);
      setRecords(response.data);
    } catch (error) {
      console.error("Erro ao carregar registros");
    }
  };

  const months = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
  ];

  // Filtrar registros por período e funcionário
  const getFilteredRecords = () => {
    let filtered = [...records];
    
    // Filtrar por funcionário
    if (selectedEmployee !== "all") {
      filtered = filtered.filter(r => r.employee_id === selectedEmployee);
    }
    
    // Filtrar por período
    const now = new Date();
    if (periodType === "daily") {
      const targetDate = new Date(selectedDate);
      filtered = filtered.filter(r => {
        const recordDate = new Date(r.timestamp);
        return recordDate.toDateString() === targetDate.toDateString();
      });
    } else if (periodType === "weekly") {
      const weekAgo = new Date(now);
      weekAgo.setDate(weekAgo.getDate() - 7);
      filtered = filtered.filter(r => new Date(r.timestamp) >= weekAgo);
    } else {
      // Monthly
      filtered = filtered.filter(r => {
        const recordDate = new Date(r.timestamp);
        return recordDate.getMonth() + 1 === month && recordDate.getFullYear() === year;
      });
    }
    
    return filtered.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  };

  // Calcular saldo de horas por funcionário
  const calculateEmployeeBalance = (employeeId) => {
    const emp = employees.find(e => e.id === employeeId);
    
    // Verificar se temos dados do backend (fonte única de verdade)
    const backendBalance = monthlyBalances[employeeId];
    if (backendBalance) {
      const totalMinutes = Math.round(backendBalance.worked_hours * 60);
      const workedHours = Math.floor(totalMinutes / 60);
      const workedMinutes = totalMinutes % 60;
      
      return {
        worked: `${workedHours}h ${workedMinutes}m`,
        expected: `${backendBalance.expected_hours}h`,
        balance: backendBalance.balance >= 0 
          ? formatHoursMinutes(backendBalance.balance, true) 
          : formatHoursMinutes(backendBalance.balance, true),
        balancePositive: backendBalance.balance >= 0,
        fromBackend: true
      };
    }
    
    // Fallback para cálculo local se backend não respondeu
    const empRecords = records.filter(r => r.employee_id === employeeId);
    let totalWorkedMinutes = 0;
    
    // Data de liberação do ponto do funcionário
    const pontoLiberado = emp?.ponto_liberado || false;
    const pontoLiberadoAt = emp?.ponto_liberado_at ? new Date(emp.ponto_liberado_at) : null;
    
    // Se ponto não está liberado, retornar sem calcular esperadas
    if (!pontoLiberado) {
      return {
        worked: "0h 0m",
        expected: "N/A",
        balance: "Ponto não liberado",
        balancePositive: true
      };
    }
    
    // Agrupar por dia
    const recordsByDay = {};
    empRecords.forEach(r => {
      const recordDate = new Date(r.timestamp);
      // Verificar se está dentro do período filtrado (month/year)
      if (recordDate.getMonth() + 1 !== month || recordDate.getFullYear() !== year) {
        return;
      }
      const day = recordDate.toDateString();
      if (!recordsByDay[day]) recordsByDay[day] = [];
      recordsByDay[day].push(r);
    });
    
    // Calcular horas por dia
    Object.values(recordsByDay).forEach(dayRecords => {
      const sorted = dayRecords.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      let dayMinutes = 0;
      for (let i = 0; i < sorted.length - 1; i += 2) {
        if (sorted[i].record_type === "entry" && sorted[i + 1]?.record_type === "exit") {
          const entry = new Date(sorted[i].timestamp);
          const exit = new Date(sorted[i + 1].timestamp);
          dayMinutes += (exit - entry) / (1000 * 60);
        }
      }
      totalWorkedMinutes += dayMinutes;
    });
    
    // Calcular dias esperados de trabalho no período filtrado
    const workDays = emp?.work_days || [1, 2, 3, 4, 5, 6];
    const workScheduleType = emp?.work_schedule_type || "standard";
    const today = new Date();
    const processedDays = new Set(Object.keys(recordsByDay));
    
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0);
    
    let expectedMinutes = 0;
    let scheduledDays = 0;
    
    // Função para verificar se é um dia de trabalho
    const shouldWorkDay = (date, dayOfWeek) => {
      return workDays.includes(dayOfWeek);
    };
    
    // Função para verificar se é depois da liberação do ponto
    const isAfterPontoLiberado = (date) => {
      if (!pontoLiberadoAt) return true;
      const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const liberadoStart = new Date(pontoLiberadoAt.getFullYear(), pontoLiberadoAt.getMonth(), pontoLiberadoAt.getDate());
      return dateStart >= liberadoStart;
    };
    
    // Função para verificar se é feriado ativo
    const isFeriadoAtivo = (date) => {
      // Verificar se feriadosAtivos está definido
      if (!feriadosAtivos || !Array.isArray(feriadosAtivos)) {
        return false;
      }
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      return feriadosAtivos.includes(dateStr);
    };
    
    // Contar dias esperados e horas
    // NÃO incluir o dia atual (ainda em andamento)
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    for (let d = new Date(startDate); d <= endDate && d <= yesterday; d.setDate(d.getDate() + 1)) {
      const date = new Date(d);
      const dayOfWeek = date.getDay();
      
      // Verificar se é dia de trabalho e após liberação do ponto
      if (isAfterPontoLiberado(date) && shouldWorkDay(date, dayOfWeek)) {
        // Não contar feriados
        if (!isFeriadoAtivo(date)) {
          scheduledDays++;
          
          // Calcular horas baseado no tipo de escala
          if (workScheduleType === "44h_semana_comercial") {
            // Sábado = 4h, outros dias = 8h
            expectedMinutes += (dayOfWeek === 6 ? 4 : 8) * 60;
          } else {
            // Escala padrão: usar carga horária semanal dividida pelos dias
            const weeklyHours = emp?.weekly_hours || 44;
            const workDaysCount = workDays.length || 6;
            const hoursPerDay = weeklyHours / workDaysCount;
            expectedMinutes += hoursPerDay * 60;
          }
        }
      }
    }
    
    const totalHours = Math.floor(totalWorkedMinutes / 60);
    const totalMins = Math.round(totalWorkedMinutes % 60);
    const expectedHoursValue = Math.round(expectedMinutes / 60);
    
    // Aplicar tolerância CLT (Art. 58, §1º) - até 10 minutos diários
    const TOLERANCIA_DIARIA = 10; // minutos
    let balanceMinutes = totalWorkedMinutes - expectedMinutes;
    
    if (Math.abs(balanceMinutes) <= TOLERANCIA_DIARIA) {
      balanceMinutes = 0;
    } else if (balanceMinutes > TOLERANCIA_DIARIA) {
      balanceMinutes = balanceMinutes - TOLERANCIA_DIARIA;
    } else if (balanceMinutes < -TOLERANCIA_DIARIA) {
      balanceMinutes = balanceMinutes + TOLERANCIA_DIARIA;
    }
    
    // Converter para horas
    let balance = balanceMinutes / 60;
    
    // ADICIONAR ajustes manuais de banco de horas ao saldo final
    const manualAdjustment = allBankHoursAdjustments[employeeId] || 0;
    const finalBalance = balance + manualAdjustment;
    
    return {
      worked: `${totalHours}h ${totalMins}m`,
      expected: `${expectedHoursValue}h`,
      balance: formatHoursMinutes(finalBalance, true),
      balancePositive: finalBalance >= 0,
      automaticBalance: formatHoursMinutes(balance),
      manualAdjustment: formatHoursMinutes(manualAdjustment)
    };
  };

  const openEmployeeDetails = (emp) => {
    const empRecords = getFilteredRecords().filter(r => r.employee_id === emp.id);
    setSelectedEmpDetails({
      ...emp,
      records: empRecords,
      balance: calculateEmployeeBalance(emp.id)
    });
    setDetailsOpen(true);
  };

  // ==================== BANCO DE HORAS - AJUSTES MANUAIS ====================
  
  // Abrir modal de ajuste de banco de horas
  const openBankHoursModal = async (emp) => {
    setBankHoursOpen(true);
    setBankHoursLoading(true);
    setBankHoursData({ employee: emp, adjustments: [], manual_balance: 0 });
    // Manter mês e ano atuais ao abrir o modal
    setAdjustmentForm({ 
      type: "credit", 
      hours: "", 
      reason: "",
      month: new Date().getMonth() + 1,  // Mês atual (1-12)
      year: new Date().getFullYear()      // Ano atual
    });
    
    try {
      const response = await axios.get(`${API}/employees/${emp.id}/bank-hours`);
      setBankHoursData({
        employee: emp,
        adjustments: response.data.adjustments || [],
        manual_balance: response.data.manual_adjustment_balance || 0
      });
    } catch (error) {
      console.error("Erro ao carregar banco de horas:", error);
      toast.error("Erro ao carregar dados do banco de horas");
    } finally {
      setBankHoursLoading(false);
    }
  };
  
  // Realizar ajuste de banco de horas
  const handleBankHoursAdjustment = async () => {
    if (!bankHoursData?.employee?.id) return;
    
    const hoursValue = parseFloat(adjustmentForm.hours);
    if (isNaN(hoursValue) || hoursValue <= 0) {
      toast.error("Informe uma quantidade de horas válida");
      return;
    }
    
    if (!adjustmentForm.reason || adjustmentForm.reason.trim().length < 3) {
      toast.error("Informe o motivo do ajuste (mínimo 3 caracteres)");
      return;
    }
    
    // Garantir que month e year são números válidos
    const monthValue = parseInt(adjustmentForm.month) || new Date().getMonth() + 1;
    const yearValue = parseInt(adjustmentForm.year) || new Date().getFullYear();
    
    console.log("=== DEBUG AJUSTE ===");
    console.log("adjustmentForm.month:", adjustmentForm.month, "-> monthValue:", monthValue);
    console.log("adjustmentForm.year:", adjustmentForm.year, "-> yearValue:", yearValue);
    console.log("Payload enviado:", { type: adjustmentForm.type, hours: hoursValue, month: monthValue, year: yearValue });
    
    setAdjusting(true);
    try {
      const response = await axios.post(
        `${API}/employees/${bankHoursData.employee.id}/bank-hours/adjust`,
        {
          type: adjustmentForm.type,
          hours: hoursValue,
          reason: adjustmentForm.reason.trim(),
          month: monthValue,
          year: yearValue
        }
      );
      
      toast.success(response.data.message);
      
      // Atualizar dados
      setBankHoursData(prev => ({
        ...prev,
        adjustments: [response.data.adjustment, ...prev.adjustments],
        manual_balance: response.data.new_balance
      }));
      
      // Limpar formulário (manter mês e ano atuais)
      setAdjustmentForm(prev => ({ 
        type: "credit", 
        hours: "", 
        reason: "",
        month: prev.month,
        year: prev.year
      }));
      
    } catch (error) {
      console.error("Erro ao ajustar banco de horas:", error);
      toast.error(error.response?.data?.detail || "Erro ao realizar ajuste");
    } finally {
      setAdjusting(false);
    }
  };
  
  // Remover ajuste de banco de horas
  const handleDeleteAdjustment = async (adjustmentId) => {
    if (!bankHoursData?.employee?.id) return;
    if (!window.confirm("Deseja realmente remover este ajuste?")) return;
    
    try {
      await axios.delete(
        `${API}/employees/${bankHoursData.employee.id}/bank-hours/${adjustmentId}`
      );
      
      toast.success("Ajuste removido com sucesso");
      
      // Atualizar lista
      const updatedAdjustments = bankHoursData.adjustments.filter(a => a.id !== adjustmentId);
      
      // Recalcular saldo
      let newBalance = 0;
      updatedAdjustments.forEach(adj => {
        if (adj.type === "credit") {
          newBalance += adj.hours;
        } else {
          newBalance -= adj.hours;
        }
      });
      
      setBankHoursData(prev => ({
        ...prev,
        adjustments: updatedAdjustments,
        manual_balance: newBalance
      }));
      
    } catch (error) {
      console.error("Erro ao remover ajuste:", error);
      toast.error(error.response?.data?.detail || "Erro ao remover ajuste");
    }
  };

  // Função para baixar relatório CSV para contabilidade
  const downloadCSV = () => {
    if (!employees.length) {
      toast.error("Não há funcionários para exportar");
      return;
    }

    const filtered = getFilteredRecords();
    if (!filtered.length) {
      toast.error("Não há registros no período selecionado");
      return;
    }

    // Calcular dados de cada funcionário
    const employeeData = employees.map(emp => {
      const balance = calculateEmployeeBalance(emp.id);
      return {
        name: emp.name,
        department: emp.department || "-",
        worked: balance.worked,
        expected: balance.expected,
        overtime: parseFloat(balance.balance) > 0 ? balance.balance.replace("+", "") : "0",
        deficit: parseFloat(balance.balance) < 0 ? balance.balance.replace("-", "") : "0"
      };
    });

    const periodLabel = periodType === "daily" ? selectedDate : 
                        periodType === "weekly" ? "Últimos 7 dias" : 
                        `${months[month - 1]} ${year}`;

    const headers = ["Funcionário", "Departamento", "Horas Trabalhadas", "Horas Esperadas", "Horas Extras", "Déficit"];
    const rows = employeeData.map(emp => [
      emp.name,
      emp.department,
      emp.worked,
      emp.expected,
      emp.overtime + "h",
      emp.deficit + "h"
    ]);

    const csvContent = [
      `Relatório de Horas - ${periodLabel}`,
      `Gerado em: ${new Date().toLocaleString("pt-BR")}`,
      `Total de funcionários: ${employees.length}`,
      `Total de registros no período: ${filtered.length}`,
      "",
      headers.join(";"),
      ...rows.map(row => row.join(";"))
    ].join("\n");

    const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `relatorio_ponto_${periodLabel.replace(/\s/g, "_")}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Relatório CSV baixado com sucesso!");
  };

  // Função para baixar relatório detalhado para contabilidade
  const downloadDetailedReport = () => {
    if (!employees.length) {
      toast.error("Não há funcionários para exportar");
      return;
    }

    const filtered = getFilteredRecords();
    if (!filtered.length) {
      toast.error("Não há registros no período selecionado");
      return;
    }

    const periodLabel = periodType === "daily" ? selectedDate : 
                        periodType === "weekly" ? "Últimos 7 dias" : 
                        `${months[month - 1]} ${year}`;

    // Calcular dados de cada funcionário com registros detalhados
    const employeeData = employees.map(emp => {
      const balance = calculateEmployeeBalance(emp.id);
      const empRecords = filtered.filter(r => r.employee_id === emp.id);
      return {
        nome: emp.name,
        email: emp.email,
        departamento: emp.department || "-",
        cargo: emp.position || "-",
        horas_trabalhadas: balance.worked,
        horas_esperadas: balance.expected,
        saldo: balance.balance,
        saldo_positivo: balance.balancePositive,
        registros: empRecords.map(r => ({
          tipo: r.record_type === "entry" ? "Entrada" : "Saída",
          data_hora: r.timestamp,
          localizacao: r.latitude ? `${r.latitude}, ${r.longitude}` : null,
          foto: r.photo_url || null
        }))
      };
    });

    const reportData = {
      periodo: periodLabel,
      tipo_periodo: periodType,
      gerado_em: new Date().toISOString(),
      resumo: {
        total_funcionarios: employees.length,
        total_registros: filtered.length,
        total_entradas: filtered.filter(r => r.record_type === "entry").length,
        total_saidas: filtered.filter(r => r.record_type === "exit").length
      },
      funcionarios: employeeData
    };

    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `relatorio_contabilidade_${periodLabel.replace(/\s/g, "_")}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Relatório detalhado baixado!");
  };

  // Função para baixar AFD (Arquivo Fonte de Dados) - Portaria 671/2021
  const downloadAFD = async () => {
    try {
      // Determinar período baseado no filtro atual
      let startDate, endDate;
      
      if (periodType === "daily") {
        startDate = selectedDate;
        endDate = selectedDate;
      } else if (periodType === "weekly") {
        const today = new Date();
        const weekAgo = new Date(today);
        weekAgo.setDate(today.getDate() - 7);
        startDate = weekAgo.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
      } else {
        // Mensal
        const firstDay = new Date(year, month - 1, 1);
        const lastDay = new Date(year, month, 0);
        startDate = firstDay.toISOString().split('T')[0];
        endDate = lastDay.toISOString().split('T')[0];
      }
      
      toast.info("Gerando AFD... Aguarde.");
      
      const response = await axios.get(
        `${API}/compliance/afd?start_date=${startDate}&end_date=${endDate}`,
        { responseType: 'blob' }
      );
      
      // Criar download
      const blob = new Blob([response.data], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `AFD_${startDate}_${endDate}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("✅ AFD gerado com sucesso! Arquivo em conformidade com a Portaria 671/2021.");
    } catch (error) {
      console.error("Erro ao gerar AFD:", error);
      if (error.response?.status === 404) {
        toast.error("Nenhum registro encontrado no período selecionado");
      } else {
        toast.error("Erro ao gerar AFD. Tente novamente.");
      }
    }
  };

  // Função para baixar relatório PDF para contabilidade
  const downloadPDF = () => {
    if (!employees.length) {
      toast.error("Não há funcionários para exportar");
      return;
    }

    const filtered = getFilteredRecords();
    if (!filtered.length) {
      toast.error("Não há registros no período selecionado");
      return;
    }

    const periodLabel = periodType === "daily" ? selectedDate : 
                        periodType === "weekly" ? "Últimos 7 dias" : 
                        `${months[month - 1]} ${year}`;

    // Criar PDF
    const doc = new jsPDF();
    
    // Título
    doc.setFontSize(18);
    doc.setTextColor(30, 58, 138); // Azul escuro
    doc.text("Relatório de Ponto", 14, 22);
    
    // Subtítulo
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text(`Período: ${periodLabel}`, 14, 32);
    doc.text(`Gerado em: ${new Date().toLocaleString("pt-BR")}`, 14, 40);
    
    // Resumo
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text("Resumo", 14, 55);
    
    doc.setFontSize(10);
    doc.text(`Total de Funcionários: ${employees.length}`, 14, 65);
    doc.text(`Total de Registros: ${filtered.length}`, 14, 72);
    doc.text(`Entradas: ${filtered.filter(r => r.record_type === "entry").length}`, 14, 79);
    doc.text(`Saídas: ${filtered.filter(r => r.record_type === "exit").length}`, 100, 79);
    
    // Tabela de funcionários - USANDO DADOS DO BACKEND
    const tableData = employees.map(emp => {
      const balance = calculateEmployeeBalance(emp.id);
      // Usar dados de faltas do backend (monthlyBalances)
      const backendData = monthlyBalances[emp.id];
      const absenceCount = backendData?.absences || 0;
      const balanceNum = parseFloat(balance.balance);
      return [
        emp.name,
        emp.department || "-",
        balance.worked,
        balance.expected,
        balanceNum > 0 ? `+${balance.balance}` : balance.balance,
        absenceCount > 0 ? `${absenceCount} FALTA(S)` : "OK"
      ];
    });

    autoTable(doc, {
      startY: 90,
      head: [["Funcionário", "Departamento", "Trabalhadas", "Esperadas", "Saldo", "Faltas"]],
      body: tableData,
      theme: "striped",
      headStyles: { 
        fillColor: [14, 165, 233], // Sky-500
        textColor: 255,
        fontStyle: "bold"
      },
      styles: {
        fontSize: 9,
        cellPadding: 4
      },
      columnStyles: {
        0: { cellWidth: 45 },
        1: { cellWidth: 30 },
        2: { cellWidth: 28, halign: "center" },
        3: { cellWidth: 28, halign: "center" },
        4: { cellWidth: 22, halign: "center" },
        5: { cellWidth: 28, halign: "center" }
      },
      didParseCell: function(data) {
        // Colorir saldo
        if (data.column.index === 4 && data.section === "body") {
          const value = data.cell.raw;
          if (value && value.startsWith("+")) {
            data.cell.styles.textColor = [22, 163, 74]; // Verde
          } else if (value && value.startsWith("-")) {
            data.cell.styles.textColor = [220, 38, 38]; // Vermelho
          }
        }
        // Colorir faltas em vermelho
        if (data.column.index === 5 && data.section === "body") {
          const value = data.cell.raw;
          if (value && value.includes("FALTA")) {
            data.cell.styles.textColor = [220, 38, 38]; // Vermelho
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fillColor = [254, 226, 226]; // Vermelho claro
          } else {
            data.cell.styles.textColor = [22, 163, 74]; // Verde
          }
        }
      }
    });

    // Rodapé
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `CiaPonto - Sistema de Ponto Eletrônico | Página ${i} de ${pageCount}`,
        doc.internal.pageSize.width / 2,
        doc.internal.pageSize.height - 10,
        { align: "center" }
      );
    }

    // Salvar PDF
    doc.save(`relatorio_ponto_${periodLabel.replace(/\s/g, "_")}.pdf`);
    toast.success("Relatório PDF baixado com sucesso!");
  };

  const filteredRecords = getFilteredRecords();

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Cabeçalho */}
      <div>
        <h1 className="font-heading font-bold text-2xl text-slate-900">Relatórios</h1>
        <p className="text-slate-600">Relatórios de horas trabalhadas por período e funcionário</p>
      </div>

      {/* Filtros */}
      <Card className="border border-slate-200">
        <CardContent className="p-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Tipo de Período */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Período</Label>
              <select
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                value={periodType}
                onChange={(e) => setPeriodType(e.target.value)}
              >
                <option value="daily">Diário</option>
                <option value="weekly">Semanal (últimos 7 dias)</option>
                <option value="monthly">Mensal</option>
              </select>
            </div>

            {/* Data (para diário) ou Mês/Ano (para mensal) */}
            {periodType === "daily" ? (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Data</Label>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="h-10"
                />
              </div>
            ) : periodType === "monthly" ? (
              <>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Mês</Label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    value={month}
                    onChange={(e) => setMonth(parseInt(e.target.value))}
                  >
                    {months.map((m, i) => (
                      <option key={i} value={i + 1}>{m}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Ano</Label>
                  <select
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                    value={year}
                    onChange={(e) => setYear(parseInt(e.target.value))}
                  >
                    {[2024, 2025, 2026].map(y => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </>
            ) : (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Período</Label>
                <div className="h-10 px-3 rounded-md border border-input bg-slate-50 flex items-center text-sm text-slate-600">
                  Últimos 7 dias
                </div>
              </div>
            )}

            {/* Filtro por Funcionário */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Funcionário</Label>
              <select
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
              >
                <option value="all">Todos os funcionários</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.name}</option>
                ))}
              </select>
            </div>

            {/* Botão Download */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">&nbsp;</Label>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button className="w-full bg-green-600 hover:bg-green-700 h-10">
                    <Download className="w-4 h-4 mr-2" />
                    Exportar
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={downloadPDF}>
                    <FileText className="w-4 h-4 mr-2" />
                    Relatório PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={downloadCSV}>
                    <FileText className="w-4 h-4 mr-2" />
                    Planilha CSV (Excel)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={downloadDetailedReport}>
                    <Download className="w-4 h-4 mr-2" />
                    Relatório JSON
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={downloadAFD} className="text-emerald-600">
                    <Shield className="w-4 h-4 mr-2" />
                    AFD (Portaria 671/2021)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1CA1BA]"></div>
        </div>
      ) : (
        <>
          {/* Resumo Geral */}
          <div className="grid sm:grid-cols-4 gap-4">
            <Card className="border border-slate-200">
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">Total Funcionários</p>
                <p className="font-heading font-bold text-2xl text-slate-900">{employees.length}</p>
              </CardContent>
            </Card>
            <Card className="border border-slate-200">
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">Registros no Período</p>
                <p className="font-heading font-bold text-2xl text-slate-900">{filteredRecords.length}</p>
              </CardContent>
            </Card>
            <Card className="border border-slate-200">
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">Entradas</p>
                <p className="font-heading font-bold text-2xl text-green-600">
                  {filteredRecords.filter(r => r.record_type === "entry").length}
                </p>
              </CardContent>
            </Card>
            <Card className="border border-slate-200">
              <CardContent className="p-4">
                <p className="text-sm text-slate-500">Saídas</p>
                <p className="font-heading font-bold text-2xl text-red-600">
                  {filteredRecords.filter(r => r.record_type === "exit").length}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Saldo de Horas por Funcionário */}
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Saldo de Horas por Funcionário</CardTitle>
              <CardDescription>Clique no funcionário para ver detalhes dos registros</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {employees.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left p-4 font-medium text-slate-600">Funcionário</th>
                        <th className="text-left p-4 font-medium text-slate-600">Departamento</th>
                        <th className="text-left p-4 font-medium text-slate-600">Horas Trabalhadas</th>
                        <th className="text-left p-4 font-medium text-slate-600">Horas Esperadas</th>
                        <th className="text-left p-4 font-medium text-slate-600">Saldo</th>
                        <th className="text-left p-4 font-medium text-slate-600">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employees.map(emp => {
                        const balance = calculateEmployeeBalance(emp.id);
                        return (
                          <tr key={emp.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center">
                                  <span className="text-ciaponto-cyan font-medium text-sm">{emp.name?.[0]}</span>
                                </div>
                                <span className="font-medium text-slate-900">{emp.name}</span>
                              </div>
                            </td>
                            <td className="p-4 text-slate-600">{emp.department || "-"}</td>
                            <td className="p-4 font-medium text-slate-900">{balance.worked}</td>
                            <td className="p-4 text-slate-600">{balance.expected}</td>
                            <td className="p-4">
                              <span className={`px-2 py-1 rounded-full text-sm font-medium ${
                                balance.balancePositive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                              }`}>
                                {balance.balance}
                              </span>
                            </td>
                            <td className="p-4">
                              <div className="flex gap-2">
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => openDetailedReport(emp)}
                                  className="text-ciaponto-cyan border-sky-300 hover:bg-cyan-50"
                                >
                                  <FileText className="w-4 h-4 mr-1" />
                                  Relatório
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => openBankHoursModal(emp)}
                                  className="text-amber-600 border-amber-300 hover:bg-amber-50"
                                >
                                  <Clock className="w-4 h-4 mr-1" />
                                  Banco
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-600">Nenhum funcionário cadastrado</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Registros Detalhados */}
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Registros do Período</CardTitle>
              <CardDescription>
                {selectedEmployee !== "all" 
                  ? `Mostrando registros de: ${employees.find(e => e.id === selectedEmployee)?.name || ""}` 
                  : "Mostrando todos os registros"}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              {filteredRecords.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left p-4 font-medium text-slate-600">Funcionário</th>
                        <th className="text-left p-4 font-medium text-slate-600">Tipo</th>
                        <th className="text-left p-4 font-medium text-slate-600">Data/Hora</th>
                        <th className="text-left p-4 font-medium text-slate-600">Foto</th>
                        <th className="text-left p-4 font-medium text-slate-600">GPS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRecords.slice(0, 50).map(record => (
                        <tr key={record.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="p-4 font-medium text-slate-900">{record.employee_name}</td>
                          <td className="p-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              record.record_type === "entry" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                            }`}>
                              {record.record_type === "entry" ? "Entrada" : "Saída"}
                            </span>
                          </td>
                          <td className="p-4 text-slate-600">
                            {new Date(record.timestamp).toLocaleString("pt-BR")}
                          </td>
                          <td className="p-4">
                            {record.photo_url ? (
                              <img src={record.photo_url} alt="Foto" className="w-8 h-8 rounded object-cover" />
                            ) : (
                              <span className="text-slate-400 text-sm">-</span>
                            )}
                          </td>
                          <td className="p-4">
                            {record.latitude ? (
                              <span className="text-green-600 text-sm">✓ Localizado</span>
                            ) : (
                              <span className="text-slate-400 text-sm">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="p-8 text-center">
                  <Clock className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-600">Nenhum registro encontrado no período</p>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Modal de Detalhes do Funcionário */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Detalhes do Funcionário</DialogTitle>
            <DialogDescription>
              Registros e saldo de horas de {selectedEmpDetails?.name}
            </DialogDescription>
          </DialogHeader>
          {selectedEmpDetails && (
            <div className="space-y-6">
              {/* Resumo */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-50 p-4 rounded-lg text-center">
                  <p className="text-sm text-slate-500">Horas Trabalhadas</p>
                  <p className="font-bold text-xl text-slate-900">{selectedEmpDetails.balance.worked}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg text-center">
                  <p className="text-sm text-slate-500">Horas Esperadas</p>
                  <p className="font-bold text-xl text-slate-900">{selectedEmpDetails.balance.expected}</p>
                </div>
                <div className={`p-4 rounded-lg text-center ${
                  selectedEmpDetails.balance.balancePositive ? "bg-green-50" : "bg-red-50"
                }`}>
                  <p className="text-sm text-slate-500">Saldo</p>
                  <p className={`font-bold text-xl ${
                    selectedEmpDetails.balance.balancePositive ? "text-green-600" : "text-red-600"
                  }`}>
                    {selectedEmpDetails.balance.balance}
                  </p>
                </div>
              </div>

              {/* Registros */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3">Registros ({selectedEmpDetails.records.length})</h4>
                {selectedEmpDetails.records.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {selectedEmpDetails.records.map(r => (
                      <div key={r.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            r.record_type === "entry" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                          }`}>
                            {r.record_type === "entry" ? "Entrada" : "Saída"}
                          </span>
                          <span className="text-slate-600">
                            {new Date(r.timestamp).toLocaleString("pt-BR")}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {r.photo_url && <Camera className="w-4 h-4 text-ciaponto-cyan" />}
                          {r.latitude && <MapPin className="w-4 h-4 text-green-500" />}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">Nenhum registro no período</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Ajuste de Banco de Horas */}
      <Dialog open={bankHoursOpen} onOpenChange={setBankHoursOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-600" />
              Banco de Horas - {bankHoursData?.employee?.name}
            </DialogTitle>
            <DialogDescription>
              Adicionar horas trabalhadas ou horas pendentes ao banco de horas
            </DialogDescription>
          </DialogHeader>
          
          {bankHoursLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600"></div>
            </div>
          ) : bankHoursData && (
            <div className="space-y-6">
              {/* Saldo Atual de Ajustes */}
              <div className={`p-4 rounded-lg text-center ${
                bankHoursData.manual_balance >= 0 ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"
              }`}>
                <p className="text-sm text-slate-600 mb-1">Saldo de Ajustes Manuais</p>
                <p className={`text-2xl font-bold ${
                  bankHoursData.manual_balance >= 0 ? "text-green-600" : "text-red-600"
                }`}>
                  {formatHoursMinutes(bankHoursData.manual_balance, true)}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  (Positivo = horas a compensar | Negativo = horas devidas)
                </p>
              </div>
              
              {/* Formulário de Ajuste */}
              <div className="bg-slate-50 p-4 rounded-lg space-y-4">
                <h4 className="font-medium text-slate-900 flex items-center gap-2">
                  <PlusCircle className="w-4 h-4" />
                  Novo Ajuste
                </h4>
                
                {/* Seletores de Mês e Ano */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Mês de Referência</Label>
                    <select
                      className="w-full mt-1 p-2 border border-slate-200 rounded-lg"
                      value={adjustmentForm.month || new Date().getMonth() + 1}
                      onChange={(e) => setAdjustmentForm({...adjustmentForm, month: parseInt(e.target.value)})}
                    >
                      <option value={1}>Janeiro</option>
                      <option value={2}>Fevereiro</option>
                      <option value={3}>Março</option>
                      <option value={4}>Abril</option>
                      <option value={5}>Maio</option>
                      <option value={6}>Junho</option>
                      <option value={7}>Julho</option>
                      <option value={8}>Agosto</option>
                      <option value={9}>Setembro</option>
                      <option value={10}>Outubro</option>
                      <option value={11}>Novembro</option>
                      <option value={12}>Dezembro</option>
                    </select>
                  </div>
                  
                  <div>
                    <Label>Ano de Referência</Label>
                    <select
                      className="w-full mt-1 p-2 border border-slate-200 rounded-lg"
                      value={adjustmentForm.year || new Date().getFullYear()}
                      onChange={(e) => setAdjustmentForm({...adjustmentForm, year: parseInt(e.target.value)})}
                    >
                      {[2024, 2025, 2026, 2027, 2028].map(y => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Tipo de Ajuste</Label>
                    <select
                      className="w-full mt-1 p-2 border border-slate-200 rounded-lg"
                      value={adjustmentForm.type}
                      onChange={(e) => setAdjustmentForm({...adjustmentForm, type: e.target.value})}
                    >
                      <option value="credit">Adicionar (Horas Trabalhadas)</option>
                      <option value="debit">Adicionar (Horas Pendentes)</option>
                    </select>
                    <p className="text-xs text-slate-500 mt-1">
                      {adjustmentForm.type === "credit" 
                        ? "Adiciona horas extras trabalhadas ao saldo"
                        : "Adiciona horas pendentes a serem compensadas"}
                    </p>
                  </div>
                  
                  <div>
                    <Label>Quantidade de Horas</Label>
                    <Input
                      type="number"
                      step="0.5"
                      min="0.5"
                      placeholder="Ex: 2.5"
                      value={adjustmentForm.hours}
                      onChange={(e) => setAdjustmentForm({...adjustmentForm, hours: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Motivo do Ajuste *</Label>
                  <textarea
                    className="w-full mt-1 p-2 border border-slate-200 rounded-lg resize-none"
                    rows={2}
                    placeholder="Ex: Compensação de banco de horas - folga dia 10/02"
                    value={adjustmentForm.reason}
                    onChange={(e) => setAdjustmentForm({...adjustmentForm, reason: e.target.value})}
                  />
                </div>
                
                <Button
                  onClick={handleBankHoursAdjustment}
                  disabled={adjusting || !adjustmentForm.hours || !adjustmentForm.reason}
                  className="w-full bg-amber-600 hover:bg-amber-700"
                >
                  {adjusting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Processando...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Registrar Ajuste
                    </>
                  )}
                </Button>
              </div>
              
              {/* Histórico de Ajustes */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Histórico de Ajustes ({bankHoursData.adjustments.length})
                </h4>
                
                {bankHoursData.adjustments.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {bankHoursData.adjustments.map(adj => (
                      <div 
                        key={adj.id} 
                        className={`p-3 rounded-lg border ${
                          adj.type === "credit" 
                            ? "bg-green-50 border-green-200" 
                            : "bg-amber-50 border-amber-200"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            adj.type === "credit"
                              ? "bg-green-100 text-green-700"
                              : "bg-amber-100 text-amber-700"
                          }`}>
                            {adj.type === "credit" ? "Horas Trabalhadas" : "Horas Pendentes"}
                          </span>
                          <div className="flex items-center gap-2">
                            {/* Mostrar mês/ano de referência */}
                            {adj.month && adj.year && (
                              <span className="px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-700">
                                {["", "Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"][adj.month]}/{adj.year}
                              </span>
                            )}
                            <span className={`font-bold ${
                              adj.type === "credit" ? "text-green-600" : "text-amber-600"
                            }`}>
                              {adj.type === "credit" ? "+" : "-"}{adj.hours}h
                            </span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteAdjustment(adj.id)}
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-sm text-slate-700">{adj.reason}</p>
                        <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
                          <span>Por: {adj.created_by}</span>
                          <span>{new Date(adj.created_at).toLocaleString("pt-BR")}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4 bg-slate-50 rounded-lg">
                    Nenhum ajuste realizado ainda
                  </p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Modal de Relatório Detalhado */}
      <DetailedReportModal
        isOpen={detailedReportOpen}
        onClose={() => setDetailedReportOpen(false)}
        employee={detailedReportEmployee}
        token={token}
      />
    </div>
  );
}

// WhatsApp Status Component
function WhatsAppPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [config, setConfig] = useState({
    twilio_account_sid: "",
    twilio_auth_token: "",
    twilio_whatsapp_number: "+14155238886"
  });
  const [testPhone, setTestPhone] = useState("");
  const [testMessage, setTestMessage] = useState("Teste de integração WhatsApp - CiaPonto");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/status`);
      setStatus(response.data);
    } catch (error) {
      toast.error("Erro ao verificar status do WhatsApp");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/whatsapp/config`, config);
      toast.success("Configuração salva com sucesso!");
      setShowConfig(false);
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar configuração");
    } finally {
      setSaving(false);
    }
  };

  const handleSendTest = async () => {
    if (!testPhone) {
      toast.error("Digite o número do WhatsApp");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/whatsapp/send`, {
        phone_number: testPhone,
        message: testMessage
      });
      toast.success("Mensagem enviada! Verifique o WhatsApp.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao enviar mensagem");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="whatsapp-page">
      <div>
        <h1 className="font-heading font-bold text-2xl text-slate-900">Integração WhatsApp</h1>
        <p className="text-slate-600">Configure a integração com WhatsApp Business</p>
      </div>

      {/* Status Card */}
      <Card className="border border-slate-200">
        <CardContent className="p-6">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1CA1BA]"></div>
            </div>
          ) : (
            <div className="text-center">
              <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${
                status?.connected ? "bg-green-100" : "bg-orange-100"
              }`}>
                <MessageSquare className={`w-8 h-8 ${status?.connected ? "text-green-600" : "text-orange-600"}`} />
              </div>
              <h3 className="font-heading font-bold text-xl mb-2">
                {status?.connected ? "WhatsApp Conectado" : "WhatsApp Não Configurado"}
              </h3>
              <p className="text-slate-600 mb-4">{status?.message}</p>
              {status?.whatsapp_number && (
                <p className="text-sm text-slate-500 mb-4">Número: {status.whatsapp_number}</p>
              )}
              
              <Button 
                onClick={() => setShowConfig(!showConfig)}
                variant={showConfig ? "outline" : "default"}
                className={!showConfig ? "bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark" : ""}
                data-testid="toggle-config-btn"
              >
                {showConfig ? "Fechar Configuração" : "Configurar Credenciais"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Configuration Form */}
      {showConfig && (
        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Configurar Twilio WhatsApp</CardTitle>
            <CardDescription>
              Digite suas credenciais do Twilio. Acesse console.twilio.com para obter.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSaveConfig} className="space-y-4">
              <div className="space-y-2">
                <Label>Account SID</Label>
                <Input
                  placeholder="AC..."
                  value={config.twilio_account_sid}
                  onChange={(e) => setConfig({ ...config, twilio_account_sid: e.target.value })}
                  required
                  data-testid="twilio-sid-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Auth Token</Label>
                <Input
                  type="password"
                  placeholder="Seu Auth Token"
                  value={config.twilio_auth_token}
                  onChange={(e) => setConfig({ ...config, twilio_auth_token: e.target.value })}
                  required
                  data-testid="twilio-token-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Número WhatsApp (Sandbox)</Label>
                <Input
                  placeholder="+14155238886"
                  value={config.twilio_whatsapp_number}
                  onChange={(e) => setConfig({ ...config, twilio_whatsapp_number: e.target.value })}
                  required
                  data-testid="twilio-number-input"
                />
              </div>
              <Button type="submit" className="w-full bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark" disabled={saving} data-testid="save-config-btn">
                {saving ? "Salvando..." : "Salvar Configuração"}
              </Button>
            </form>

            <div className="mt-6 p-4 bg-sky-50 rounded-lg border border-sky-100">
              <h4 className="font-medium text-sky-900 mb-2">Como obter as credenciais:</h4>
              <ol className="text-sm text-ciaponto-navy space-y-1">
                <li>1. Acesse <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="underline">console.twilio.com</a></li>
                <li>2. Na página inicial, copie o Account SID e Auth Token</li>
                <li>3. Vá em Messaging → Try it out → Send a WhatsApp message</li>
                <li>4. Conecte seu número enviando &quot;join [código]&quot; para o sandbox</li>
              </ol>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Test Message */}
      {status?.connected && (
        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Enviar Mensagem de Teste</CardTitle>
            <CardDescription>
              Teste a integração enviando uma mensagem para um número conectado ao sandbox.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Número WhatsApp (com DDD)</Label>
                <Input
                  placeholder="5588888467856"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  data-testid="test-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Mensagem</Label>
                <Input
                  value={testMessage}
                  onChange={(e) => setTestMessage(e.target.value)}
                  data-testid="test-message-input"
                />
              </div>
              <Button onClick={handleSendTest} className="w-full bg-green-600 hover:bg-green-700" disabled={sending} data-testid="send-test-btn">
                {sending ? "Enviando..." : "Enviar Mensagem de Teste"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

    </div>
  );
}

// Correção de Ponto Component
function PointCorrectionPage() {
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [newRecord, setNewRecord] = useState({
    employee_id: "",
    record_type: "entry",
    timestamp: "",
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [empResponse, recResponse] = await Promise.all([
        axios.get(`${API}/employees`),
        axios.get(`${API}/time-records`)
      ]);
      setEmployees(empResponse.data);
      setRecords(recResponse.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleAddRecord = async (e) => {
    e.preventDefault();
    if (!newRecord.employee_id || !newRecord.timestamp) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    try {
      await axios.post(`${API}/time-records/manual`, {
        ...newRecord,
        source: "manual_correction"
      });
      toast.success("Registro adicionado com sucesso!");
      setDialogOpen(false);
      setNewRecord({ employee_id: "", record_type: "entry", timestamp: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao adicionar registro");
    }
  };

  const handleEditRecord = async (e) => {
    e.preventDefault();
    if (!editingRecord) return;

    try {
      await axios.put(`${API}/time-records/${editingRecord.id}`, {
        timestamp: editingRecord.timestamp,
        record_type: editingRecord.record_type,
        notes: editingRecord.notes
      });
      toast.success("Registro atualizado com sucesso!");
      setEditDialogOpen(false);
      setEditingRecord(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao atualizar registro");
    }
  };

  const handleDeleteRecord = async (recordId) => {
    if (!window.confirm("Tem certeza que deseja excluir este registro?")) return;
    
    try {
      await axios.delete(`${API}/time-records/${recordId}`);
      toast.success("Registro excluído com sucesso!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir registro");
    }
  };

  const filteredRecords = selectedEmployee
    ? records.filter(r => r.employee_id === selectedEmployee)
    : records;

  const getEmployeeName = (employeeId) => {
    const emp = employees.find(e => e.id === employeeId);
    return emp ? emp.name : "Desconhecido";
  };

  return (
    <div className="space-y-6" data-testid="point-correction-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">Correção de Ponto</h1>
          <p className="text-slate-600">Corrija ou adicione registros de ponto dos funcionários</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark" data-testid="add-manual-record-btn">
              <PlusCircle className="w-4 h-4 mr-2" />
              Adicionar Registro Manual
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Adicionar Registro Manual</DialogTitle>
              <DialogDescription>
                Adicione um registro de ponto que foi esquecido pelo funcionário
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddRecord} className="space-y-4">
              <div className="space-y-2">
                <Label>Funcionário</Label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  value={newRecord.employee_id}
                  onChange={(e) => setNewRecord({ ...newRecord, employee_id: e.target.value })}
                  required
                >
                  <option value="">Selecione...</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Tipo de Registro</Label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  value={newRecord.record_type}
                  onChange={(e) => setNewRecord({ ...newRecord, record_type: e.target.value })}
                >
                  <option value="entry">Entrada</option>
                  <option value="exit">Saída</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>Data e Hora</Label>
                <Input
                  type="datetime-local"
                  value={newRecord.timestamp}
                  onChange={(e) => setNewRecord({ ...newRecord, timestamp: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Motivo / Observação</Label>
                <Input
                  placeholder="Ex: Funcionário esqueceu de bater o ponto"
                  value={newRecord.notes}
                  onChange={(e) => setNewRecord({ ...newRecord, notes: e.target.value })}
                />
              </div>
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1 bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark">
                  Adicionar Registro
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filtro por funcionário */}
      <div className="flex items-center gap-4">
        <Label className="whitespace-nowrap">Filtrar por funcionário:</Label>
        <select
          className="h-10 px-3 rounded-md border border-input bg-background text-sm max-w-xs"
          value={selectedEmployee}
          onChange={(e) => setSelectedEmployee(e.target.value)}
        >
          <option value="">Todos os funcionários</option>
          {employees.map(emp => (
            <option key={emp.id} value={emp.id}>{emp.name}</option>
          ))}
        </select>
      </div>

      {/* Lista de registros */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1CA1BA]"></div>
        </div>
      ) : filteredRecords.length > 0 ? (
        <Card className="border border-slate-200">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left p-4 font-medium text-slate-600">Funcionário</th>
                    <th className="text-left p-4 font-medium text-slate-600">Tipo</th>
                    <th className="text-left p-4 font-medium text-slate-600">Data/Hora</th>
                    <th className="text-left p-4 font-medium text-slate-600">Origem</th>
                    <th className="text-left p-4 font-medium text-slate-600">Observação</th>
                    <th className="text-right p-4 font-medium text-slate-600">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.slice(0, 50).map((record) => (
                    <tr key={record.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-4 font-medium text-slate-900">{record.employee_name || getEmployeeName(record.employee_id)}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          record.record_type === "entry" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                        }`}>
                          {record.record_type === "entry" ? "Entrada" : "Saída"}
                        </span>
                      </td>
                      <td className="p-4 text-slate-600">
                        {new Date(record.timestamp).toLocaleString("pt-BR")}
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          record.source === "manual_correction" ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-700"
                        }`}>
                          {record.source === "manual_correction" ? "Correção" : record.source || "Web"}
                        </span>
                      </td>
                      <td className="p-4 text-slate-500 text-sm">{record.notes || "-"}</td>
                      <td className="p-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingRecord({
                                ...record,
                                timestamp: record.timestamp.slice(0, 16)
                              });
                              setEditDialogOpen(true);
                            }}
                          >
                            <Edit3 className="w-4 h-4 text-ciaponto-cyan" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteRecord(record.id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border border-slate-200">
          <CardContent className="p-12 text-center">
            <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">Nenhum registro encontrado</p>
          </CardContent>
        </Card>
      )}

      {/* Dialog de edição */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Editar Registro de Ponto</DialogTitle>
            <DialogDescription>
              Corrija as informações do registro de ponto
            </DialogDescription>
          </DialogHeader>
          {editingRecord && (
            <form onSubmit={handleEditRecord} className="space-y-4">
              <div className="space-y-2">
                <Label>Tipo de Registro</Label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  value={editingRecord.record_type}
                  onChange={(e) => setEditingRecord({ ...editingRecord, record_type: e.target.value })}
                >
                  <option value="entry">Entrada</option>
                  <option value="exit">Saída</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label>Data e Hora</Label>
                <Input
                  type="datetime-local"
                  value={editingRecord.timestamp}
                  onChange={(e) => setEditingRecord({ ...editingRecord, timestamp: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Observação</Label>
                <Input
                  placeholder="Motivo da correção"
                  value={editingRecord.notes || ""}
                  onChange={(e) => setEditingRecord({ ...editingRecord, notes: e.target.value })}
                />
              </div>
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)} className="flex-1">
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1 bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark">
                  Salvar Alterações
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== FÉRIAS PAGE - GERENCIAMENTO DE FÉRIAS ====================
function FeriasPage() {
  const [ferias, setFerias] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFerias, setEditingFerias] = useState(null);
  const [activeVacations, setActiveVacations] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterEmployee, setFilterEmployee] = useState("all");
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    employee_id: "",
    start_date: "",
    end_date: "",
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fazer chamadas em paralelo mas com tratamento individual de erros
      const results = await Promise.allSettled([
        axios.get(`${API}/vacations`),
        axios.get(`${API}/employees`),
        axios.get(`${API}/vacations/active`)
      ]);
      
      // Processar resultados individualmente
      if (results[0].status === 'fulfilled') {
        setFerias(Array.isArray(results[0].value.data) ? results[0].value.data : []);
      } else {
        console.error("Erro ao carregar férias:", results[0].reason);
        setFerias([]);
      }
      
      if (results[1].status === 'fulfilled') {
        setEmployees(Array.isArray(results[1].value.data) ? results[1].value.data : []);
      } else {
        console.error("Erro ao carregar funcionários:", results[1].reason);
        setEmployees([]);
      }
      
      if (results[2].status === 'fulfilled') {
        const activeData = results[2].value.data;
        setActiveVacations(Array.isArray(activeData?.employees_on_vacation) ? activeData.employees_on_vacation : []);
      } else {
        console.error("Erro ao carregar férias ativas:", results[2].reason);
        setActiveVacations([]);
      }
      
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      setError("Erro ao carregar dados de férias. Tente novamente.");
      toast.error("Erro ao carregar dados de férias");
    } finally {
      setLoading(false);
    }
  };

  const openNewDialog = () => {
    setEditingFerias(null);
    setFormData({
      employee_id: "",
      start_date: "",
      end_date: "",
      notes: ""
    });
    setDialogOpen(true);
  };

  const openEditDialog = (vacation) => {
    setEditingFerias(vacation);
    setFormData({
      employee_id: vacation.employee_id,
      start_date: vacation.start_date,
      end_date: vacation.end_date,
      notes: vacation.notes || ""
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.employee_id || !formData.start_date || !formData.end_date) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    try {
      if (editingFerias) {
        await axios.put(`${API}/vacations/${editingFerias.id}`, formData);
        toast.success("Férias atualizadas com sucesso!");
      } else {
        await axios.post(`${API}/vacations`, formData);
        toast.success("Férias cadastradas com sucesso!");
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Erro ao salvar férias:", error);
      toast.error(error.response?.data?.detail || "Erro ao salvar férias");
    }
  };

  const handleCancel = async (vacationId) => {
    if (!window.confirm("Tem certeza que deseja cancelar estas férias?")) return;
    
    try {
      await axios.delete(`${API}/vacations/${vacationId}`);
      toast.success("Férias canceladas com sucesso!");
      fetchData();
    } catch (error) {
      console.error("Erro ao cancelar férias:", error);
      toast.error("Erro ao cancelar férias");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      scheduled: { label: "Agendada", color: "bg-blue-100 text-blue-700" },
      active: { label: "Em Férias", color: "bg-green-100 text-green-700" },
      completed: { label: "Concluída", color: "bg-gray-100 text-gray-700" },
      cancelled: { label: "Cancelada", color: "bg-red-100 text-red-700" }
    };
    const config = statusConfig[status] || statusConfig.scheduled;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>{config.label}</span>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr + "T12:00:00");
    return date.toLocaleDateString("pt-BR");
  };

  const filteredFerias = ferias.filter(v => {
    if (filterStatus !== "all" && v.status !== filterStatus) return false;
    if (filterEmployee !== "all" && v.employee_id !== filterEmployee) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1CA1BA]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={fetchData} className="bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark">
          Tentar Novamente
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ferias-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">Férias</h1>
          <p className="text-slate-500 text-sm mt-1">Gerencie as férias dos funcionários</p>
        </div>
        <Button onClick={openNewDialog} className="bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark">
          <Plus className="w-4 h-4 mr-2" />
          Cadastrar Férias
        </Button>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500 rounded-lg">
                <Palmtree className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-green-600">De Férias Agora</p>
                <p className="text-2xl font-bold text-green-700">{activeVacations.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500 rounded-lg">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-blue-600">Agendadas</p>
                <p className="text-2xl font-bold text-blue-700">{ferias.filter(v => v.status === "scheduled").length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-500 rounded-lg">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Concluídas</p>
                <p className="text-2xl font-bold text-gray-700">{ferias.filter(v => v.status === "completed").length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-500 rounded-lg">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total Cadastradas</p>
                <p className="text-2xl font-bold text-slate-700">{ferias.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Funcionários de Férias (se houver) */}
      {activeVacations && activeVacations.length > 0 && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2 text-green-700">
              <Palmtree className="w-5 h-5" />
              Funcionários de Férias Agora
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {activeVacations.map((v, index) => (
                <div key={v?.vacation_id || index} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-green-200">
                  <div className="w-10 h-10 rounded-full bg-green-200 flex items-center justify-center">
                    {v?.employee_photo ? (
                      <img src={v.employee_photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <User className="w-5 h-5 text-green-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 truncate">{v?.employee_name || "Funcionário"}</p>
                    <p className="text-xs text-slate-500">{v?.employee_position || "Funcionário"}</p>
                    <p className="text-xs text-green-600">
                      Volta em {v?.days_remaining || 0} dia(s) • {formatDate(v?.end_date)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Funcionário</label>
              <select
                value={filterEmployee}
                onChange={(e) => setFilterEmployee(e.target.value)}
                className="w-full p-2 border rounded-lg text-sm"
              >
                <option value="all">Todos os funcionários</option>
                {employees && employees.map((emp, index) => (
                  <option key={emp?.id || index} value={emp?.id || ""}>{emp?.name || "Funcionário"}</option>
                ))}
              </select>
            </div>
            <div className="min-w-[150px]">
              <label className="text-sm font-medium text-slate-700 mb-1 block">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full p-2 border rounded-lg text-sm"
              >
                <option value="all">Todos</option>
                <option value="active">Em Férias</option>
                <option value="scheduled">Agendadas</option>
                <option value="completed">Concluídas</option>
                <option value="cancelled">Canceladas</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabela de Férias */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Histórico de Férias</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredFerias.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Palmtree className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>Nenhum registro de férias encontrado</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Funcionário</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Período</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Dias</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Observações</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFerias.map((vacation, index) => (
                    <tr key={vacation?.id || index} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <p className="font-medium text-slate-900">{vacation?.employee_name || "N/A"}</p>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {formatDate(vacation?.start_date)} - {formatDate(vacation?.end_date)}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {vacation?.total_days || 0} dias
                      </td>
                      <td className="py-3 px-4">
                        {getStatusBadge(vacation?.status)}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-500 max-w-[200px] truncate">
                        {vacation?.notes || "-"}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex justify-end gap-2">
                          {vacation?.status !== "cancelled" && vacation?.status !== "completed" && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openEditDialog(vacation)}
                              >
                                <Edit3 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => handleCancel(vacation?.id)}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Cadastro/Edição */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingFerias ? "Editar Férias" : "Cadastrar Férias"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Funcionário <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.employee_id}
                onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                className="w-full p-2 border rounded-lg"
                required
                disabled={editingFerias}
              >
                <option value="">Selecione o funcionário</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.name}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Data Início <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="w-full p-2 border rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Data Fim <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="w-full p-2 border rounded-lg"
                  required
                />
              </div>
            </div>

            {formData.start_date && formData.end_date && (
              <div className="p-3 bg-sky-50 rounded-lg">
                <p className="text-sm text-ciaponto-navy">
                  <strong>Total:</strong>{" "}
                  {Math.max(1, Math.ceil((new Date(formData.end_date) - new Date(formData.start_date)) / (1000 * 60 * 60 * 24)) + 1)} dias de férias
                </p>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Observações</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="w-full p-2 border rounded-lg"
                rows={3}
                placeholder="Observações sobre as férias (opcional)"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark">
                {editingFerias ? "Salvar Alterações" : "Cadastrar Férias"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== ATESTADOS PAGE - GERENCIAMENTO DE ATESTADOS E DISPENSAS ====================
function AtestadosPage() {
  const [atestados, setAtestados] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAtestado, setEditingAtestado] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [uploading, setUploading] = useState(false);
  const [viewImageUrl, setViewImageUrl] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]); // Imagens selecionadas para upload
  const [imagePreview, setImagePreview] = useState([]); // Preview das imagens
  const [selectedAtestadoForImages, setSelectedAtestadoForImages] = useState(null); // Atestado selecionado para ver imagens
  
  const [formData, setFormData] = useState({
    employee_id: "",
    leave_type: "atestado_medico",
    start_date: "",
    end_date: "",
    hours: "",
    reason: "",
    doctor_name: "",
    crm: ""
  });

  const leaveTypes = [
    { value: "atestado_medico", label: "Atestado Médico", icon: Stethoscope, color: "bg-red-100 text-red-700" },
    { value: "licenca_maternidade", label: "Licença Maternidade", icon: Baby, color: "bg-pink-100 text-pink-700" },
    { value: "licenca_paternidade", label: "Licença Paternidade", icon: Baby, color: "bg-blue-100 text-blue-700" },
    { value: "luto", label: "Licença Luto", icon: Heart, color: "bg-slate-100 text-slate-700" },
    { value: "casamento", label: "Licença Casamento", icon: Heart, color: "bg-rose-100 text-rose-700" },
    { value: "outro", label: "Outro", icon: HelpCircle, color: "bg-amber-100 text-amber-700" }
  ];

  useEffect(() => {
    fetchAtestados();
    fetchEmployees();
    fetchDashboard();
  }, [selectedMonth, selectedYear]);

  // Função para formatar URL da imagem (adiciona URL base se necessário)
  const getImageUrl = (url) => {
    if (!url) return null;
    // Se a URL já é absoluta, retorna como está
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // Se é uma URL relativa do backend, adiciona a URL base
    if (url.startsWith('/api/')) {
      const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
      return `${baseUrl}${url}`;
    }
    return url;
  };

  const fetchAtestados = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/medical-leaves?month=${selectedMonth}&year=${selectedYear}`);
      setAtestados(response.data);
    } catch (error) {
      toast.error("Erro ao carregar atestados");
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error("Erro ao carregar funcionários");
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/medical-leaves/summary/dashboard`);
      setDashboardData(response.data);
    } catch (error) {
      console.error("Erro ao carregar dashboard de atestados");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setUploading(true);
      const payload = {
        ...formData,
        hours: formData.hours ? parseFloat(formData.hours) : null,
        end_date: formData.end_date || null
      };

      let atestadoId;
      
      if (editingAtestado) {
        await axios.put(`${API}/medical-leaves/${editingAtestado.id}`, payload);
        atestadoId = editingAtestado.id;
        toast.success("Atestado atualizado com sucesso!");
      } else {
        const response = await axios.post(`${API}/medical-leaves`, payload);
        atestadoId = response.data.id;
        toast.success("Atestado cadastrado com sucesso!");
      }

      // Upload das imagens selecionadas
      if (selectedImages.length > 0 && atestadoId) {
        for (const file of selectedImages) {
          const formDataUpload = new FormData();
          formDataUpload.append("file", file);
          await axios.post(`${API}/medical-leaves/${atestadoId}/images`, formDataUpload, {
            headers: { "Content-Type": "multipart/form-data" }
          });
        }
        toast.success(`${selectedImages.length} imagem(ns) enviada(s)!`);
      }

      setDialogOpen(false);
      resetForm();
      fetchAtestados();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar atestado");
    } finally {
      setUploading(false);
    }
  };

  // Manipular seleção de imagens
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setSelectedImages(prev => [...prev, ...files]);
      
      // Criar previews
      files.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreview(prev => [...prev, { file: file.name, url: reader.result }]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  // Remover imagem selecionada (antes do upload)
  const removeSelectedImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreview(prev => prev.filter((_, i) => i !== index));
  };

  // Resetar formulário
  const resetForm = () => {
    setFormData({
      employee_id: "",
      leave_type: "atestado_medico",
      start_date: "",
      end_date: "",
      hours: "",
      reason: "",
      doctor_name: "",
      crm: ""
    });
    setSelectedImages([]);
    setImagePreview([]);
    setEditingAtestado(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este atestado?")) return;
    try {
      await axios.delete(`${API}/medical-leaves/${id}`);
      toast.success("Atestado excluído com sucesso!");
      fetchAtestados();
      fetchDashboard();
    } catch (error) {
      toast.error("Erro ao excluir atestado");
    }
  };

  const handleImageUpload = async (atestadoId, files) => {
    setUploading(true);
    try {
      for (const file of files) {
        const formDataUpload = new FormData();
        formDataUpload.append("file", file);
        await axios.post(`${API}/medical-leaves/${atestadoId}/images`, formDataUpload, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }
      toast.success("Imagens enviadas com sucesso!");
      fetchAtestados();
    } catch (error) {
      toast.error("Erro ao enviar imagens");
    } finally {
      setUploading(false);
    }
  };

  // Deletar imagem de atestado existente
  const handleDeleteImage = async (atestadoId, imageUrl) => {
    if (!window.confirm("Tem certeza que deseja excluir esta imagem?")) return;
    
    try {
      await axios.delete(`${API}/medical-leaves/${atestadoId}/images`, {
        params: { image_url: imageUrl }
      });
      toast.success("Imagem excluída com sucesso!");
      setViewImageUrl(null);
      setSelectedAtestadoForImages(null);
      fetchAtestados();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir imagem");
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(
        `${API}/medical-leaves/report/pdf?month=${selectedMonth}&year=${selectedYear}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `atestados_${selectedMonth}_${selectedYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Relatório gerado com sucesso!");
    } catch (error) {
      toast.error("Erro ao gerar relatório PDF");
    }
  };

  const handleDownloadExcel = async () => {
    try {
      const response = await axios.get(
        `${API}/reports/absences/excel?month=${selectedMonth}&year=${selectedYear}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `relatorio_ausencias_${selectedMonth}_${selectedYear}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Relatório Excel gerado com sucesso!");
    } catch (error) {
      toast.error("Erro ao gerar relatório Excel");
    }
  };

  const openEditDialog = (atestado) => {
    setEditingAtestado(atestado);
    setFormData({
      employee_id: atestado.employee_id,
      leave_type: atestado.leave_type,
      start_date: atestado.start_date,
      end_date: atestado.end_date || "",
      hours: atestado.hours || "",
      reason: atestado.reason || "",
      doctor_name: atestado.doctor_name || "",
      crm: atestado.crm || ""
    });
    setDialogOpen(true);
  };

  const getLeaveTypeInfo = (type) => {
    return leaveTypes.find(t => t.value === type) || leaveTypes[5];
  };

  const months = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Atestados e Dispensas</h1>
          <p className="text-slate-500">Gerencie atestados médicos e outras dispensas dos funcionários</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handleDownloadExcel} data-testid="download-excel-btn" className="bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100">
            <FileText className="w-4 h-4 mr-2" />
            Relatório Excel (RH)
          </Button>
          <Button variant="outline" onClick={handleDownloadPDF} data-testid="download-pdf-btn">
            <Download className="w-4 h-4 mr-2" />
            Relatório PDF
          </Button>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="new-atestado-btn">
            <Plus className="w-4 h-4 mr-2" />
            Novo Atestado
          </Button>
        </div>
      </div>

      {/* Dashboard Cards */}
      {dashboardData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600">Total de Atestados</p>
                  <p className="text-3xl font-bold text-red-700">{dashboardData.summary.total_leaves}</p>
                  <p className="text-xs text-red-500">Últimos 30 dias</p>
                </div>
                <Stethoscope className="w-10 h-10 text-red-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-600">Horas Dispensadas</p>
                  <p className="text-3xl font-bold text-amber-700">{dashboardData.summary.total_hours}h</p>
                  <p className="text-xs text-amber-500">Total creditado</p>
                </div>
                <Clock className="w-10 h-10 text-amber-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600">Funcionários</p>
                  <p className="text-3xl font-bold text-blue-700">{dashboardData.summary.employees_affected}</p>
                  <p className="text-xs text-blue-500">Com atestados</p>
                </div>
                <Users className="w-10 h-10 text-blue-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-emerald-600">Por Tipo</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {dashboardData.summary.by_type.slice(0, 3).map((t, i) => (
                      <span key={i} className="text-xs bg-emerald-200 text-emerald-700 px-2 py-0.5 rounded">
                        {t.label}: {t.count}
                      </span>
                    ))}
                  </div>
                </div>
                <ClipboardList className="w-10 h-10 text-emerald-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Label>Mês:</Label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="border rounded-lg px-3 py-2 text-sm"
              >
                {months.map((m, i) => (
                  <option key={i} value={i + 1}>{m}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <Label>Ano:</Label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="border rounded-lg px-3 py-2 text-sm"
              >
                {[2024, 2025, 2026, 2027].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Atestados */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5" />
            Atestados de {months[selectedMonth - 1]} {selectedYear}
          </CardTitle>
          <CardDescription>
            {atestados.length} atestado(s) registrado(s)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600"></div>
            </div>
          ) : atestados.length === 0 ? (
            <div className="text-center py-10">
              <Stethoscope className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Nenhum atestado registrado neste período</p>
              <Button className="mt-4" onClick={() => { resetForm(); setDialogOpen(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                Cadastrar Atestado
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {atestados.map((atestado) => {
                const typeInfo = getLeaveTypeInfo(atestado.leave_type);
                const TypeIcon = typeInfo.icon;
                return (
                  <div
                    key={atestado.id}
                    className="border rounded-lg p-4 hover:bg-slate-50 transition-colors"
                    data-testid={`atestado-${atestado.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-lg ${typeInfo.color}`}>
                          <TypeIcon className="w-6 h-6" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900">{atestado.employee_name}</h3>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <span className={`text-xs px-2 py-1 rounded-full ${typeInfo.color}`}>
                              {atestado.leave_type_label}
                            </span>
                            <span className="text-sm text-slate-500">
                              <CalendarDays className="w-3 h-3 inline mr-1" />
                              {atestado.start_date}
                              {atestado.end_date && atestado.end_date !== atestado.start_date && (
                                <> a {atestado.end_date}</>
                              )}
                            </span>
                            <span className="text-sm font-medium text-amber-600">
                              <Clock className="w-3 h-3 inline mr-1" />
                              {atestado.total_hours}h creditadas
                            </span>
                          </div>
                          {atestado.reason && (
                            <p className="text-sm text-slate-500 mt-1">{atestado.reason}</p>
                          )}
                          {atestado.doctor_name && (
                            <p className="text-xs text-slate-400 mt-1">
                              Dr(a). {atestado.doctor_name} {atestado.crm && `- CRM: ${atestado.crm}`}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {/* Imagens - ao clicar abre modal com todas as imagens */}
                        {atestado.images && atestado.images.length > 0 && (
                          <button
                            onClick={() => setSelectedAtestadoForImages(atestado)}
                            className="flex -space-x-2 hover:opacity-80 transition-opacity"
                            title="Ver todas as imagens"
                          >
                            {atestado.images.slice(0, 3).map((img, i) => (
                              <div
                                key={i}
                                className="w-8 h-8 rounded border-2 border-white bg-slate-100 overflow-hidden hover:z-10 hover:scale-110 transition-transform"
                              >
                                <img src={getImageUrl(img)} alt="" className="w-full h-full object-cover" />
                              </div>
                            ))}
                            {atestado.images.length > 3 && (
                              <span className="w-8 h-8 rounded border-2 border-white bg-slate-200 flex items-center justify-center text-xs font-medium">
                                +{atestado.images.length - 3}
                              </span>
                            )}
                          </button>
                        )}

                        {/* Upload de imagem */}
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            multiple
                            className="hidden"
                            onChange={(e) => handleImageUpload(atestado.id, e.target.files)}
                            disabled={uploading}
                          />
                          <div className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700">
                            <FileImage className="w-5 h-5" />
                          </div>
                        </label>

                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(atestado)}>
                              <Pencil className="w-4 h-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => handleDelete(atestado.id)}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Excluir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Visualização de Imagens do Atestado */}
      <Dialog open={!!selectedAtestadoForImages} onOpenChange={() => setSelectedAtestadoForImages(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileImage className="w-5 h-5" />
              Imagens do Atestado
            </DialogTitle>
            <DialogDescription>
              {selectedAtestadoForImages?.employee_name} - {selectedAtestadoForImages?.start_date}
            </DialogDescription>
          </DialogHeader>
          
          {selectedAtestadoForImages?.images && selectedAtestadoForImages.images.length > 0 ? (
            <div className="grid grid-cols-2 gap-4">
              {selectedAtestadoForImages.images.map((img, index) => (
                <div key={index} className="relative group">
                  <img 
                    src={getImageUrl(img)} 
                    alt={`Atestado ${index + 1}`} 
                    className="w-full h-48 object-cover rounded-lg border border-slate-200 cursor-pointer hover:opacity-90"
                    onClick={() => window.open(getImageUrl(img), '_blank')}
                  />
                  <button
                    onClick={() => handleDeleteImage(selectedAtestadoForImages.id, img)}
                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
                    title="Excluir imagem"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <span className="absolute bottom-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                    Imagem {index + 1}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <FileImage className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>Nenhuma imagem cadastrada</p>
            </div>
          )}
          
          {/* Upload de nova imagem */}
          {selectedAtestadoForImages && (
            <div className="mt-4 border-t pt-4">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    handleImageUpload(selectedAtestadoForImages.id, e.target.files);
                    setTimeout(() => {
                      // Atualizar o atestado selecionado após upload
                      const updatedAtestado = atestados.find(a => a.id === selectedAtestadoForImages.id);
                      if (updatedAtestado) setSelectedAtestadoForImages(updatedAtestado);
                    }, 1500);
                  }}
                  disabled={uploading}
                />
                <div className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-300 rounded-lg hover:border-sky-400 transition-colors">
                  <Upload className="w-5 h-5 text-slate-400" />
                  <span className="text-sm text-slate-600">
                    {uploading ? "Enviando..." : "Adicionar mais imagens"}
                  </span>
                </div>
              </label>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Visualização de Imagem Individual (mantido para compatibilidade) */}
      <Dialog open={!!viewImageUrl} onOpenChange={() => setViewImageUrl(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Imagem do Atestado</DialogTitle>
          </DialogHeader>
          {viewImageUrl && (
            <img src={viewImageUrl} alt="Atestado" className="w-full rounded-lg" />
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Cadastro/Edição */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setDialogOpen(open); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Stethoscope className="w-5 h-5" />
              {editingAtestado ? "Editar Atestado" : "Novo Atestado"}
            </DialogTitle>
            <DialogDescription>
              Cadastre uma dispensa ou atestado médico para um funcionário
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Funcionário */}
            <div className="space-y-2">
              <Label>Funcionário *</Label>
              <select
                value={formData.employee_id}
                onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                className="w-full border rounded-lg px-3 py-2"
                required
                disabled={!!editingAtestado}
              >
                <option value="">Selecione um funcionário</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.name} - {emp.position}</option>
                ))}
              </select>
            </div>

            {/* Tipo de Dispensa */}
            <div className="space-y-2">
              <Label>Tipo de Dispensa *</Label>
              <div className="grid grid-cols-2 gap-2">
                {leaveTypes.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, leave_type: type.value })}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        formData.leave_type === type.value
                          ? "border-[#1CA1BA] bg-sky-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded ${type.color}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <span className="text-sm font-medium">{type.label}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Datas */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data Início *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Data Fim</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  min={formData.start_date}
                />
                <p className="text-xs text-slate-500">Deixe vazio se for apenas 1 dia</p>
              </div>
            </div>

            {/* Horas (opcional) */}
            <div className="space-y-2">
              <Label>Horas Específicas (opcional)</Label>
              <Input
                type="number"
                step="0.5"
                min="0.5"
                max="24"
                placeholder="Ex: 4 (para meio período)"
                value={formData.hours}
                onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
              />
              <p className="text-xs text-slate-500">
                Preencha apenas se for uma dispensa parcial (ex: consulta médica de 2h)
              </p>
            </div>

            {/* Motivo */}
            <div className="space-y-2">
              <Label>Motivo/Observação</Label>
              <Input
                placeholder="Ex: Gripe, Consulta médica, etc"
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              />
            </div>

            {/* Dados do Médico (apenas para atestado médico) */}
            {formData.leave_type === "atestado_medico" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome do Médico</Label>
                  <Input
                    placeholder="Dr(a). Nome"
                    value={formData.doctor_name}
                    onChange={(e) => setFormData({ ...formData, doctor_name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>CRM</Label>
                  <Input
                    placeholder="12345-UF"
                    value={formData.crm}
                    onChange={(e) => setFormData({ ...formData, crm: e.target.value })}
                  />
                </div>
              </div>
            )}

            {/* Upload de Imagem do Atestado */}
            <div className="space-y-2">
              <Label>Foto do Atestado</Label>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 hover:border-sky-400 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageSelect}
                  className="hidden"
                  id="atestado-image-upload"
                />
                <label
                  htmlFor="atestado-image-upload"
                  className="flex flex-col items-center justify-center cursor-pointer"
                >
                  <Upload className="w-8 h-8 text-slate-400 mb-2" />
                  <span className="text-sm text-slate-600">Clique para adicionar imagem(ns)</span>
                  <span className="text-xs text-slate-400 mt-1">JPG, PNG ou PDF</span>
                </label>
              </div>
              
              {/* Preview das imagens selecionadas */}
              {imagePreview.length > 0 && (
                <div className="mt-3 space-y-2">
                  <p className="text-sm font-medium text-slate-700">Imagens selecionadas:</p>
                  <div className="flex flex-wrap gap-2">
                    {imagePreview.map((img, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={img.url}
                          alt={`Preview ${index + 1}`}
                          className="w-20 h-20 object-cover rounded-lg border border-slate-200"
                        />
                        <button
                          type="button"
                          onClick={() => removeSelectedImage(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Info box */}
            <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                As horas de dispensa serão <strong>creditadas automaticamente</strong> ao funcionário e aparecerão destacadas nos registros de ponto.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">
                <Save className="w-4 h-4 mr-2" />
                {editingAtestado ? "Salvar Alterações" : "Cadastrar Atestado"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== HOURS HISTORY PAGE - HISTÓRICO DE BANCO DE HORAS ====================
// ==================== HOURS HISTORY PAGE - HISTÓRICO DE BANCO DE HORAS ====================
function HoursHistoryPage() {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Filtros de período
  const [periodType, setPeriodType] = useState("monthly"); // monthly, weekly, yearly
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const months = [
    "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
  ];

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data || []);
    } catch (error) {
      console.error("Erro ao buscar funcionários:", error);
      toast.error("Erro ao carregar funcionários");
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async (employee) => {
    setSelectedEmployee(employee);
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/employees/${employee.id}/hours-history`);
      setHistoryData(response.data);
    } catch (error) {
      console.error("Erro ao buscar histórico:", error);
      toast.error("Erro ao carregar histórico de horas");
      setHistoryData(null);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Estado para saldo mensal do backend
  const [monthlyBalance, setMonthlyBalance] = useState(null);
  
  // Estado para saldo anual do backend
  const [yearlyBalance, setYearlyBalance] = useState(null);
  
  // Buscar saldo mensal/anual quando mudar período/mês/ano
  useEffect(() => {
    if (selectedEmployee) {
      if (periodType === "monthly" || periodType === "weekly") {
        fetchMonthlyBalance(selectedEmployee.id, selectedMonth, selectedYear);
      } else if (periodType === "yearly") {
        fetchYearlyBalance(selectedEmployee.id, selectedYear);
      }
    }
  }, [selectedEmployee, periodType, selectedMonth, selectedYear]);
  
  const fetchMonthlyBalance = async (employeeId, month, year) => {
    try {
      const response = await axios.get(`${API}/employees/${employeeId}/monthly-balance?month=${month}&year=${year}`);
      setMonthlyBalance(response.data);
    } catch (error) {
      console.error("Erro ao buscar saldo mensal:", error);
      setMonthlyBalance(null);
    }
  };
  
  const fetchYearlyBalance = async (employeeId, year) => {
    try {
      const response = await axios.get(`${API}/employees/${employeeId}/yearly-balance?year=${year}`);
      setYearlyBalance(response.data);
    } catch (error) {
      console.error("Erro ao buscar saldo anual:", error);
      setYearlyBalance(null);
    }
  };

  const filteredEmployees = employees.filter(emp => 
    emp.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calcular dados do período selecionado
  // PRIORIZA dados do novo endpoint monthly-balance para período mensal
  const getPeriodData = () => {
    // Para período MENSAL, usar dados do novo endpoint (fonte única de verdade)
    if (periodType === "monthly" && monthlyBalance) {
      return {
        worked: monthlyBalance.worked_hours,
        expected: monthlyBalance.expected_hours,
        absences: monthlyBalance.absences,
        balance: monthlyBalance.balance,
        adjustments: monthlyBalance.manual_adjustment || 0,
        label: `${months[selectedMonth - 1]} ${selectedYear}`,
        automatic_balance: monthlyBalance.automatic_balance,
        days_worked: monthlyBalance.days_worked,
        expected_days: monthlyBalance.expected_days,
        overtime: monthlyBalance.overtime,
        deficit: monthlyBalance.deficit
      };
    }
    
    // Para período ANUAL, usar novo endpoint yearly-balance (soma de todos os meses)
    if (periodType === "yearly" && yearlyBalance) {
      return {
        worked: yearlyBalance.total_worked_hours,
        expected: yearlyBalance.total_expected_hours,
        absences: yearlyBalance.total_absences,
        balance: yearlyBalance.total_balance,
        adjustments: yearlyBalance.total_adjustments || 0,
        label: `${selectedYear}`,
        overtime: yearlyBalance.overtime,
        deficit: yearlyBalance.deficit,
        monthlyBreakdown: yearlyBalance.monthly_breakdown
      };
    }
    
    if (!historyData || !historyData.history) return null;
    
    if (periodType === "yearly") {
      // Fallback para hours-history se yearly-balance não disponível
      const yearData = historyData.history.filter(h => h.year === selectedYear);
      return {
        worked: yearData.reduce((sum, h) => sum + h.worked_hours, 0),
        expected: yearData.reduce((sum, h) => sum + h.expected_hours, 0),
        absences: yearData.reduce((sum, h) => sum + h.absences, 0),
        balance: yearData.reduce((sum, h) => sum + h.month_balance, 0),
        adjustments: yearData.reduce((sum, h) => sum + h.adjustments, 0),
        label: `${selectedYear}`
      };
    } else if (periodType === "monthly") {
      // Fallback para dados do hours-history se monthly-balance não disponível
      const monthData = historyData.history.find(h => h.year === selectedYear && h.month === selectedMonth);
      if (!monthData) return null;
      return {
        worked: monthData.worked_hours,
        expected: monthData.expected_hours,
        absences: monthData.absences,
        balance: monthData.month_balance,
        adjustments: monthData.adjustments,
        label: `${months[selectedMonth - 1]} ${selectedYear}`
      };
    } else {
      // Semanal - mostra a semana atual do mês selecionado
      const monthData = historyData.history.find(h => h.year === selectedYear && h.month === selectedMonth);
      if (!monthData) return null;
      // Simplificado: divide por 4 semanas
      return {
        worked: Math.round(monthData.worked_hours / 4 * 10) / 10,
        expected: Math.round(monthData.expected_hours / 4 * 10) / 10,
        absences: Math.round(monthData.absences / 4),
        balance: Math.round(monthData.month_balance / 4 * 10) / 10,
        adjustments: Math.round(monthData.adjustments / 4 * 10) / 10,
        label: `Semana - ${months[selectedMonth - 1]} ${selectedYear}`
      };
    }
  };

  const periodData = getPeriodData();

  // Função para gerar PDF do histórico
  const generateHistoryPDF = () => {
    if (!historyData || !historyData.history || historyData.history.length === 0) {
      toast.error("Sem dados de histórico para gerar relatório");
      return;
    }
    
    const doc = new jsPDF();
    
    const primaryColor = [0, 120, 167];
    const positiveColor = [34, 197, 94];
    const negativeColor = [239, 68, 68];
    
    // Cabeçalho
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 210, 35, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("Relatório de Banco de Horas", 105, 15, { align: "center" });
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(selectedEmployee?.name || "", 105, 23, { align: "center" });
    doc.setFontSize(10);
    doc.text(`Emitido em: ${new Date().toLocaleDateString('pt-BR')}`, 105, 30, { align: "center" });
    
    let yPos = 45;
    
    // Resumo geral
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Resumo Geral", 14, yPos);
    yPos += 8;
    
    const cardWidth = 45;
    const cardHeight = 20;
    const cards = [
      { label: "Total Trabalhadas", value: `${historyData.total_worked_hours}h`, color: [59, 130, 246] },
      { label: "Total Esperadas", value: `${historyData.total_expected_hours}h`, color: [100, 116, 139] },
      { label: "Total Faltas", value: `${historyData.total_absences}`, color: [239, 68, 68] },
      { label: "Saldo Final", value: `${historyData.total_balance >= 0 ? '+' : ''}${historyData.total_balance}h`, 
        color: historyData.total_balance >= 0 ? positiveColor : negativeColor }
    ];
    
    cards.forEach((card, i) => {
      const x = 14 + (i * 48);
      doc.setFillColor(...card.color);
      doc.roundedRect(x, yPos, cardWidth, cardHeight, 2, 2, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.text(card.label, x + cardWidth/2, yPos + 7, { align: "center" });
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(card.value, x + cardWidth/2, yPos + 15, { align: "center" });
    });
    
    yPos += 30;
    
    // Gráfico de barras
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Evolução do Saldo Mensal", 14, yPos);
    yPos += 10;
    
    const chartWidth = 180;
    const chartHeight = 50;
    const chartX = 14;
    const chartY = yPos;
    
    doc.setFillColor(248, 250, 252);
    doc.rect(chartX, chartY, chartWidth, chartHeight, 'F');
    
    const zeroY = chartY + chartHeight / 2;
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.line(chartX, zeroY, chartX + chartWidth, zeroY);
    
    const barWidth = Math.min(15, (chartWidth - 20) / historyData.history.length);
    const maxBalance = Math.max(...historyData.history.map(h => Math.abs(h.accumulated_balance)), 10);
    const scale = (chartHeight / 2 - 5) / maxBalance;
    
    historyData.history.forEach((month, i) => {
      const x = chartX + 10 + (i * (barWidth + 2));
      const barHeight = Math.abs(month.accumulated_balance) * scale;
      
      if (month.accumulated_balance >= 0) {
        doc.setFillColor(...positiveColor);
        doc.rect(x, zeroY - barHeight, barWidth, barHeight, 'F');
      } else {
        doc.setFillColor(...negativeColor);
        doc.rect(x, zeroY, barWidth, barHeight, 'F');
      }
      
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(6);
      doc.setFont("helvetica", "normal");
      doc.text(month.month_name, x + barWidth/2, chartY + chartHeight + 4, { align: "center" });
    });
    
    yPos = chartY + chartHeight + 15;
    
    // Tabela de histórico
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Detalhamento Mensal", 14, yPos);
    yPos += 8;
    
    const headers = ["Mês/Ano", "Trabalhadas", "Esperadas", "Faltas", "Atestados", "Ajustes", "Saldo", "Acumulado"];
    const colWidths = [25, 22, 22, 18, 22, 20, 22, 25];
    
    doc.setFillColor(...primaryColor);
    doc.rect(14, yPos, 182, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    
    let colX = 14;
    headers.forEach((header, i) => {
      doc.text(header, colX + colWidths[i]/2, yPos + 5.5, { align: "center" });
      colX += colWidths[i];
    });
    
    yPos += 8;
    
    doc.setFont("helvetica", "normal");
    historyData.history.forEach((month, idx) => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
      
      if (idx % 2 === 0) {
        doc.setFillColor(248, 250, 252);
        doc.rect(14, yPos, 182, 7, 'F');
      }
      
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(7);
      
      const row = [
        month.period,
        `${month.worked_hours}h`,
        `${month.expected_hours}h`,
        month.absences.toString(),
        month.medical_leaves.toString(),
        month.adjustments !== 0 ? `${month.adjustments > 0 ? '+' : ''}${month.adjustments}h` : '-',
        `${month.month_balance >= 0 ? '+' : ''}${month.month_balance}h`,
        `${month.accumulated_balance >= 0 ? '+' : ''}${month.accumulated_balance}h`
      ];
      
      colX = 14;
      row.forEach((cell, i) => {
        if (i === 6) {
          doc.setTextColor(month.month_balance >= 0 ? 34 : 239, month.month_balance >= 0 ? 197 : 68, month.month_balance >= 0 ? 94 : 68);
        } else if (i === 7) {
          doc.setTextColor(month.accumulated_balance >= 0 ? 34 : 239, month.accumulated_balance >= 0 ? 197 : 68, month.accumulated_balance >= 0 ? 94 : 68);
          doc.setFont("helvetica", "bold");
        } else {
          doc.setTextColor(0, 0, 0);
          doc.setFont("helvetica", "normal");
        }
        doc.text(cell, colX + colWidths[i]/2, yPos + 5, { align: "center" });
        colX += colWidths[i];
      });
      
      yPos += 7;
    });
    
    yPos += 10;
    if (yPos > 270) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setDrawColor(200, 200, 200);
    doc.line(14, yPos, 196, yPos);
    yPos += 5;
    
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(8);
    doc.setFont("helvetica", "italic");
    doc.text(`Período: Desde ${historyData.ponto_liberado_at || 'N/A'} até ${new Date().toLocaleDateString('pt-BR')}`, 14, yPos);
    doc.text(`Total de ajustes manuais: ${historyData.total_adjustments > 0 ? '+' : ''}${historyData.total_adjustments}h`, 14, yPos + 5);
    
    doc.save(`Historico_Banco_Horas_${selectedEmployee?.name?.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success("Relatório de histórico exportado com sucesso!");
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading font-bold text-2xl text-slate-800">Histórico de Banco de Horas</h1>
        <p className="text-slate-600">Visualize o histórico completo de horas dos funcionários</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista de Funcionários */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-ciaponto-cyan" />
              Selecionar Funcionário
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Busca */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar funcionário..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-sky-500 focus:border-[#1CA1BA]"
              />
            </div>
            
            {/* Lista */}
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {loading ? (
                <div className="text-center py-8 text-slate-500">Carregando...</div>
              ) : filteredEmployees.length === 0 ? (
                <div className="text-center py-8 text-slate-500">Nenhum funcionário encontrado</div>
              ) : (
                filteredEmployees.map(emp => (
                  <button
                    key={emp.id}
                    onClick={() => fetchHistory(emp)}
                    className={`w-full p-3 rounded-lg border text-left transition-all ${
                      selectedEmployee?.id === emp.id
                        ? 'bg-sky-50 border-sky-300 ring-2 ring-sky-200'
                        : 'bg-white hover:bg-slate-50 border-slate-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                        emp.ponto_liberado ? 'bg-[#1CA1BA]' : 'bg-slate-400'
                      }`}>
                        {emp.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-800 truncate">{emp.name}</p>
                        <p className="text-xs text-slate-500 truncate">{emp.departamento || emp.cargo || 'Funcionário'}</p>
                      </div>
                      {!emp.ponto_liberado && (
                        <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                          Ponto não liberado
                        </span>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Detalhes do Histórico */}
        <Card className="lg:col-span-2">
          <CardContent className="p-0">
            {!selectedEmployee ? (
              <div className="flex flex-col items-center justify-center py-24 text-slate-400">
                <Users className="w-16 h-16 mb-4 opacity-50" />
                <p className="text-lg">Selecione um funcionário na lista</p>
                <p className="text-sm">para visualizar o histórico de horas</p>
              </div>
            ) : loadingHistory ? (
              <div className="flex items-center justify-center py-24">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1CA1BA]"></div>
              </div>
            ) : historyData?.message ? (
              <div className="p-6">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
                  <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                  <p className="font-medium text-amber-700">{historyData.message}</p>
                  <p className="text-sm text-amber-600 mt-2">
                    Para ver o histórico, é necessário que o funcionário tenha o ponto liberado e registros.
                  </p>
                </div>
              </div>
            ) : historyData?.history?.length > 0 ? (
              <div className="space-y-4 p-4">
                {/* Filtros de Período */}
                <div className="bg-white rounded-xl border p-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-600 mb-1 block">Período</label>
                      <select
                        value={periodType}
                        onChange={(e) => setPeriodType(e.target.value)}
                        className="w-full p-2 border rounded-lg text-sm"
                      >
                        <option value="monthly">Mensal</option>
                        <option value="weekly">Semanal</option>
                        <option value="yearly">Anual</option>
                      </select>
                    </div>
                    {periodType !== "yearly" && (
                      <div>
                        <label className="text-sm font-medium text-slate-600 mb-1 block">Mês</label>
                        <select
                          value={selectedMonth}
                          onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                          className="w-full p-2 border rounded-lg text-sm"
                        >
                          {months.map((m, i) => (
                            <option key={i} value={i + 1}>{m}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div>
                      <label className="text-sm font-medium text-slate-600 mb-1 block">Ano</label>
                      <select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                        className="w-full p-2 border rounded-lg text-sm"
                      >
                        {[2024, 2025, 2026].map(y => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Card Saldo Total Acumulado */}
                <div className={`rounded-xl p-4 border-2 ${
                  historyData.total_balance >= 0 
                    ? 'bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200' 
                    : 'bg-gradient-to-r from-orange-50 to-red-50 border-orange-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        historyData.total_balance >= 0 ? 'bg-emerald-100' : 'bg-orange-100'
                      }`}>
                        <TrendingUp className={`w-5 h-5 ${
                          historyData.total_balance >= 0 ? 'text-emerald-600' : 'text-orange-600'
                        }`} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-700">Saldo Total Acumulado</p>
                        <p className="text-xs text-slate-500">Desde a liberação do ponto</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-3xl font-bold ${
                        historyData.total_balance >= 0 ? 'text-emerald-600' : 'text-orange-600'
                      }`}>
                        {historyData.total_balance >= 0 ? '+' : ''}{historyData.total_balance}h
                      </p>
                      <p className="text-xs text-slate-500">
                        {historyData.total_balance >= 0 ? 'Horas extras acumuladas' : 'Horas pendentes'}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mt-4 pt-3 border-t border-slate-200/50">
                    <div className="text-center">
                      <p className="text-xs text-slate-500">Total Trabalhadas</p>
                      <p className="font-bold text-blue-600">{historyData.total_worked_hours}h</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-500">Total Esperadas</p>
                      <p className="font-bold text-slate-600">{historyData.total_expected_hours}h</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-500">Total Faltas</p>
                      <p className="font-bold text-red-600">{historyData.total_absences}</p>
                    </div>
                  </div>
                </div>

                {/* Card Saldo do Período */}
                {periodData && (
                  <div className="bg-white rounded-xl border p-6 text-center">
                    <p className="text-sm text-slate-500">Saldo de Horas - {periodData.label}</p>
                    <p className={`text-5xl font-bold mt-2 ${
                      periodData.balance >= 0 ? 'text-emerald-500' : 'text-red-500'
                    }`}>
                      {periodData.balance >= 0 ? '+' : ''}{formatHoursMinutes(periodData.balance)}
                    </p>
                    {periodData.balance < 0 && (
                      <p className="text-sm text-amber-600 mt-2 flex items-center justify-center gap-1">
                        <AlertTriangle className="w-4 h-4" />
                        Horas a compensar
                      </p>
                    )}
                  </div>
                )}

                {/* Cards de Resumo do Período */}
                {periodData && (
                  <div className="grid grid-cols-4 gap-3">
                    <Card className="border">
                      <CardContent className="p-4">
                        <p className="text-xs text-slate-500">Horas Trabalhadas</p>
                        <p className="text-2xl font-bold text-blue-600">{formatHoursMinutes(periodData.worked)}</p>
                      </CardContent>
                    </Card>
                    <Card className="border">
                      <CardContent className="p-4">
                        <p className="text-xs text-slate-500">Horas Esperadas</p>
                        <p className="text-2xl font-bold text-slate-600">{formatHoursMinutes(periodData.expected)}</p>
                      </CardContent>
                    </Card>
                    <Card className="border">
                      <CardContent className="p-4">
                        <p className="text-xs text-slate-500">Horas Extras</p>
                        <p className="text-2xl font-bold text-green-600">
                          +{formatHoursMinutes(Math.max(0, periodData.balance))}
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="border bg-red-50/50">
                      <CardContent className="p-4">
                        <p className="text-xs text-slate-500">Déficit</p>
                        <p className="text-2xl font-bold text-red-600">
                          {formatHoursMinutes(Math.min(0, periodData.balance), true)}
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Progresso do Período */}
                {periodData && (
                  <Card className="border">
                    <CardContent className="p-4">
                      <h4 className="font-semibold text-slate-700 mb-2">Progresso do Período</h4>
                      <p className="text-sm text-slate-500 mb-3">{periodData.label}</p>
                      <div className="mb-2">
                        <div className="flex justify-between text-xs text-slate-500 mb-1">
                          <span>Horas trabalhadas vs esperadas</span>
                          <span>{formatHoursMinutes(periodData.worked)} / {formatHoursMinutes(periodData.expected)} ({periodData.expected > 0 ? Math.round(periodData.worked / periodData.expected * 100) : 0}%)</span>
                        </div>
                        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full transition-all ${
                              periodData.worked / periodData.expected >= 1 ? 'bg-emerald-500' :
                              periodData.worked / periodData.expected >= 0.8 ? 'bg-blue-500' :
                              periodData.worked / periodData.expected >= 0.5 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(100, periodData.expected > 0 ? (periodData.worked / periodData.expected * 100) : 0)}%` }}
                          />
                        </div>
                      </div>
                      <div className="flex gap-4 text-xs mt-3">
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"></span> ≥100% (meta atingida)</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500"></span> 80-99% (bom)</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"></span> 50-79% (atenção)</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500"></span> &lt;50% (crítico)</span>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Tabela de histórico mensal */}
                <Card className="border">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Detalhamento Mensal</CardTitle>
                      <Button
                        size="sm"
                        onClick={generateHistoryPDF}
                        className="bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Baixar PDF
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto max-h-[300px]">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-100 sticky top-0">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium text-slate-600">Mês/Ano</th>
                            <th className="px-3 py-2 text-center font-medium text-slate-600">Trabalhadas</th>
                            <th className="px-3 py-2 text-center font-medium text-slate-600">Esperadas</th>
                            <th className="px-3 py-2 text-center font-medium text-slate-600">Faltas</th>
                            <th className="px-3 py-2 text-center font-medium text-slate-600">Ajustes</th>
                            <th className="px-3 py-2 text-center font-medium text-slate-600">Saldo Mês</th>
                            <th className="px-3 py-2 text-center font-medium text-slate-600">Acumulado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {historyData.history.map((month, idx) => (
                            <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                              <td className="px-3 py-2 font-medium text-slate-700">{month.period}</td>
                              <td className="px-3 py-2 text-center text-blue-600">{month.worked_hours}h</td>
                              <td className="px-3 py-2 text-center text-slate-600">{month.expected_hours}h</td>
                              <td className="px-3 py-2 text-center">
                                <span className={month.absences > 0 ? 'text-red-600 font-medium' : 'text-green-600'}>
                                  {month.absences}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-center">
                                {month.adjustments !== 0 ? (
                                  <span className={month.adjustments > 0 ? 'text-green-600' : 'text-red-600'}>
                                    {month.adjustments > 0 ? '+' : ''}{month.adjustments}h
                                  </span>
                                ) : '-'}
                              </td>
                              <td className={`px-3 py-2 text-center font-medium ${
                                month.month_balance >= 0 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {month.month_balance >= 0 ? '+' : ''}{month.month_balance}h
                              </td>
                              <td className={`px-3 py-2 text-center font-bold ${
                                month.accumulated_balance >= 0 ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'
                              }`}>
                                {month.accumulated_balance >= 0 ? '+' : ''}{month.accumulated_balance}h
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="text-center py-24 text-slate-400">
                <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>Nenhum histórico disponível</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ==================== HOLIDAYS PAGE - GERENCIAMENTO DE FERIADOS ====================
function HolidaysPage() {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [stats, setStats] = useState({ total: 0, ativos: 0 });
  const [customDialogOpen, setCustomDialogOpen] = useState(false);
  const [customFormData, setCustomFormData] = useState({
    data: "",
    nome: "",
    observacao: ""
  });
  const [savingHoliday, setSavingHoliday] = useState(null);

  useEffect(() => {
    fetchHolidays();
  }, [selectedYear]);

  const fetchHolidays = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/holidays/company?year=${selectedYear}`);
      setHolidays(response.data.feriados || []);
      setStats({
        total: response.data.total || 0,
        ativos: response.data.ativos || 0
      });
    } catch (error) {
      console.error("Erro ao carregar feriados:", error);
      toast.error("Erro ao carregar feriados");
    } finally {
      setLoading(false);
    }
  };

  const toggleHoliday = async (holiday) => {
    setSavingHoliday(holiday.data);
    try {
      const response = await axios.post(`${API}/holidays/company/toggle?data=${holiday.data}&ativo=${!holiday.ativo}`);
      const data = response.data;
      
      if (!holiday.ativo && data.funcionarios_beneficiados > 0) {
        // Quando ativou e há funcionários beneficiados
        toast.success(
          `${data.nome} ativado! ${data.funcionarios_beneficiados} funcionário(s) receberão ${data.horas_creditadas}h creditadas automaticamente.`,
          { duration: 5000 }
        );
      } else {
        toast.success(`Feriado ${!holiday.ativo ? "ativado" : "desativado"} com sucesso!`);
      }
      fetchHolidays();
    } catch (error) {
      console.error("Erro ao atualizar feriado:", error);
      toast.error("Erro ao atualizar feriado");
    } finally {
      setSavingHoliday(null);
    }
  };

  const activateAll = async () => {
    const inactiveHolidays = holidays.filter(h => !h.ativo);
    if (inactiveHolidays.length === 0) {
      toast.info("Todos os feriados já estão ativos");
      return;
    }
    
    try {
      let totalFuncionarios = 0;
      let totalHoras = 0;
      
      for (const holiday of inactiveHolidays) {
        const response = await axios.post(`${API}/holidays/company/toggle?data=${holiday.data}&ativo=true`);
        if (response.data.funcionarios_beneficiados) {
          totalFuncionarios += response.data.funcionarios_beneficiados;
          totalHoras += response.data.horas_creditadas;
        }
      }
      
      if (totalHoras > 0) {
        toast.success(
          `${inactiveHolidays.length} feriados ativados! Total de ${totalHoras}h creditadas para funcionários.`,
          { duration: 5000 }
        );
      } else {
        toast.success(`${inactiveHolidays.length} feriados ativados!`);
      }
      fetchHolidays();
    } catch (error) {
      toast.error("Erro ao ativar feriados");
    }
  };

  const deactivateAll = async () => {
    const activeHolidays = holidays.filter(h => h.ativo);
    if (activeHolidays.length === 0) {
      toast.info("Nenhum feriado está ativo");
      return;
    }
    
    try {
      for (const holiday of activeHolidays) {
        await axios.post(`${API}/holidays/company/toggle?data=${holiday.data}&ativo=false`);
      }
      toast.success(`${activeHolidays.length} feriados desativados!`);
      fetchHolidays();
    } catch (error) {
      toast.error("Erro ao desativar feriados");
    }
  };

  const handleCreateCustomHoliday = async (e) => {
    e.preventDefault();
    if (!customFormData.data || !customFormData.nome) {
      toast.error("Preencha a data e o nome do feriado");
      return;
    }

    try {
      await axios.post(
        `${API}/holidays/company/custom?data=${customFormData.data}&nome=${encodeURIComponent(customFormData.nome)}&observacao=${encodeURIComponent(customFormData.observacao || "")}`
      );
      toast.success("Feriado personalizado criado com sucesso!");
      setCustomDialogOpen(false);
      setCustomFormData({ data: "", nome: "", observacao: "" });
      fetchHolidays();
    } catch (error) {
      console.error("Erro ao criar feriado:", error);
      toast.error(error.response?.data?.detail || "Erro ao criar feriado");
    }
  };

  const deleteCustomHoliday = async (data) => {
    if (!window.confirm("Tem certeza que deseja excluir este feriado?")) return;
    
    try {
      await axios.delete(`${API}/holidays/company/${data}`);
      toast.success("Feriado excluído com sucesso!");
      fetchHolidays();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao excluir feriado");
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + "T12:00:00");
    return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "long" });
  };

  const getDayOfWeek = (dateStr) => {
    const date = new Date(dateStr + "T12:00:00");
    const days = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
    return days[date.getDay()];
  };

  const getMonthName = (month) => {
    const months = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", 
                    "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
    return months[month - 1];
  };

  // Agrupar feriados por mês
  const holidaysByMonth = holidays.reduce((acc, holiday) => {
    const month = parseInt(holiday.data.split("-")[1]);
    if (!acc[month]) acc[month] = [];
    acc[month].push(holiday);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="holidays-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">Feriados</h1>
          <p className="text-slate-600">Gerencie os feriados da sua empresa</p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-3 py-2 border rounded-lg text-sm bg-white"
          >
            {[2024, 2025, 2026, 2027].map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          
          <Dialog open={customDialogOpen} onOpenChange={setCustomDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark">
                <Plus className="w-4 h-4 mr-2" />
                Feriado Personalizado
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Criar Feriado Personalizado</DialogTitle>
                <DialogDescription>
                  Adicione feriados municipais, pontos facultativos ou datas especiais.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateCustomHoliday} className="space-y-4">
                <div className="space-y-2">
                  <Label>Data *</Label>
                  <Input
                    type="date"
                    value={customFormData.data}
                    onChange={(e) => setCustomFormData({ ...customFormData, data: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nome do Feriado *</Label>
                  <Input
                    value={customFormData.nome}
                    onChange={(e) => setCustomFormData({ ...customFormData, nome: e.target.value })}
                    placeholder="Ex: Aniversário da Cidade"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Observação (opcional)</Label>
                  <Input
                    value={customFormData.observacao}
                    onChange={(e) => setCustomFormData({ ...customFormData, observacao: e.target.value })}
                    placeholder="Ex: Ponto Facultativo"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setCustomDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark">
                    Criar Feriado
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-100 rounded-lg">
                <Calendar className="w-5 h-5 text-ciaponto-cyan" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Total</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Ativos</p>
                <p className="text-2xl font-bold text-green-600">{stats.ativos}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <XCircle className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-slate-600">Inativos</p>
                <p className="text-2xl font-bold text-slate-600">{stats.total - stats.ativos}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={activateAll} className="flex-1 text-xs">
                Ativar Todos
              </Button>
              <Button size="sm" variant="outline" onClick={deactivateAll} className="flex-1 text-xs">
                Desativar
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Card */}
      <Card className="bg-sky-50 border-sky-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-ciaponto-cyan flex-shrink-0 mt-0.5" />
            <div className="text-sm text-ciaponto-navy">
              <p className="font-medium mb-1">Como funciona?</p>
              <p>Quando um feriado está <strong>ativo</strong>, os funcionários que trabalhariam nesse dia receberão as horas automaticamente creditadas, sem necessidade de bater ponto. Isso facilita a gestão da folha de pagamento e garante que ninguém tenha horas negativas em dias de feriado.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1CA1BA]"></div>
        </div>
      ) : (
        /* Lista de Feriados por Mês */
        <div className="space-y-6">
          {Object.keys(holidaysByMonth).sort((a, b) => parseInt(a) - parseInt(b)).map(month => (
            <Card key={month}>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold text-slate-800">
                  {getMonthName(parseInt(month))}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {holidaysByMonth[month].map(holiday => (
                    <div 
                      key={holiday.data}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                        holiday.ativo 
                          ? "bg-green-50 border-green-200" 
                          : "bg-slate-50 border-slate-200"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          holiday.ativo ? "bg-green-100" : "bg-slate-200"
                        }`}>
                          <span className="text-sm font-bold text-slate-700">
                            {holiday.dia}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{holiday.nome}</p>
                          <p className="text-xs text-slate-500">
                            {getDayOfWeek(holiday.data)} • {holiday.tipo === "personalizado" ? "Personalizado" : "Nacional"}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {holiday.ativo && (
                          <span className="hidden sm:inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Ativo
                          </span>
                        )}
                        
                        <Button
                          size="sm"
                          variant={holiday.ativo ? "outline" : "default"}
                          onClick={() => toggleHoliday(holiday)}
                          disabled={savingHoliday === holiday.data}
                          className={!holiday.ativo ? "bg-green-500 hover:bg-green-600" : ""}
                        >
                          {savingHoliday === holiday.data ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                          ) : holiday.ativo ? (
                            "Desativar"
                          ) : (
                            "Ativar"
                          )}
                        </Button>
                        
                        {holiday.tipo === "personalizado" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteCustomHoliday(holiday.data)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
          
          {Object.keys(holidaysByMonth).length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600">Nenhum feriado encontrado para {selectedYear}</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

// Número do suporte (mesmo da landing page)
const SUPPORT_WHATSAPP = "5588988676142";

// ==================== NOTAS FISCAIS PAGE (APENAS SUPERADMIN) ====================
function NotasFiscaisPage() {
  const [paidInvoices, setPaidInvoices] = useState([]);
  const [nfseList, setNfseList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState("pending"); // pending, generated
  const { user } = useAuth();

  useEffect(() => {
    if (user?.role === "superadmin") {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [invoicesRes, nfseRes] = await Promise.all([
        axios.get(`${API}/nfse/invoices-paid`),
        axios.get(`${API}/nfse`)
      ]);
      setPaidInvoices(invoicesRes.data);
      setNfseList(nfseRes.data);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      toast.error("Erro ao carregar dados de notas fiscais");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateNFSe = async (invoiceId) => {
    try {
      setGenerating(true);
      const response = await axios.post(`${API}/nfse/generate`, { invoice_id: invoiceId });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      console.error("Erro ao gerar NFS-e:", error);
      toast.error(error.response?.data?.detail || "Erro ao gerar nota fiscal");
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadPDF = async (nfseId) => {
    try {
      const response = await axios.get(`${API}/nfse/${nfseId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `NFSe_${nfseId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF baixado com sucesso!");
    } catch (error) {
      toast.error("Erro ao baixar PDF");
    }
  };

  const handleDownloadXML = async (nfseId) => {
    try {
      const response = await axios.get(`${API}/nfse/${nfseId}/xml`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `NFSe_${nfseId}.xml`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("XML baixado com sucesso!");
    } catch (error) {
      toast.error("Erro ao baixar XML");
    }
  };

  const handleCancelNFSe = async (nfseId) => {
    if (!window.confirm("Tem certeza que deseja cancelar esta nota fiscal?")) return;
    
    try {
      await axios.delete(`${API}/nfse/${nfseId}`);
      toast.success("Nota fiscal cancelada");
      fetchData();
    } catch (error) {
      toast.error("Erro ao cancelar nota fiscal");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("pt-BR");
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL"
    }).format(value || 0);
  };

  // Faturas pagas sem nota gerada
  const pendingInvoices = paidInvoices.filter(inv => !inv.has_nfse);

  if (user?.role !== "superadmin") {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 mx-auto text-red-500 mb-4" />
          <p className="text-slate-600">Acesso restrito ao administrador master.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1CA1BA]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="nfse-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">Notas Fiscais de Serviço</h1>
          <p className="text-slate-500 text-sm mt-1">Gerencie as NFS-e das faturas pagas</p>
        </div>
      </div>

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-amber-600">Pendentes</p>
                <p className="text-2xl font-bold text-amber-700">{pendingInvoices.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500 rounded-lg">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-green-600">Emitidas</p>
                <p className="text-2xl font-bold text-green-700">{nfseList.filter(n => n.status === "emitida").length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500 rounded-lg">
                <XCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-red-600">Canceladas</p>
                <p className="text-2xl font-bold text-red-700">{nfseList.filter(n => n.status === "cancelada").length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500 rounded-lg">
                <Receipt className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-blue-600">Total Emitido</p>
                <p className="text-2xl font-bold text-blue-700">
                  {formatCurrency(nfseList.filter(n => n.status === "emitida").reduce((sum, n) => sum + n.valor_servico, 0))}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab("pending")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "pending"
              ? "border-[#1CA1BA] text-ciaponto-cyan"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Faturas Pagas ({pendingInvoices.length} pendentes)
        </button>
        <button
          onClick={() => setActiveTab("generated")}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "generated"
              ? "border-[#1CA1BA] text-ciaponto-cyan"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          Notas Emitidas ({nfseList.length})
        </button>
      </div>

      {/* Tab: Faturas Pagas Pendentes */}
      {activeTab === "pending" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Faturas Pagas - Aguardando Emissão de NFS-e</CardTitle>
            <CardDescription>Clique em "Gerar NFS-e" para emitir a nota fiscal</CardDescription>
          </CardHeader>
          <CardContent>
            {pendingInvoices.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-400" />
                <p>Todas as faturas pagas já têm nota fiscal gerada!</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Fatura</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Empresa</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">CNPJ</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Valor</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Pago em</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Ação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingInvoices.map((invoice) => (
                      <tr key={invoice.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4 text-sm font-medium">#{invoice.invoice_number}</td>
                        <td className="py-3 px-4 text-sm">{invoice.company_name}</td>
                        <td className="py-3 px-4 text-sm text-slate-500">{invoice.company_cnpj}</td>
                        <td className="py-3 px-4 text-sm font-medium text-green-600">{formatCurrency(invoice.amount)}</td>
                        <td className="py-3 px-4 text-sm text-slate-500">{formatDate(invoice.paid_at)}</td>
                        <td className="py-3 px-4 text-right">
                          <Button
                            size="sm"
                            onClick={() => handleGenerateNFSe(invoice.id)}
                            disabled={generating}
                            className="bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark"
                          >
                            {generating ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                            ) : (
                              <>
                                <Receipt className="w-4 h-4 mr-1" />
                                Gerar NFS-e
                              </>
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab: Notas Emitidas */}
      {activeTab === "generated" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Notas Fiscais Emitidas</CardTitle>
            <CardDescription>Baixe o PDF e XML para enviar ao contador</CardDescription>
          </CardHeader>
          <CardContent>
            {nfseList.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Receipt className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                <p>Nenhuma nota fiscal emitida ainda</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Nº NFS-e</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Tomador</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">CNPJ</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Valor</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">ISS</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Emissão</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nfseList.map((nfse) => (
                      <tr key={nfse.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4 text-sm font-bold text-ciaponto-cyan">#{nfse.numero_nfse}</td>
                        <td className="py-3 px-4 text-sm">{nfse.tomador_razao_social}</td>
                        <td className="py-3 px-4 text-sm text-slate-500">{nfse.tomador_cnpj}</td>
                        <td className="py-3 px-4 text-sm font-medium">{formatCurrency(nfse.valor_servico)}</td>
                        <td className="py-3 px-4 text-sm text-slate-500">{formatCurrency(nfse.valor_iss)}</td>
                        <td className="py-3 px-4 text-sm text-slate-500">{formatDate(nfse.data_emissao)}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            nfse.status === "emitida" 
                              ? "bg-green-100 text-green-700" 
                              : "bg-red-100 text-red-700"
                          }`}>
                            {nfse.status === "emitida" ? "Emitida" : "Cancelada"}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadPDF(nfse.id)}
                              title="Baixar PDF"
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadXML(nfse.id)}
                              title="Baixar XML"
                              className="text-orange-600"
                            >
                              <Code className="w-4 h-4" />
                            </Button>
                            {nfse.status === "emitida" && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCancelNFSe(nfse.id)}
                                title="Cancelar NFS-e"
                                className="text-red-600 hover:bg-red-50"
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Informações do Prestador */}
      <Card className="bg-slate-50">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-slate-600">Dados do Prestador (Emissor)</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-slate-600">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="font-medium">Razão Social</p>
              <p>CIAPAY SISTEM LTDA</p>
            </div>
            <div>
              <p className="font-medium">CNPJ</p>
              <p>64.199.817/0001-13</p>
            </div>
            <div>
              <p className="font-medium">Inscrição Municipal</p>
              <p>100113</p>
            </div>
            <div>
              <p className="font-medium">Alíquota ISS</p>
              <p>2%</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== COMPONENTE DE CONFIGURAÇÕES ====================
function ConfiguracoesPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [systemStartDate, setSystemStartDate] = useState("");
  const [faceVerificationEnabled, setFaceVerificationEnabled] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/company/settings`);
      setSettings(response.data);
      setSystemStartDate(response.data.system_start_date || "");
      setFaceVerificationEnabled(response.data.face_verification_enabled || false);
    } catch (error) {
      console.error("Erro ao carregar configurações:", error);
      toast.error("Erro ao carregar configurações");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStartDate = async () => {
    if (!systemStartDate) {
      toast.error("Selecione uma data de início");
      return;
    }

    try {
      setSaving(true);
      await axios.post(`${API}/company/system-start-date`, {
        start_date: systemStartDate
      });
      toast.success("Data de início do sistema salva com sucesso!");
      fetchSettings();
    } catch (error) {
      console.error("Erro ao salvar:", error);
      toast.error(error.response?.data?.detail || "Erro ao salvar configuração");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveStartDate = async () => {
    try {
      setSaving(true);
      await axios.delete(`${API}/company/system-start-date`);
      setSystemStartDate("");
      toast.success("Data de início removida. O sistema agora considera todos os meses.");
      fetchSettings();
    } catch (error) {
      console.error("Erro ao remover:", error);
      toast.error("Erro ao remover configuração");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleFaceVerification = async () => {
    try {
      const newValue = !faceVerificationEnabled;
      await axios.put(`${API}/company/settings`, { face_verification_enabled: newValue });
      setFaceVerificationEnabled(newValue);
      toast.success(newValue ? "Verificação facial ativada" : "Verificação facial desativada");
    } catch (error) {
      console.error("Erro ao alterar verificação facial:", error);
      toast.error("Erro ao alterar configuração");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">Configurações</h1>
          <p className="text-slate-600">Configure as opções do sistema de ponto</p>
        </div>
      </div>

      {/* Card: Data de Início do Sistema */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-500" />
            Data de Início do Sistema
          </CardTitle>
          <CardDescription>
            Configure a partir de qual data o sistema deve considerar os registros de ponto.
            <br />
            <strong className="text-orange-600">Meses anteriores a esta data serão ignorados nos cálculos de horas.</strong>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Data de Início
              </label>
              <input
                type="date"
                value={systemStartDate}
                onChange={(e) => setSystemStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex gap-2 pt-6">
              <Button
                onClick={handleSaveStartDate}
                disabled={saving || !systemStartDate}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Salvar
              </Button>
              {settings?.system_start_date && (
                <Button
                  onClick={handleRemoveStartDate}
                  disabled={saving}
                  variant="outline"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  <X className="w-4 h-4 mr-2" />
                  Remover
                </Button>
              )}
            </div>
          </div>
          
          {settings?.system_start_date && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-blue-800">
                  Sistema configurado para iniciar em: {new Date(settings.system_start_date + 'T00:00:00').toLocaleDateString('pt-BR')}
                </span>
              </div>
              <p className="text-sm text-blue-700 mt-1">
                Os relatórios e cálculos de horas ignorarão dados anteriores a esta data.
              </p>
            </div>
          )}
          
          {!settings?.system_start_date && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                <span className="font-medium text-yellow-800">
                  Nenhuma data de início configurada
                </span>
              </div>
              <p className="text-sm text-yellow-700 mt-1">
                O sistema está considerando todos os meses desde a criação da empresa.
                Se sua empresa começou a usar o sistema recentemente, configure a data de início para evitar horas pendentes de meses anteriores.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Card: Verificação Facial */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-purple-500" />
            Verificação Facial (AWS Rekognition)
          </CardTitle>
          <CardDescription>
            Ative para exigir que os funcionários confirmem a identidade ao registrar o ponto usando reconhecimento facial.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-slate-700">Verificação Facial no Ponto</p>
              <p className="text-sm text-slate-500">
                A foto tirada será comparada com a foto de cadastro usando IA (~$0.001/verificação)
              </p>
            </div>
            <button
              onClick={handleToggleFaceVerification}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                faceVerificationEnabled ? 'bg-blue-600' : 'bg-slate-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  faceVerificationEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Card: Documentos e Termos de Uso */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-emerald-500" />
            Documentos e Termos de Uso
          </CardTitle>
          <CardDescription>
            Baixe os termos de uso da plataforma para empresa e funcionários.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border rounded-lg p-4 hover:bg-slate-50 transition-colors">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Building className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-slate-900">Termo de Uso - Empresa</h4>
                  <p className="text-sm text-slate-500">Contrato de utilização da plataforma</p>
                </div>
              </div>
              <Button 
                variant="outline" 
                className="w-full border-blue-300 text-blue-700 hover:bg-blue-50"
                onClick={() => {
                  const token = localStorage.getItem("token");
                  window.open(`${API}/termos/empresa/pdf?token=${token}`, '_blank');
                }}
                data-testid="download-termo-empresa-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Abrir Termo
              </Button>
            </div>

            <div className="border rounded-lg p-4 hover:bg-slate-50 transition-colors">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Users className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h4 className="font-medium text-slate-900">Termo de Uso - Funcionário</h4>
                  <p className="text-sm text-slate-500">Termo para assinatura dos colaboradores</p>
                </div>
              </div>
              <Button 
                variant="outline" 
                className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                onClick={() => {
                  const token = localStorage.getItem("token");
                  window.open(`${API}/termos/funcionario/pdf?token=${token}`, '_blank');
                }}
                data-testid="download-termo-funcionario-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Abrir Termo
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
function FinanceiroPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentModal, setPaymentModal] = useState(false);
  const [generatingPayment, setGeneratingPayment] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [savingCompany, setSavingCompany] = useState(false);
  const [downloadingContract, setDownloadingContract] = useState(false);
  const [contractData, setContractData] = useState(null);
  const [companyForm, setCompanyForm] = useState({
    cnpj: "",
    cep: "",
    address: "",
    number: "",
    bairro: "",
    city: "",
    state: "",
    phone: ""
  });

  useEffect(() => {
    fetchInvoices();
    fetchCompanyData();
    fetchContractData();
  }, []);

  const fetchContractData = async () => {
    try {
      const response = await axios.get(`${API}/contract/view`);
      setContractData(response.data);
    } catch (error) {
      console.log("Contrato não encontrado ou não aceito ainda");
    }
  };

  const handleDownloadContract = async () => {
    setDownloadingContract(true);
    try {
      const response = await axios.get(`${API}/contract/pdf`, {
        responseType: 'blob'
      });
      
      // Criar link para download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Contrato_CiaPonto_${companyData?.trade_name || companyData?.name || 'empresa'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Contrato baixado com sucesso!");
    } catch (error) {
      console.error("Erro ao baixar contrato:", error);
      toast.error(error.response?.data?.detail || "Erro ao baixar contrato. Verifique se você já aceitou o contrato.");
    } finally {
      setDownloadingContract(false);
    }
  };

  const fetchCompanyData = async () => {
    try {
      const response = await axios.get(`${API}/company/me`);
      setCompanyData(response.data);
      setCompanyForm({
        cnpj: response.data.cnpj || "",
        cep: response.data.cep || "",
        address: response.data.address || "",
        number: response.data.number || "",
        bairro: response.data.bairro || "",
        city: response.data.city || "",
        state: response.data.state || "",
        phone: response.data.phone || ""
      });
    } catch (error) {
      console.error("Error fetching company data:", error);
    }
  };

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices/my`);
      setInvoices(response.data.invoices);
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCompanyData = async () => {
    if (!companyForm.cnpj || !companyForm.cep || !companyForm.address || !companyForm.city || !companyForm.state) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }
    
    setSavingCompany(true);
    try {
      await axios.put(`${API}/company/update-billing`, companyForm);
      toast.success("Dados salvos com sucesso!");
      setShowCompanyForm(false);
      fetchCompanyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar dados");
    } finally {
      setSavingCompany(false);
    }
  };

  const handleGeneratePayment = async (invoice) => {
    // Verificar se empresa tem dados completos
    if (!companyData?.cnpj || !companyData?.address || !companyData?.city) {
      toast.error("Complete os dados da empresa antes de gerar boleto");
      setShowCompanyForm(true);
      return;
    }
    
    setSelectedInvoice(invoice);
    setGeneratingPayment(true);
    try {
      const response = await axios.post(`${API}/invoices/${invoice.id}/generate-payment`);
      setPaymentData(response.data);
      setPaymentModal(true);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Erro ao gerar pagamento";
      if (errorMsg.includes("CNPJ") || errorMsg.includes("endereço")) {
        setShowCompanyForm(true);
      }
      toast.error(errorMsg);
    } finally {
      setGeneratingPayment(false);
    }
  };

  const handleCheckStatus = async (invoiceId) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoiceId}/check-status`);
      if (response.data.status === "paid") {
        toast.success("Pagamento confirmado!");
        fetchInvoices();
        setPaymentModal(false);
      } else {
        toast.info("Pagamento ainda não confirmado");
      }
    } catch (error) {
      toast.error("Erro ao verificar pagamento");
    }
  };

  const getStatusBadge = (status, dueDate) => {
    const today = new Date().toISOString().split("T")[0];
    if (status === "paid") return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">Pago</span>;
    if (status === "overdue" || (status === "pending" && dueDate < today)) return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">Vencida</span>;
    return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">Pendente</span>;
  };

  // Filtrar faturas - excluir canceladas
  const activeInvoices = invoices.filter(i => i.status !== "cancelled" && i.status !== "canceled");
  const paidInvoices = activeInvoices.filter(i => i.status === "paid");
  const pendingInvoices = activeInvoices.filter(i => i.status === "pending" || i.status === "overdue");
  const totalPaid = paidInvoices.reduce((sum, i) => sum + i.amount, 0);
  const totalPending = pendingInvoices.reduce((sum, i) => sum + i.amount, 0);

  // Gerar PDF do carnê completo
  const generateCarnePDF = () => {
    const doc = new jsPDF();
    
    // Cabeçalho
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("CARNÊ DE PAGAMENTO", 105, 20, { align: "center" });
    
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(`Gerado em: ${new Date().toLocaleDateString("pt-BR")}`, 14, 35);
    
    const totalValue = activeInvoices.reduce((sum, inv) => sum + inv.amount, 0);
    doc.text(`Total do Carnê: R$ ${totalValue.toFixed(2)}`, 14, 42);
    doc.text(`Parcelas: ${activeInvoices.length}x`, 14, 49);
    doc.text(`Pagas: ${paidInvoices.length} | Pendentes: ${pendingInvoices.length}`, 14, 56);
    
    // Tabela de parcelas
    autoTable(doc, {
      startY: 65,
      head: [["Parcela", "Vencimento", "Valor", "Status"]],
      body: activeInvoices.map(inv => [
        `${inv.invoice_number}/${activeInvoices.length}`,
        new Date(inv.due_date).toLocaleDateString("pt-BR"),
        `R$ ${inv.amount.toFixed(2)}`,
        inv.status === "paid" ? "Pago" : inv.status === "overdue" ? "Em Atraso" : "Pendente"
      ]),
      theme: "grid",
      headStyles: { fillColor: [14, 165, 233] },
      styles: { fontSize: 10 }
    });
    
    // Rodapé
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(
        `CiaPonto - Sistema de Ponto Eletrônico | Página ${i} de ${pageCount}`,
        105,
        doc.internal.pageSize.height - 10,
        { align: "center" }
      );
    }
    
    doc.save(`meu_carne_${new Date().toISOString().split("T")[0]}.pdf`);
    toast.success("Carnê baixado com sucesso!");
  };

  // Gerar PDF de uma fatura individual
  const generateBoletoPDF = (invoice) => {
    const doc = new jsPDF();
    
    // Cabeçalho
    doc.setFillColor(14, 165, 233);
    doc.rect(0, 0, 210, 40, "F");
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.text("FATURA", 105, 25, { align: "center" });
    
    // Dados da fatura
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    
    let y = 55;
    doc.text(`Parcela: ${invoice.invoice_number}/${activeInvoices.length}`, 14, y);
    y += 10;
    doc.text(`Vencimento: ${new Date(invoice.due_date).toLocaleDateString("pt-BR")}`, 14, y);
    y += 15;
    
    // Valor em destaque
    doc.setFillColor(240, 253, 244);
    doc.rect(14, y - 5, 180, 25, "F");
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(`Valor: R$ ${invoice.amount.toFixed(2)}`, 20, y + 10);
    y += 35;
    
    // Status
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    const statusText = invoice.status === "paid" ? "PAGO" : "PENDENTE";
    const statusColor = invoice.status === "paid" ? [34, 197, 94] : [234, 179, 8];
    doc.setFillColor(...statusColor);
    doc.rect(14, y - 5, 50, 12, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.text(statusText, 20, y + 3);
    doc.setTextColor(0, 0, 0);
    y += 20;
    
    // Forma de pagamento
    doc.setFont("helvetica", "normal");
    doc.text(`Forma de Pagamento: BOLETO BANCÁRIO`, 14, y);
    y += 20;
    
    // Instruções
    doc.setFillColor(254, 249, 195);
    doc.rect(14, y - 5, 180, 35, "F");
    doc.setFontSize(10);
    doc.text("Instruções:", 20, y + 3);
    doc.text("• Pague esta fatura até a data de vencimento", 20, y + 12);
    doc.text("• Após o pagamento, aguarde a confirmação automática", 20, y + 20);
    doc.text("• Em caso de dúvidas, entre em contato com o suporte", 20, y + 28);
    
    // Rodapé
    doc.setFontSize(8);
    doc.text(
      "CiaPonto - Sistema de Ponto Eletrônico",
      105,
      doc.internal.pageSize.height - 10,
      { align: "center" }
    );
    
    doc.save(`fatura_parcela${invoice.invoice_number}.pdf`);
    toast.success("Fatura baixada com sucesso!");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900">Financeiro</h1>
          <p className="text-slate-600">Gerencie suas faturas e pagamentos</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowCompanyForm(true)}>
            <Building2 className="w-4 h-4 mr-2" />
            Dados p/ Boleto
          </Button>
          {activeInvoices.length > 0 && (
            <>
              <Button variant="outline" onClick={generateCarnePDF}>
                <Download className="w-4 h-4 mr-2" />
                Baixar Carnê
              </Button>
              <Button variant="outline" onClick={() => { generateCarnePDF(); setTimeout(() => window.print(), 1000); }}>
                <Printer className="w-4 h-4 mr-2" />
                Imprimir
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Alerta se empresa não tem dados completos */}
      {companyData && (!companyData.cnpj || !companyData.address) && (
        <Card className="border-yellow-300 bg-yellow-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-yellow-600" />
              <div>
                <p className="font-medium text-yellow-800">Dados incompletos para gerar boleto</p>
                <p className="text-sm text-yellow-600">Preencha o CNPJ e endereço da empresa para gerar boletos.</p>
              </div>
              <Button size="sm" variant="outline" className="ml-auto" onClick={() => setShowCompanyForm(true)}>
                Preencher dados
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal de dados da empresa para boleto */}
      <Dialog open={showCompanyForm} onOpenChange={setShowCompanyForm}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Dados para Boleto</DialogTitle>
            <DialogDescription>
              Preencha os dados da empresa para gerar boletos
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>CNPJ da Empresa *</Label>
              <Input
                value={companyForm.cnpj}
                onChange={(e) => setCompanyForm({...companyForm, cnpj: e.target.value})}
                placeholder="00.000.000/0001-00"
              />
            </div>
            <div className="space-y-2">
              <Label>Telefone *</Label>
              <Input
                value={companyForm.phone}
                onChange={(e) => setCompanyForm({...companyForm, phone: e.target.value})}
                placeholder="(00) 00000-0000"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>CEP *</Label>
                <Input
                  value={companyForm.cep}
                  onChange={(e) => setCompanyForm({...companyForm, cep: e.target.value})}
                  placeholder="00000-000"
                />
              </div>
              <div className="space-y-2">
                <Label>Estado *</Label>
                <Input
                  value={companyForm.state}
                  onChange={(e) => setCompanyForm({...companyForm, state: e.target.value})}
                  placeholder="SP"
                  maxLength={2}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Cidade *</Label>
              <Input
                value={companyForm.city}
                onChange={(e) => setCompanyForm({...companyForm, city: e.target.value})}
                placeholder="São Paulo"
              />
            </div>
            <div className="space-y-2">
              <Label>Bairro *</Label>
              <Input
                value={companyForm.bairro}
                onChange={(e) => setCompanyForm({...companyForm, bairro: e.target.value})}
                placeholder="Centro"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Endereço (Rua) *</Label>
                <Input
                  value={companyForm.address}
                  onChange={(e) => setCompanyForm({...companyForm, address: e.target.value})}
                  placeholder="Rua das Flores"
                />
              </div>
              <div className="space-y-2">
                <Label>Número *</Label>
                <Input
                  value={companyForm.number}
                  onChange={(e) => setCompanyForm({...companyForm, number: e.target.value})}
                  placeholder="123"
                />
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => setShowCompanyForm(false)}>
              Cancelar
            </Button>
            <Button className="flex-1" onClick={handleSaveCompanyData} disabled={savingCompany}>
              {savingCompany ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cards de resumo */}
      <div className="grid sm:grid-cols-3 gap-4">
        <Card className="border border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Total de Faturas</p>
                <p className="font-heading font-bold text-3xl text-slate-900">{activeInvoices.length}</p>
              </div>
              <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                <Receipt className="w-6 h-6 text-ciaponto-cyan" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-green-200 bg-green-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Valor Pago</p>
                <p className="font-heading font-bold text-3xl text-green-700">R$ {totalPaid.toFixed(2)}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-yellow-200 bg-yellow-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-600">Valor Pendente</p>
                <p className="font-heading font-bold text-3xl text-yellow-700">R$ {totalPending.toFixed(2)}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Card do Contrato */}
      <Card className="border border-slate-200 mb-6">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-ciaponto-cyan" />
              </div>
              <div>
                <CardTitle className="font-heading text-lg">Meu Contrato</CardTitle>
                <CardDescription>Contrato de prestação de serviços CiaPonto</CardDescription>
              </div>
            </div>
            {contractData && (
              <Button
                onClick={handleDownloadContract}
                disabled={downloadingContract}
                className="bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark"
              >
                {downloadingContract ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Gerando...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Baixar PDF
                  </>
                )}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {contractData ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="space-y-2">
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Plano:</span>
                  <span className="font-medium">{contractData.contract?.plan_name || contractData.contract?.plan_type}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Valor Mensal:</span>
                  <span className="font-medium text-green-600">R$ {contractData.contract?.monthly_amount?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Status:</span>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Ativo
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Data de Aceite:</span>
                  <span className="font-medium">
                    {contractData.contract?.accepted_at 
                      ? new Date(contractData.contract.accepted_at).toLocaleDateString("pt-BR")
                      : "N/A"
                    }
                  </span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Dia de Vencimento:</span>
                  <span className="font-medium">Todo dia {contractData.contract?.subscription_day || 5}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Prestador:</span>
                  <span className="font-medium text-xs">{contractData.provider?.razao_social}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-6 text-slate-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Nenhum contrato encontrado.</p>
              <p className="text-sm mt-1">O contrato será gerado após aceitar os termos de uso.</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lista de faturas */}
      <Card className="border border-slate-200">
        <CardHeader>
          <CardTitle className="font-heading">Minhas Faturas</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1CA1BA]"></div>
            </div>
          ) : activeInvoices.length === 0 ? (
            <p className="text-center text-slate-500 py-8">Nenhuma fatura encontrada</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left p-3 font-medium text-slate-600">Nº</th>
                    <th className="text-left p-3 font-medium text-slate-600">Vencimento</th>
                    <th className="text-left p-3 font-medium text-slate-600">Valor</th>
                    <th className="text-left p-3 font-medium text-slate-600">Status</th>
                    <th className="text-right p-3 font-medium text-slate-600">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {activeInvoices.map((invoice) => (
                    <tr key={invoice.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-3 font-medium">{invoice.invoice_number}/{activeInvoices.length}</td>
                      <td className="p-3">{new Date(invoice.due_date).toLocaleDateString("pt-BR")}</td>
                      <td className="p-3 font-medium">R$ {invoice.amount.toFixed(2)}</td>
                      <td className="p-3">{getStatusBadge(invoice.status, invoice.due_date)}</td>
                      <td className="p-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => generateBoletoPDF(invoice)}
                            title="Baixar Fatura"
                          >
                            <FileDown className="w-4 h-4 text-slate-600" />
                          </Button>
                          {invoice.status !== "paid" && (
                            <Button
                              size="sm"
                              onClick={() => handleGeneratePayment(invoice)}
                              disabled={generatingPayment}
                              className="bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark"
                            >
                              <CreditCard className="w-4 h-4 mr-1" />
                              Pagar
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Pagamento */}
      <Dialog open={paymentModal} onOpenChange={setPaymentModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Pagar Fatura {selectedInvoice?.invoice_number}/12</DialogTitle>
            <DialogDescription>Valor: R$ {selectedInvoice?.amount?.toFixed(2)}</DialogDescription>
          </DialogHeader>
          
          {paymentData && (
            <div className="space-y-4 py-4">
              {paymentData.boleto_link && (
                <div className="text-center space-y-3">
                  <Button
                    onClick={() => window.open(paymentData.boleto_link, "_blank")}
                    className="w-full bg-orange-500 hover:bg-orange-600"
                  >
                    📄 Visualizar/Imprimir Boleto
                  </Button>
                  <p className="text-xs text-slate-500">
                    O boleto contém QR Code PIX para pagamento instantâneo
                  </p>
                </div>
              )}
              
              {paymentData.barcode && (
                <div className="space-y-2">
                  <p className="text-sm text-slate-600 text-center">Linha digitável:</p>
                  <div className="bg-slate-100 p-3 rounded-lg">
                    <p className="text-xs font-mono text-center break-all">{paymentData.barcode}</p>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      navigator.clipboard.writeText(paymentData.barcode);
                      toast.success("Código de barras copiado!");
                    }}
                  >
                    Copiar linha digitável
                  </Button>
                </div>
              )}
              
              <Button
                onClick={() => handleCheckStatus(selectedInvoice?.id)}
                className="w-full"
              >
                Já paguei! Verificar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Componente de Tela de Bloqueio
function TrialBlockedScreen({ daysRemaining, onLogout, companyId: propCompanyId, isEarlySignup = false, onCancel }) {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState("basico");
  // Sempre usa boleto (já inclui QRCode PIX para pagamento facilitado)
  const [contractData, setContractData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [step, setStep] = useState(1); // 1=select plan, 2=form, 3=payment
  const [companyPlan, setCompanyPlan] = useState(null);
  const [companyId, setCompanyId] = useState(propCompanyId || user?.company_id || "");
  
  // Form data for payment
  const [payerData, setPayerData] = useState({
    name: user?.name || "",
    cnpj: "",
    email: user?.email || ""
  });

  useEffect(() => {
    fetchPlans();
    fetchCompanyPlan();
  }, []);

  const fetchCompanyPlan = async () => {
    try {
      const response = await axios.get(`${API}/company/me`);
      const companyData = response.data;
      
      // Salvar o company_id se não tiver
      if (companyData.id && !companyId) {
        setCompanyId(companyData.id);
      }
      
      // Preencher automaticamente os dados do pagador com dados da empresa
      setPayerData(prev => ({
        ...prev,
        name: companyData.name || prev.name,
        cnpj: companyData.cnpj || prev.cnpj,
        email: companyData.email || prev.email
      }));
      
      const plan = companyData.plan;
      if (plan && ["basico", "profissional", "enterprise"].includes(plan)) {
        setSelectedPlan(plan);
        setCompanyPlan(plan);
      }
    } catch (error) {
      console.error("Error fetching company plan:", error);
    }
  };

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/plans`);
      setPlans(response.data.plans);
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  };

  const handleAcceptContract = async () => {
    if (!payerData.cnpj || !payerData.name) {
      toast.error("Preencha a razão social e CNPJ para continuar");
      return;
    }
    
    if (!companyId) {
      toast.error("Erro: ID da empresa não encontrado. Por favor, recarregue a página.");
      return;
    }
    
    setLoading(true);
    try {
      console.log("Accepting contract for company:", companyId);
      const response = await axios.post(`${API}/contract/accept`, {
        company_id: companyId,
        plan_type: selectedPlan,
        payer_name: payerData.name,
        payer_cnpj: payerData.cnpj,
        payer_email: payerData.email,
        payment_method: "boleto"  // Sempre boleto (já inclui QRCode PIX)
      });
      
      setContractData(response.data);
      setStep(3);
      toast.success("Contrato aceito! 12 mensalidades foram geradas.");
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMsg = typeof errorDetail === 'string' 
        ? errorDetail 
        : typeof errorDetail === 'object' 
          ? JSON.stringify(errorDetail) 
          : "Erro ao processar contrato";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const copyBarcode = () => {
    const barcode = contractData?.first_invoice?.barcode;
    if (barcode) {
      navigator.clipboard.writeText(barcode);
      setCopied(true);
      toast.success("Código de barras copiado!");
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const checkPaymentStatus = async () => {
    if (!contractData?.first_invoice?.id) return;
    
    setCheckingPayment(true);
    try {
      const response = await axios.get(`${API}/invoices/${contractData.first_invoice.id}/check-status`);
      if (response.data.status === "paid") {
        toast.success("Pagamento confirmado! Recarregando...");
        setTimeout(() => window.location.reload(), 2000);
      } else {
        toast.info("Pagamento ainda não confirmado. Aguarde alguns instantes.");
      }
    } catch (error) {
      toast.error("Erro ao verificar pagamento");
    } finally {
      setCheckingPayment(false);
    }
  };

  const selectedPlanData = plans.find(p => p.id === selectedPlan);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full border-2 border-[#1CA1BA]/30 bg-white/95 backdrop-blur">
        <CardHeader className="text-center pb-2">
          {isEarlySignup && onCancel && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onCancel}
              className="absolute top-4 left-4"
            >
              <X className="w-4 h-4 mr-1" />
              Voltar
            </Button>
          )}
          <div className={`w-16 h-16 ${isEarlySignup ? 'bg-green-100' : 'bg-cyan-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
            <FileText className={`w-8 h-8 ${isEarlySignup ? 'text-green-600' : 'text-ciaponto-cyan'}`} />
          </div>
          <CardTitle className="font-heading text-2xl text-slate-900">
            {step === 3 ? "Contrato Aceito!" : isEarlySignup ? "Assinar Agora" : "Finalizar Contratação"}
          </CardTitle>
          <CardDescription className="text-base">
            {step === 3 
              ? "Suas 12 mensalidades foram geradas. Pague a primeira para liberar o acesso."
              : isEarlySignup
              ? "Comece a usar o sistema agora mesmo com valor integral do plano."
              : "Seu período de teste de 7 dias terminou. Aceite o contrato para continuar."
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {step === 1 && (
            <>
              {/* Seleção de Plano */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Escolha seu plano (12 meses):</Label>
                  {companyPlan && (
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                      Plano escolhido no cadastro
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {plans.map((plan) => (
                    <button
                      key={plan.id}
                      onClick={() => setSelectedPlan(plan.id)}
                      className={`p-4 rounded-lg border-2 text-left transition-all relative ${
                        selectedPlan === plan.id
                          ? "border-[#1CA1BA] bg-sky-50"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      {companyPlan === plan.id && (
                        <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                          Seu plano
                        </span>
                      )}
                      <p className="font-medium text-slate-900">{plan.name}</p>
                      <p className="text-2xl font-bold text-ciaponto-cyan">R$ {plan.price}</p>
                      <p className="text-xs text-slate-500">/mês</p>
                    </button>
                  ))}
                </div>
                <p className="text-sm text-slate-500 text-center">
                  Total anual: R$ {(selectedPlanData?.price || 99) * 12} (12x de R$ {selectedPlanData?.price || 99} - vencimento todo dia {new Date().getDate() > 28 ? 28 : new Date().getDate()})
                </p>
              </div>

              {/* Forma de Pagamento */}
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">📄</span>
                  <div>
                    <p className="font-medium text-green-800">Pagamento via Boleto Bancário</p>
                    <p className="text-sm text-green-600">O boleto já inclui QR Code PIX para pagamento instantâneo</p>
                  </div>
                </div>
              </div>

              <Button onClick={() => setStep(2)} className="w-full h-12 bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark">
                Continuar
              </Button>
            </>
          )}

          {step === 2 && (
            <>
              {/* Resumo */}
              <div className="bg-sky-50 p-4 rounded-lg border border-sky-200">
                <p className="font-medium text-ciaponto-navy">{selectedPlanData?.name}</p>
                <p className="text-sm text-ciaponto-cyan">12 mensalidades de R$ {selectedPlanData?.price}/mês</p>
                <p className="text-sm text-ciaponto-cyan">Vencimento: todo dia 5</p>
              </div>

              {/* Dados do Pagador */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Dados para faturamento (Empresa):</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Razão Social *</Label>
                    <Input
                      value={payerData.name}
                      onChange={(e) => setPayerData({...payerData, name: e.target.value})}
                      placeholder="Razão Social da empresa"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">CNPJ *</Label>
                    <Input
                      value={payerData.cnpj}
                      onChange={(e) => setPayerData({...payerData, cnpj: e.target.value})}
                      placeholder="00.000.000/0001-00"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Email</Label>
                  <Input
                    value={payerData.email}
                    onChange={(e) => setPayerData({...payerData, email: e.target.value})}
                    placeholder="email@empresa.com"
                  />
                </div>
              </div>

              {/* Termos */}
              <div className="bg-slate-50 p-4 rounded-lg text-sm text-slate-600">
                <p className="font-medium mb-2">Ao aceitar, você concorda com:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Pagamento de 12 mensalidades no valor de R$ {selectedPlanData?.price}/mês</li>
                  <li>Vencimento todo dia 5 de cada mês</li>
                  <li>Primeira parcela proporcional aos dias até o próximo dia 5</li>
                </ul>
              </div>

              <div className="flex gap-3">
                <Button onClick={() => setStep(1)} variant="outline" className="flex-1">
                  Voltar
                </Button>
                <Button 
                  onClick={handleAcceptContract}
                  disabled={loading}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                  ) : (
                    <>✓ Aceitar Contrato</>
                  )}
                </Button>
              </div>
            </>
          )}

          {step === 3 && contractData && (
            <>
              {/* Resumo do contrato */}
              <div className="bg-green-50 p-4 rounded-lg border border-green-200 text-center">
                <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="text-green-800 font-medium">12 mensalidades geradas!</p>
                <p className="text-sm text-green-600">Pague a 1ª parcela para liberar o sistema</p>
              </div>

              {/* Primeira parcela */}
              <div className="bg-sky-50 p-4 rounded-lg border border-sky-200">
                <p className="font-medium text-ciaponto-navy">1ª Parcela (Proporcional)</p>
                <p className="text-2xl font-bold text-ciaponto-cyan">R$ {contractData.first_invoice?.amount?.toFixed(2)}</p>
                <p className="text-sm text-ciaponto-cyan">Vencimento: {contractData.first_invoice?.due_date}</p>
              </div>

              {/* Boleto (já inclui QRCode PIX) */}
              {contractData.first_invoice?.boleto_link && (
                <div className="space-y-3">
                  <Button 
                    onClick={() => window.open(contractData.first_invoice.boleto_link, "_blank")}
                    className="w-full bg-orange-500 hover:bg-orange-600"
                  >
                    📄 Visualizar/Imprimir Boleto
                  </Button>
                  <p className="text-xs text-center text-slate-500">
                    O boleto contém QR Code PIX para pagamento instantâneo
                  </p>
                </div>
              )}

              {contractData.first_invoice?.barcode && (
                <div className="space-y-2">
                  <p className="text-sm text-slate-600 text-center">Linha digitável:</p>
                  <div className="bg-slate-100 p-3 rounded-lg">
                    <p className="text-xs font-mono text-center break-all">{contractData.first_invoice.barcode}</p>
                  </div>
                  <Button onClick={copyBarcode} variant="outline" className={`w-full ${copied ? "bg-green-50" : ""}`}>
                    {copied ? <CheckCircle className="w-4 h-4 mr-2" /> : null}
                    {copied ? "Copiado!" : "Copiar linha digitável"}
                  </Button>
                </div>
              )}

              {/* Mensagem quando pagamento não foi gerado (erro de integração) */}
              {!contractData.payment_generated && (
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 text-center">
                  <AlertCircle className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
                  <p className="text-yellow-800 font-medium">Pagamento será gerado em breve</p>
                  <p className="text-sm text-yellow-600">Suas faturas foram criadas. Acesse a área financeira para gerar o código de pagamento.</p>
                </div>
              )}

              <Button 
                onClick={checkPaymentStatus}
                disabled={checkingPayment}
                className="w-full bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark"
              >
                {checkingPayment ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                ) : (
                  "Já paguei! Verificar pagamento"
                )}
              </Button>
            </>
          )}

          {/* Botão de logout */}
          <div className="pt-4 border-t border-slate-200">
            <Button onClick={onLogout} variant="ghost" className="w-full text-slate-600">
              <LogOut className="w-4 h-4 mr-2" />
              Sair do Sistema
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Tela de aguardando pagamento (contrato aceito, fatura pendente)
function PendingPaymentScreen({ invoice, onLogout }) {
  const [loading, setLoading] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [copied, setCopied] = useState(false);

  const generatePayment = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/invoices/${invoice.id}/generate-payment`);
      setPaymentData(response.data);
      toast.success("Código de pagamento gerado!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao gerar pagamento");
    } finally {
      setLoading(false);
    }
  };

  const copyBarcode = () => {
    const code = paymentData?.barcode || invoice?.barcode;
    if (code) {
      navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success("Código de barras copiado!");
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const checkPayment = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/invoices/${invoice.id}/check-status`);
      if (response.data.status === "paid") {
        toast.success("Pagamento confirmado! Recarregando...");
        setTimeout(() => window.location.reload(), 2000);
      } else {
        toast.info("Pagamento ainda não confirmado. Aguarde alguns instantes.");
      }
    } catch (error) {
      toast.error("Erro ao verificar pagamento");
    } finally {
      setLoading(false);
    }
  };

  const boletoLink = paymentData?.boleto_link || invoice?.boleto_link;
  const barcode = paymentData?.barcode || invoice?.barcode;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="max-w-lg w-full border-2 border-[#1CA1BA]/30 bg-white/95 backdrop-blur">
        <CardHeader className="text-center pb-2">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <CardTitle className="font-heading text-2xl text-slate-900">
            Contrato Aceito!
          </CardTitle>
          <CardDescription className="text-base">
            Pague a 1ª parcela para liberar o sistema
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Info da fatura */}
          <div className="bg-sky-50 p-4 rounded-lg border border-sky-200 text-center">
            <p className="font-medium text-ciaponto-navy">1ª Parcela (Proporcional)</p>
            <p className="text-3xl font-bold text-ciaponto-cyan">R$ {invoice?.amount?.toFixed(2)}</p>
            <p className="text-sm text-ciaponto-cyan">Vencimento: {invoice?.due_date}</p>
          </div>

          {/* Se não tem boleto, gerar pagamento */}
          {!boletoLink && (
            <Button 
              onClick={generatePayment} 
              disabled={loading}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
              ) : (
                "Gerar Boleto"
              )}
            </Button>
          )}

          {/* Boleto link */}
          {boletoLink && (
            <div className="space-y-3">
              <Button 
                onClick={() => window.open(boletoLink, "_blank")}
                className="w-full bg-orange-500 hover:bg-orange-600"
              >
                📄 Visualizar/Imprimir Boleto
              </Button>
              <p className="text-xs text-center text-slate-500">
                O boleto contém QR Code PIX para pagamento instantâneo
              </p>
            </div>
          )}

          {/* Linha digitável */}
          {barcode && (
            <div className="space-y-2">
              <p className="text-sm text-slate-600 text-center">Linha digitável:</p>
              <div className="bg-slate-100 p-3 rounded-lg">
                <p className="text-xs font-mono text-center break-all">{barcode}</p>
              </div>
              <Button onClick={copyBarcode} variant="outline" className={`w-full ${copied ? "bg-green-50" : ""}`}>
                {copied ? <CheckCircle className="w-4 h-4 mr-2" /> : null}
                {copied ? "Copiado!" : "Copiar linha digitável"}
              </Button>
            </div>
          )}

          {/* Verificar pagamento */}
          <Button 
            onClick={checkPayment}
            disabled={loading}
            className="w-full bg-[#1CA1BA] hover:bg-[#1CA1BA]-dark"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            ) : (
              "Já paguei! Verificar pagamento"
            )}
          </Button>

          <Button variant="ghost" onClick={onLogout} className="w-full">
            Sair do Sistema
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// Banner de aviso de período de teste com opção de assinar antecipadamente
function TrialBanner({ daysRemaining, onSignNow }) {
  return (
    <div className={`px-4 py-2 text-center text-sm font-medium ${
      daysRemaining <= 1 
        ? "bg-red-500 text-white" 
        : daysRemaining <= 3
        ? "bg-orange-500 text-white"
        : "bg-[#1CA1BA] text-white"
    }`}>
      {daysRemaining <= 3 ? (
        <>
          ⚠️ Seu período de teste expira em {daysRemaining} {daysRemaining === 1 ? "dia" : "dias"}. 
          <button onClick={onSignNow} className="underline ml-2 hover:text-sky-100">
            Assinar agora
          </button>
        </>
      ) : (
        <>
          📅 Você está no período de teste ({daysRemaining} dias restantes). 
          <button onClick={onSignNow} className="underline ml-2 hover:text-sky-100">
            Assinar agora e garantir acesso completo
          </button>
        </>
      )}
    </div>
  );
}

// Main Admin Dashboard
export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [trialStatus, setTrialStatus] = useState(null);
  const [loadingTrial, setLoadingTrial] = useState(true);
  const [companyData, setCompanyData] = useState(null);

  const navigation = [
    { name: "Dashboard", href: "/admin", icon: Home },
    { name: "Funcionários", href: "/admin/employees", icon: Users },
    { name: "Registros", href: "/admin/records", icon: Clock },
    { name: "Correção Ponto", href: "/admin/correction", icon: Edit3 },
    { name: "Férias", href: "/admin/ferias", icon: Palmtree },
    { name: "Atestados", href: "/admin/atestados", icon: Stethoscope },
    { name: "Feriados", href: "/admin/holidays", icon: Calendar },
    { name: "Histórico Horas", href: "/admin/historico-horas", icon: TrendingUp },
    { name: "Relatórios", href: "/admin/reports", icon: FileText },
    { name: "Financeiro", href: "/admin/financeiro", icon: DollarSign },
    { name: "Configurações", href: "/admin/configuracoes", icon: Settings },
    // Apenas para superadmin
    ...(user?.role === "superadmin" ? [
      { name: "Notas Fiscais", href: "/admin/nfse", icon: Receipt }
    ] : [])
  ];

  useEffect(() => {
    checkTrialStatus();
    fetchCompanyData();
  }, []);

  const fetchCompanyData = async () => {
    try {
      const response = await axios.get(`${API}/company/info`);
      setCompanyData(response.data);
    } catch (error) {
      console.error("Erro ao carregar dados da empresa:", error);
    }
  };

  const checkTrialStatus = async () => {
    try {
      const response = await axios.get(`${API}/company/trial-status`);
      setTrialStatus(response.data);
    } catch (error) {
      console.error("Erro ao verificar período de teste:", error);
      // Se der erro, assume que está ok para não bloquear indevidamente
      setTrialStatus({ is_blocked: false, is_trial: false });
    } finally {
      setLoadingTrial(false);
    }
  };

  const openSupportWhatsApp = () => {
    const message = encodeURIComponent("Olá! Preciso de suporte com o sistema CiaPonto.");
    window.open(`https://wa.me/${SUPPORT_WHATSAPP}?text=${message}`, "_blank");
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Estado para assinar contrato antecipadamente
  const [showEarlySignup, setShowEarlySignup] = useState(false);

  const isActive = (href) => {
    if (href === "/admin") return location.pathname === "/admin";
    return location.pathname.startsWith(href);
  };

  // Loading
  if (loadingTrial) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1CA1BA]"></div>
      </div>
    );
  }

  // Tela de bloqueio se período de teste expirou
  if (trialStatus?.is_blocked) {
    // Se já tem contrato aceito, mostrar tela para pagar a primeira fatura
    if (trialStatus?.contract_accepted && trialStatus?.first_invoice) {
      return <PendingPaymentScreen 
        invoice={trialStatus.first_invoice} 
        onLogout={handleLogout}
      />;
    }
    // Se não tem contrato, mostrar tela de contratação
    return <TrialBlockedScreen daysRemaining={0} onLogout={handleLogout} companyId={user?.company_id} />;
  }

  // Tela de assinatura antecipada
  if (showEarlySignup) {
    return <TrialBlockedScreen 
      daysRemaining={trialStatus?.days_remaining || 0} 
      onLogout={handleLogout} 
      companyId={user?.company_id}
      isEarlySignup={true}
      onCancel={() => setShowEarlySignup(false)}
    />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Banner de aviso do período de teste */}
      {trialStatus?.is_trial && (
        <TrialBanner daysRemaining={trialStatus.days_remaining} onSignNow={() => setShowEarlySignup(true)} />
      )}

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-50 h-full w-64 bg-[#137082] transform transition-transform duration-200 lg:translate-x-0 ${
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      }`}>
        {/* Header com botão fechar mobile */}
        <div className="flex items-center justify-end p-4 border-b border-slate-800 lg:hidden">
          <button className="text-slate-400" onClick={() => setSidebarOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Dados da Empresa com Logo da Empresa */}
        <div className="p-4 border-b border-slate-800 bg-[#17889E]/50">
          <div className="flex items-center gap-3">
            {/* Logo/Foto de Perfil da Empresa */}
            <div className="w-12 h-12 bg-gradient-to-br from-sky-500 to-sky-700 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 ring-2 ring-sky-400/30">
              {companyData?.logo_url ? (
                <img 
                  src={companyData.logo_url} 
                  alt={companyData?.name} 
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="font-bold text-xl text-white">{companyData?.name?.[0] || user?.company_name?.[0] || "E"}</span>
              )}
            </div>
            
            {/* Nome Fantasia e Razão Social */}
            <div className="flex-1 min-w-0">
              {/* Nome Fantasia primeiro */}
              <p className="text-sm font-bold text-white leading-tight break-words">
                {companyData?.trade_name || companyData?.name || user?.company_name || "Minha Empresa"}
              </p>
              {/* Razão Social abaixo - só mostra se for DIFERENTE do nome fantasia */}
              {companyData?.trade_name && companyData?.name && 
               companyData.name.toLowerCase().trim() !== companyData.trade_name.toLowerCase().trim() && (
                <p className="text-xs text-slate-400 break-words mt-0.5">
                  {companyData.name}
                </p>
              )}
            </div>
          </div>
        </div>

        <nav className="p-4 space-y-1">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                isActive(item.href)
                  ? "bg-[#1CA1BA] text-white"
                  : "text-slate-400 hover:text-white hover:bg-[#17889E]"
              }`}
              data-testid={`nav-${item.name.toLowerCase()}`}
            >
              <item.icon className="w-5 h-5" />
              {item.name}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
          {/* Painel Administrativo */}
          <p className="text-xs text-slate-500 text-center mb-3">Painel Administrativo</p>
          
          {/* Botão de Suporte WhatsApp */}
          <Button 
            variant="ghost" 
            className="w-full justify-start text-green-400 hover:text-white hover:bg-green-600 mb-2"
            onClick={openSupportWhatsApp}
            data-testid="support-btn"
          >
            <HeadphonesIcon className="w-4 h-4 mr-2" />
            Suporte WhatsApp
          </Button>
          
          <Button 
            variant="ghost" 
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-[#17889E]"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sair
          </Button>
          
          {/* Powered by CiaPonto */}
          <div className="mt-3 pt-3 border-t border-slate-700 text-center">
            <a 
              href="https://www.control-isp.com.br" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-slate-500 hover:text-cyan-400 transition-colors"
            >
              Powered by <span className="font-semibold">CiaPonto</span>
            </a>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 h-16">
            <button
              className="lg:hidden p-2 -ml-2 text-slate-600"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-toggle"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex-1" />
            {/* Logo CiaPonto colorida */}
            <img 
              src="/logo-ciaponto-color.png" 
              alt="CiaPonto" 
              className="h-12 w-auto"
            />
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          <Routes>
            <Route index element={<DashboardOverview />} />
            <Route path="employees" element={<EmployeesPage />} />
            <Route path="records" element={<TimeRecordsPage />} />
            <Route path="correction" element={<PointCorrectionPage />} />
            <Route path="ferias" element={<FeriasPage />} />
            <Route path="atestados" element={<AtestadosPage />} />
            <Route path="holidays" element={<HolidaysPage />} />
            <Route path="historico-horas" element={<HoursHistoryPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="financeiro" element={<FinanceiroPage />} />
            <Route path="configuracoes" element={<ConfiguracoesPage />} />
            <Route path="nfse" element={<NotasFiscaisPage />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
